/**
 * Internationalization (i18n) support.
 * Supported locales: en, th, ja, ko, zh
 */

export type Locale = "en" | "th" | "ja" | "ko" | "zh";

export const LOCALES: Array<{ code: Locale; label: string; flag: string }> = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "th", label: "ไทย", flag: "🇹🇭" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ko", label: "한국어", flag: "🇰🇷" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];

type TranslationKeys = {
  // Nav groups
  "nav.chat": string;
  "nav.dashboard": string;
  "nav.automation": string;
  "nav.system": string;

  // Simple mode nav labels
  "nav.simple.chat": string;
  "nav.simple.status": string;
  "nav.simple.settings": string;
  "nav.simple.tools": string;
  "nav.simple.help": string;

  // Nav mode toggle
  "nav.mode.simple": string;
  "nav.mode.advanced": string;
  "nav.mode.toggle_label": string;

  // Tab titles
  "tab.chat": string;
  "tab.overview": string;
  "tab.dashboard": string;
  "tab.channels": string;
  "tab.instances": string;
  "tab.sessions": string;
  "tab.usage": string;
  "tab.agents": string;
  "tab.skills": string;
  "tab.cron": string;
  "tab.terminal": string;
  "tab.files": string;
  "tab.nodes": string;
  "tab.config": string;
  "tab.debug": string;
  "tab.heartbeat": string;
  "sub.heartbeat": string;
  "hb.title": string;
  "hb.subtitle": string;
  "hb.timeline": string;
  "hb.event_log": string;
  "hb.no_events": string;
  "hb.refresh": string;
  "hb.pause": string;
  "hb.resume": string;
  "hb.loading": string;
  "hb.new": string;
  "hb.stat_total": string;
  "hb.stat_ok_rate": string;
  "hb.stat_alert_rate": string;
  "hb.stat_avg_duration": string;
  "hb.stat_skipped": string;
  "hb.stat_failed": string;
  "hb.stat_duplicates": string;
  "hb.stat_api_saved": string;
  "hb.status_sent": string;
  "hb.status_ok-empty": string;
  "hb.status_ok-token": string;
  "hb.status_skipped": string;
  "hb.status_failed": string;
  "tab.logs": string;
  "tab.learning": string;
  "tab.mind": string;
  "tab.activity-log": string;
  "tab.spawn-monitor": string;

  // Tab subtitles
  "sub.chat": string;
  "sub.overview": string;
  "sub.dashboard": string;
  "sub.channels": string;
  "sub.instances": string;
  "sub.sessions": string;
  "sub.agents": string;
  "sub.skills": string;
  "sub.cron": string;
  "sub.terminal": string;
  "sub.files": string;
  "sub.nodes": string;
  "sub.config": string;
  "sub.debug": string;
  "sub.logs": string;
  "sub.learning": string;
  "sub.mind": string;
  "mind.soul": string;
  "mind.soul.emotions": string;
  "mind.soul.lifecycle": string;
  "mind.soul.personality": string;
  "mind.soul.values": string;
  "mind.disconnected": string;
  "mind.plugin_unavailable": string;
  "mind.plugin_unavailable_hint": string;
  "mind.loading": string;
  "mind.retry": string;
  "mind.sec_overview": string;
  "mind.sec_soul": string;
  "mind.sec_depth": string;
  "mind.sec_thoughts": string;
  "mind.sec_workspace": string;
  "mind.sec_identity": string;
  "mind.sec_analytics": string;
  "mind.emotion_love": string;
  "mind.emotion_anger": string;
  "mind.emotion_sadness": string;
  "mind.emotion_joy": string;
  "mind.emotion_fear": string;
  "mind.emotion_trust": string;
  "mind.emotion_greed": string;
  "mind.emotion_delusion": string;
  "mind.mood_valence": string;
  "mind.mood_balanced": string;
  "mind.mood_unknown": string;
  "mind.loyalty": string;
  "mind.stage_newborn": string;
  "mind.stage_child": string;
  "mind.stage_adolescent": string;
  "mind.stage_adult": string;
  "mind.stage_elder": string;
  "mind.stage_transcendent": string;
  "mind.meter_health": string;
  "mind.meter_energy": string;
  "mind.meter_wisdom": string;
  "mind.personality_developing": string;
  "mind.overview_title": string;
  "mind.card_depth": string;
  "mind.card_mirror": string;
  "mind.card_workspace": string;
  "mind.card_identity": string;
  "mind.card_goals": string;
  "mind.card_thoughts": string;
  "mind.inactive": string;
  "mind.no_data": string;
  "mind.active": string;
  "mind.idle": string;
  "mind.waiting": string;
  "mind.entries": string;
  "mind.traits": string;
  "mind.values_count": string;
  "mind.active_count": string;
  "mind.recent_count": string;
  "mind.global_memory": string;
  "mind.inner_monologue": string;
  "mind.no_goals": string;
  "mind.unavailable": string;
  "mind.recent_thoughts": string;
  "mind.no_thoughts": string;
  "mind.depth_title": string;
  "mind.depth_current": string;
  "mind.depth_instinctive": string;
  "mind.depth_associative": string;
  "mind.depth_analytical": string;
  "mind.depth_strategic": string;
  "mind.confidence": string;
  "mind.depth_distribution": string;
  "mind.recent_classifications": string;
  "mind.col_depth": string;
  "mind.col_confidence": string;
  "mind.col_phases": string;
  "mind.col_time": string;
  "mind.loading_depth": string;
  "mind.thoughts_title": string;
  "mind.no_thoughts_yet": string;
  "mind.workspace_title": string;
  "mind.ws_entries": string;
  "mind.ws_active_entries": string;
  "mind.ws_attention": string;
  "mind.ws_top5": string;
  "mind.ws_salience": string;
  "mind.ws_description": string;
  "mind.identity_title": string;
  "mind.id_continuity": string;
  "mind.id_consistent": string;
  "mind.id_drift": string;
  "mind.id_traits": string;
  "mind.id_values": string;
  "mind.id_no_traits": string;
  "mind.id_no_values": string;
  "mind.id_loading": string;
  "mind.analytics_title": string;
  "mind.an_classifications": string;
  "mind.an_override_rate": string;
  "mind.an_loop_perf": string;
  "mind.an_avg": string;
  "mind.an_runs_max": string;
  "mind.an_dominant": string;
  "mind.an_avg_confidence": string;
  "mind.an_phase_failures": string;
  "mind.an_failures": string;
  "mind.an_loading": string;
  "mind.time_just_now": string;
  "mind.time_m_ago": string;
  "mind.time_h_ago": string;
  "mind.time_d_ago": string;
  "sub.activity-log": string;
  "sub.spawn-monitor": string;

  // Spawn Monitor
  "spawn_monitor.title": string;
  "spawn_monitor.sub": string;
  "spawn_monitor.active_now": string;
  "spawn_monitor.total_recent": string;
  "spawn_monitor.avg_runtime": string;
  "spawn_monitor.success_rate": string;
  "spawn_monitor.active_title": string;
  "spawn_monitor.no_active": string;
  "spawn_monitor.elapsed": string;
  "spawn_monitor.recent_title": string;
  "spawn_monitor.recent_sub": string;
  "spawn_monitor.no_recent": string;
  "spawn_monitor.col_message": string;
  "spawn_monitor.col_status": string;
  "spawn_monitor.col_model": string;
  "spawn_monitor.col_runtime": string;
  "spawn_monitor.col_time": string;
  "spawn_monitor.events_title": string;
  "spawn_monitor.events_sub": string;
  "spawn_monitor.no_events": string;
  "spawn_monitor.clear_events": string;
  "spawn_monitor.auto_refresh": string;

  // Dashboard
  "dashboard.nodes": string;
  "dashboard.nodes_online": string;
  "dashboard.agents": string;
  "dashboard.agents_registered": string;
  "dashboard.devices": string;
  "dashboard.devices_connected": string;
  "dashboard.realtime": string;
  "dashboard.refresh": string;
  "dashboard.connected_nodes": string;
  "dashboard.disconnected_nodes": string;
  "dashboard.loading": string;
  "dashboard.no_nodes": string;
  "dashboard.commands": string;
  "dashboard.node_capabilities": string;
  "dashboard.node_capabilities_sub": string;
  "dashboard.node": string;
  "dashboard.status": string;
  "dashboard.capabilities": string;
  "dashboard.uptime": string;
  "dashboard.agents_list": string;
  "dashboard.queue_title": string;
  "dashboard.queue_sub": string;
  "dashboard.queue_active": string;
  "dashboard.queue_queued": string;
  "dashboard.queue_lane": string;
  "dashboard.queue_concurrency": string;
  "dashboard.queue_idle": string;
  "dashboard.queue_idle_lanes": string;

  // Learning
  "learning.overview_title": string;
  "learning.memory_entries": string;
  "learning.emotions_active": string;
  "learning.user_messages": string;
  "learning.modules": string;
  "learning.mod_memory": string;
  "learning.mod_memory_desc": string;
  "learning.mod_soul": string;
  "learning.mod_soul_desc": string;
  "learning.mod_emotions": string;
  "learning.mod_emotions_desc": string;
  "learning.mod_user": string;
  "learning.mod_user_desc": string;
  "learning.mod_tiered": string;
  "learning.mod_tiered_desc": string;
  "learning.active": string;
  "learning.inactive": string;
  "learning.generate": string;
  "learning.regenerate": string;
  "learning.generating": string;
  "learning.init_all": string;
  "learning.generate_memory": string;
  "learning.generate_soul": string;
  "learning.generate_emotions": string;
  "learning.generate_user": string;
  "learning.generate_knowledge": string;
  "learning.current_mood": string;
  "learning.mood_valence": string;
  "learning.mood_stability": string;
  "learning.mood_energy": string;
  "learning.mood_warmth": string;
  "learning.memory_title": string;
  "learning.tiered_memory": string;
  "learning.short_term": string;
  "learning.long_term": string;
  "learning.entities": string;
  "learning.entity_graph": string;
  "learning.recent_memories": string;
  "learning.memory_file": string;
  "learning.no_memory": string;
  "learning.memory_history": string;
  "learning.load_history": string;
  "learning.no_history": string;
  "learning.emotions_title": string;
  "learning.emotion_vector": string;
  "learning.vec_valence": string;
  "learning.vec_arousal": string;
  "learning.vec_dominance": string;
  "learning.vec_attachment": string;
  "learning.vec_desire": string;
  "learning.vec_confusion": string;
  "learning.active_emotions": string;
  "learning.loyalty": string;
  "learning.loyalty_level": string;
  "learning.bond_strength": string;
  "learning.protective": string;
  "learning.devotion_style": string;
  "learning.emotional_memories": string;
  "learning.no_emotions": string;
  "learning.personality_title": string;
  "learning.soul_file": string;
  "learning.identity_file": string;
  "learning.no_soul": string;
  "learning.user_title": string;
  "learning.user_profile": string;
  "learning.comm_style": string;
  "learning.expertise": string;
  "learning.response_pref": string;
  "learning.mental_state": string;
  "learning.interests": string;
  "learning.interaction_stats": string;
  "learning.stat_messages": string;
  "learning.stat_questions": string;
  "learning.stat_commands": string;
  "learning.stat_code": string;
  "learning.user_file": string;
  "learning.no_user": string;
  "learning.knowledge_title": string;
  "learning.long_term_knowledge": string;
  "learning.no_knowledge": string;
  "learning.disconnected": string;
  "learning.loading": string;
  "learning.refresh": string;
  "learning.sec_overview": string;
  "learning.sec_memory": string;
  "learning.sec_emotions": string;
  "learning.sec_personality": string;
  "learning.sec_user": string;
  "learning.sec_knowledge": string;

  // Knowledge Sources
  "ks.title": string;
  "ks.loading": string;
  "ks.refresh": string;
  "ks.add_source": string;
  "ks.edit_source": string;
  "ks.empty_title": string;
  "ks.empty_description": string;
  "ks.tab_sources": string;
  "ks.tab_collected": string;
  "ks.status_idle": string;
  "ks.status_collecting": string;
  "ks.status_success": string;
  "ks.status_error": string;
  "ks.disabled": string;
  "ks.schedule": string;
  "ks.last_collected": string;
  "ks.docs_ingested": string;
  "ks.runs": string;
  "ks.collect_now": string;
  "ks.edit": string;
  "ks.enable": string;
  "ks.disable": string;
  "ks.delete": string;
  "ks.delete_confirm": string;
  "ks.field_name": string;
  "ks.field_name_placeholder": string;
  "ks.field_description": string;
  "ks.field_description_placeholder": string;
  "ks.field_description_hint": string;
  "ks.field_credentials": string;
  "ks.field_credentials_hint": string;
  "ks.field_username": string;
  "ks.field_password": string;
  "ks.field_api_key": string;
  "ks.field_interval": string;
  "ks.minutes": string;
  "ks.cancel": string;
  "ks.save": string;
  "ks.create": string;
  "ks.just_now": string;

  // Login
  "login.title": string;
  "login.subtitle": string;
  "login.password": string;
  "login.signin": string;
  "login.hint": string;

  // Topbar
  "topbar.connected": string;
  "topbar.offline": string;
  "topbar.signout": string;

  // Overview
  "overview.connection": string;
  "overview.online": string;
  "overview.offline": string;
  "overview.running_for": string;
  "overview.not_connected": string;
  "overview.channels": string;
  "overview.active_sessions": string;
  "overview.system_info": string;
  "overview.getting_started": string;
  "overview.getting_started_sub": string;
  "overview.tip_connect": string;
  "overview.tip_connect_desc": string;
  "overview.tip_chat": string;
  "overview.tip_chat_desc": string;
  "overview.tip_settings": string;
  "overview.tip_settings_desc": string;

  // Terminal
  "terminal.quick_commands": string;
  "terminal.quick_sub": string;
  "terminal.run_command": string;
  "terminal.run_sub": string;
  "terminal.run": string;
  "terminal.running": string;
  "terminal.output": string;
  "terminal.clear": string;
  "terminal.no_commands": string;
  "terminal.no_commands_sub": string;
  "terminal.not_connected": string;
  "terminal.welcome": string;
  "terminal.welcome_hint": string;
  "terminal.status": string;
  "terminal.config": string;
  "terminal.disk": string;
  "terminal.memory": string;
  "terminal.processes": string;
  "terminal.network": string;
  "terminal.uptime": string;
  "terminal.logs_btn": string;

  // Files
  "files.quick_access": string;
  "files.quick_sub": string;
  "files.home": string;
  "files.nabe_config": string;
  "files.app_files": string;
  "files.extensions": string;
  "files.temp": string;
  "files.root": string;
  "files.go_up": string;
  "files.refresh": string;
  "files.show_hidden": string;
  "files.items": string;
  "files.name": string;
  "files.size": string;
  "files.modified": string;
  "files.empty": string;
  "files.loading": string;
  "files.opening": string;
  "files.save": string;
  "files.saving": string;
  "files.close": string;
  "files.unsaved": string;
  "files.delete": string;
  "files.delete_confirm": string;
  "files.not_connected": string;

  // Config
  "config.save": string;
  "config.saving": string;
  "config.undo": string;
  "config.apply": string;
  "config.applying": string;
  "config.update": string;
  "config.updating": string;
  "config.visual": string;
  "config.advanced": string;
  "config.search": string;

  // Hints — tooltips for technical terms (shown as * icon)
  "hint.gateway": string;
  "hint.websocket": string;
  "hint.session_key": string;
  "hint.thinking_level": string;
  "hint.block_streaming": string;
  "hint.block_streaming_break": string;
  "hint.block_streaming_chunk": string;
  "hint.exec_approvals": string;
  "hint.bindings": string;
  "hint.auto_follow": string;
  "hint.agent": string;
  "hint.skill": string;
  "hint.cron": string;
  "hint.node": string;
  "hint.instance": string;
  "hint.channel": string;
  "hint.tls": string;
  "hint.tailscale": string;
  "hint.rate_limit": string;
  "hint.trusted_proxy": string;
  "hint.sandbox": string;
  "hint.tool_policy": string;
  "hint.subagent": string;
  "hint.memory_search": string;
  "hint.tts": string;
  "hint.vision": string;
  "hint.ollama": string;
  "hint.api_key": string;
  "hint.model": string;
  "hint.token": string;
  "hint.password_auth": string;
  "hint.webhook": string;
  "hint.presence": string;
  "hint.compaction": string;
  "hint.funnel": string;
  "hint.basepath": string;

  // Step-by-step guides
  "guide.connect_app_title": string;
  "guide.connect_app_steps": string;
  "guide.setup_agent_title": string;
  "guide.setup_agent_steps": string;
  "guide.add_skill_title": string;
  "guide.add_skill_steps": string;
  "guide.schedule_task_title": string;
  "guide.schedule_task_steps": string;

  // Overview page
  "overview.sessions_now": string;
  "overview.scheduled_tasks": string;
  "overview.scheduled_none": string;
  "overview.scheduled_active": string;
  "overview.scheduled_paused": string;
  "overview.attention": string;
  "overview.auth_required": string;
  "overview.auth_hint_dashboard": string;
  "overview.auth_hint_token": string;
  "overview.auth_failed": string;
  "overview.docs_auth": string;
  "overview.insecure_context": string;
  "overview.insecure_stay": string;
  "overview.docs_tailscale": string;
  "overview.docs_insecure": string;
  "overview.connection_settings": string;
  "overview.connection_sub": string;
  "overview.server_address": string;
  "overview.password": string;
  "overview.enter_password": string;
  "overview.session_name": string;
  "overview.reconnect": string;
  "overview.connect_btn": string;
  "overview.refresh_data": string;
  "overview.system_sub": string;
  "overview.running_time": string;
  "overview.connected_devices": string;
  "overview.last_refresh": string;
  "overview.never": string;
  "overview.all_good": string;

  // Channels page
  "channels.details": string;
  "channels.details_sub": string;
  "channels.never_checked": string;
  "channels.no_snapshot": string;
  "channels.status_sub": string;
  "channels.configured": string;
  "channels.running": string;
  "channels.connected": string;
  "channels.last_inbound": string;
  "channels.form_unavailable": string;
  "channels.form_unavailable_sub": string;
  "channels.plugin_needed": string;
  "channels.plugin_needed_sub": string;
  "channels.loading_schema": string;
  "channels.save_changes": string;
  "channels.undo_changes": string;
  "channels.saving": string;

  // Channel picker
  "picker.title": string;
  "picker.subtitle": string;
  "picker.add": string;
  "picker.add_channel": string;
  "picker.all_added": string;
  "picker.already_added": string;
  "picker.empty_state": string;
  "picker.easy": string;
  "picker.medium": string;
  "picker.hard": string;
  "picker.enabled": string;

  // Channel setup guide
  "guide.title": string;
  "guide.full_docs": string;

  // Config page
  "config.general": string;
  "config.updates": string;
  "config.ai_agents": string;
  "config.security": string;
  "config.messaging_apps": string;
  "config.messages": string;
  "config.commands": string;
  "config.hooks": string;
  "config.skills_section": string;
  "config.tools": string;
  "config.server": string;
  "config.setup_guide": string;
  "config.settings": string;
  "config.all_settings": string;
  "config.unsaved": string;
  "config.no_changes": string;
  "config.discard_title": string;
  "config.save_title": string;
  "config.restart_title": string;
  "config.update_title": string;
  "config.loading_schema": string;
  "config.advanced_warning": string;
  "config.raw_json5": string;
  "config.all": string;

  // Agents page
  "agents.title": string;
  "agents.configured": string;
  "agents.no_agents": string;
  "agents.select": string;
  "agents.select_sub": string;
  "agents.tab_overview": string;
  "agents.tab_files": string;
  "agents.tab_tools": string;
  "agents.tab_skills": string;
  "agents.tab_channels": string;
  "agents.tab_cron": string;
  "agents.tab_runtime": string;
  "agents.runtime_title": string;
  "agents.runtime_sub": string;
  "agents.runtime_active": string;
  "agents.runtime_no_active": string;
  "agents.runtime_elapsed": string;
  "agents.runtime_queue": string;
  "agents.runtime_queue_unavailable": string;
  "agents.runtime_slots": string;
  "agents.runtime_recent": string;
  "agents.runtime_recent_sub": string;
  "agents.runtime_no_recent": string;
  "agents.runtime_col_label": string;
  "agents.runtime_col_status": string;
  "agents.runtime_col_model": string;
  "agents.runtime_col_runtime": string;
  "agents.runtime_watchdog": string;
  "agents.runtime_watchdog_on": string;
  "agents.runtime_watchdog_off": string;
  "agents.runtime_scans": string;
  "agents.runtime_aborted": string;
  "agents.runtime_registered": string;
  "agents.overview_sub": string;
  "agents.workspace": string;
  "agents.primary_model": string;
  "agents.identity_name": string;
  "agents.default": string;
  "agents.identity_emoji": string;
  "agents.skills_filter": string;
  "agents.selected": string;
  "agents.all_skills": string;
  "agents.model_selection": string;
  "agents.primary_default": string;
  "agents.inherit_default": string;
  "agents.fallbacks": string;
  "agents.fallbacks_placeholder": string;
  "agents.reload_config": string;
  "agents.api_keys_title": string;
  "agents.api_keys_sub": string;
  "agents.api_key_set": string;
  "agents.api_key_placeholder": string;
  "agents.catalog_title": string;
  "agents.catalog_sub": string;
  "agents.catalog_model_id": string;
  "agents.catalog_alias": string;
  "agents.api_key_label": string;
  "agents.catalog_actions": string;
  "agents.catalog_add": string;
  "agents.catalog_add_placeholder": string;
  "agents.catalog_alias_placeholder": string;
  "agents.catalog_empty": string;
  "agents.catalog_confirm_remove": string;
  "agents.catalog_provider": string;
  "agents.catalog_model": string;
  "agents.catalog_no_providers": string;
  "agents.catalog_select_provider": string;
  "agents.catalog_select_model": string;
  "agents.fallback_title": string;
  "agents.fallback_sub": string;
  "agents.fallback_add": string;
  "agents.fallback_empty": string;
  "agents.custom_env_title": string;
  "agents.custom_env_sub": string;
  "agents.custom_env_add": string;
  "agents.custom_env_name_placeholder": string;
  "agents.custom_env_value_placeholder": string;

  // Sessions page
  "sessions.title": string;
  "sessions.title_sub": string;
  "sessions.active_within": string;
  "sessions.limit": string;
  "sessions.include_global": string;
  "sessions.include_unknown": string;
  "sessions.store": string;
  "sessions.col_key": string;
  "sessions.col_label": string;
  "sessions.col_kind": string;
  "sessions.col_updated": string;
  "sessions.col_tokens": string;
  "sessions.col_thinking": string;
  "sessions.col_verbose": string;
  "sessions.col_reasoning": string;
  "sessions.col_actions": string;
  "sessions.no_sessions": string;
  "sessions.optional": string;
  "sessions.inherit": string;
  "sessions.task_log": string;
  "sessions.no_messages": string;
  "sessions.messages_label": string;

  // Cron page
  "cron.title": string;
  "cron.title_sub": string;
  "cron.enabled": string;
  "cron.jobs": string;
  "cron.next_wake": string;
  "cron.new_job": string;
  "cron.new_job_sub": string;
  "cron.name": string;
  "cron.description": string;
  "cron.agent_id": string;
  "cron.schedule": string;
  "cron.every": string;
  "cron.at": string;
  "cron.cron_expr": string;
  "cron.session": string;
  "cron.wake_mode": string;
  "cron.payload": string;
  "cron.main": string;
  "cron.isolated": string;
  "cron.now": string;
  "cron.next_heartbeat": string;
  "cron.system_event": string;
  "cron.agent_turn": string;
  "cron.delivery": string;
  "cron.timeout": string;
  "cron.channel": string;
  "cron.to": string;
  "cron.announce": string;
  "cron.none_internal": string;
  "cron.add_job": string;
  "cron.jobs_title": string;
  "cron.jobs_sub": string;
  "cron.no_jobs": string;
  "cron.run_history": string;
  "cron.run_history_sub": string;
  "cron.select_job": string;
  "cron.no_runs": string;
  "cron.run_at": string;
  "cron.expression": string;
  "cron.timezone": string;
  "cron.unit": string;
  "cron.unit_minutes": string;
  "cron.unit_hours": string;
  "cron.unit_days": string;
  "cron.system": string;
  "cron.system_text": string;
  "cron.agent_message": string;
  "cron.prompt": string;
  "cron.run_btn": string;
  "cron.history_btn": string;
  "cron.remove": string;
  "cron.status": string;
  "cron.next": string;
  "cron.last": string;
  "cron.open_run_chat": string;

  // Skills page
  "skills.title": string;
  "skills.title_sub": string;
  "skills.filter": string;
  "skills.search": string;
  "skills.shown": string;
  "skills.no_skills": string;
  "skills.missing": string;
  "skills.reason": string;
  "skills.api_key": string;
  "skills.save_key": string;

  // Nodes page
  "nodes.title": string;
  "nodes.title_sub": string;
  "nodes.no_nodes": string;
  "nodes.devices": string;
  "nodes.devices_sub": string;
  "nodes.pending": string;
  "nodes.paired": string;
  "nodes.no_paired": string;
  "nodes.role": string;
  "nodes.roles": string;
  "nodes.scopes": string;
  "nodes.approve": string;
  "nodes.reject": string;
  "nodes.tokens_none": string;
  "nodes.tokens": string;
  "nodes.revoked": string;
  "nodes.active_token": string;
  "nodes.rotate": string;
  "nodes.revoke": string;
  "nodes.exec_binding": string;
  "nodes.exec_binding_sub": string;
  "nodes.switch_form": string;
  "nodes.load_config": string;
  "nodes.default_binding": string;
  "nodes.default_binding_sub": string;
  "nodes.any_node": string;
  "nodes.no_exec_nodes": string;
  "nodes.no_agents_found": string;
  "nodes.default_agent": string;
  "nodes.uses_default": string;
  "nodes.node": string;
  "nodes.override": string;
  "nodes.binding": string;
  "nodes.use_default": string;
  "nodes.status_paired": string;
  "nodes.status_unpaired": string;
  "nodes.status_connected": string;
  "nodes.status_offline": string;

  // Instances page
  "instances.title": string;
  "instances.title_sub": string;
  "instances.no_instances": string;
  "instances.last_input": string;
  "instances.reason": string;

  // Debug page
  "debug.snapshots": string;
  "debug.snapshots_sub": string;
  "debug.status": string;
  "debug.health": string;
  "debug.last_heartbeat": string;
  "debug.security_audit": string;
  "debug.for_details": string;
  "debug.manual_rpc": string;
  "debug.manual_rpc_sub": string;
  "debug.method": string;
  "debug.params": string;
  "debug.call": string;
  "debug.models": string;
  "debug.models_sub": string;
  "debug.event_log": string;
  "debug.event_log_sub": string;
  "debug.no_events": string;

  // Logs page
  "logs.title": string;
  "logs.title_sub": string;
  "logs.export": string;
  "logs.filter": string;
  "logs.search": string;
  "logs.auto_follow": string;
  "logs.truncated": string;
  "logs.no_entries": string;

  // Chat page
  "chat.compacting": string;
  "chat.compacted": string;
  "chat.attachment_preview": string;
  "chat.remove_attachment": string;
  "chat.placeholder": string;
  "chat.placeholder_shift": string;
  "chat.not_connected": string;
  "chat.loading": string;
  "chat.exit_focus": string;
  "chat.queued": string;
  "chat.remove_queued": string;
  "chat.image_count": string;
  "chat.new_messages": string;
  "chat.message": string;
  "chat.stop": string;
  "chat.new_session": string;
  "chat.queue_btn": string;
  "chat.queued_batch": string;
  "chat.send": string;
  "chat.history_hidden": string;
  "chat.compaction_divider": string;

  // App chrome
  "app.brand": string;
  "app.subtitle": string;
  "app.expand_sidebar": string;
  "app.collapse_sidebar": string;
  "app.resources": string;
  "app.docs": string;
  "app.refresh_chat": string;
  "app.disabled_onboarding": string;
  "app.toggle_thinking": string;
  "app.toggle_focus": string;
  "app.theme_system": string;
  "app.theme_light": string;
  "app.theme_dark": string;
  "app.theme": string;

  // Common
  "common.loading": string;
  "common.error": string;
  "common.cancel": string;
  "common.confirm": string;
  "common.yes": string;
  "common.no": string;
  "common.na": string;
  "common.save": string;
  "common.saving": string;
  "common.refresh": string;
  "common.refreshing": string;
  "common.delete": string;
  "common.enable": string;
  "common.disable": string;
  "common.enabled": string;
  "common.disabled": string;
  "common.language": string;
  "common.what_is_this": string;
  "common.learn_more": string;
  "common.step": string;
  "common.docs": string;
  "common.resources": string;
  "common.agent": string;

  // Quick Settings
  "qs.title": string;
  "qs.subtitle": string;
  "qs.ai": string;
  "qs.ai.model": string;
  "qs.ai.model_help": string;
  "qs.ai.name": string;
  "qs.ai.name_help": string;
  "qs.ai.workspace": string;
  "qs.ai.workspace_help": string;
  "qs.conn": string;
  "qs.conn.port": string;
  "qs.conn.port_help": string;
  "qs.conn.bind": string;
  "qs.conn.bind_help": string;
  "qs.conn.bind.loopback": string;
  "qs.conn.bind.lan": string;
  "qs.conn.bind.tailnet": string;
  "qs.conn.bind.auto": string;
  "qs.conn.auth": string;
  "qs.conn.auth_help": string;
  "qs.conn.password": string;
  "qs.apps": string;
  "qs.apps.subtitle": string;
  "qs.apps.configure": string;
  "qs.updates": string;
  "qs.updates.channel": string;
  "qs.updates.channel_help": string;
  "qs.updates.auto": string;
  "qs.updates.auto_help": string;
  "qs.log": string;
  "qs.log.level": string;
  "qs.log.level_help": string;
  "qs.all_settings": string;
};

const en: TranslationKeys = {
  "nav.chat": "Chat",
  "nav.dashboard": "Dashboard",
  "nav.automation": "Automation",
  "nav.system": "System",
  "nav.simple.chat": "Chat",
  "nav.simple.status": "Status",
  "nav.simple.settings": "Settings",
  "nav.simple.tools": "Tools",
  "nav.simple.help": "Help",
  "nav.mode.simple": "Simple",
  "nav.mode.advanced": "Advanced",
  "nav.mode.toggle_label": "Navigation mode",
  "tab.chat": "Chat",
  "tab.overview": "Home",
  "tab.dashboard": "Dashboard",
  "tab.channels": "Connections",
  "tab.instances": "Devices",
  "tab.sessions": "Conversations",
  "tab.usage": "Usage",
  "tab.agents": "Agents",
  "tab.skills": "Skills",
  "tab.cron": "Scheduled Tasks",
  "tab.terminal": "Terminal",
  "tab.files": "Files",
  "tab.nodes": "Computers",
  "tab.config": "Settings",
  "tab.debug": "Advanced",
  "tab.heartbeat": "Heartbeat",
  "sub.heartbeat": "Heartbeat analytics and behavior monitoring",
  "hb.title": "Heartbeat Analytics",
  "hb.subtitle": "Real-time heartbeat behavior monitoring",
  "hb.timeline": "Interval Timeline",
  "hb.event_log": "Event Log",
  "hb.no_events": "No events recorded yet",
  "hb.refresh": "Refresh",
  "hb.pause": "Pause",
  "hb.resume": "Resume",
  "hb.loading": "Loading...",
  "hb.new": "NEW",
  "hb.stat_total": "Total runs",
  "hb.stat_ok_rate": "OK rate",
  "hb.stat_alert_rate": "Alert rate",
  "hb.stat_avg_duration": "Avg duration",
  "hb.stat_skipped": "Skipped",
  "hb.stat_failed": "Failed",
  "hb.stat_duplicates": "Duplicates caught",
  "hb.stat_api_saved": "API calls saved",
  "hb.status_sent": "Sent",
  "hb.status_ok-empty": "OK (empty)",
  "hb.status_ok-token": "OK (token)",
  "hb.status_skipped": "Skipped",
  "hb.status_failed": "Failed",
  "tab.logs": "Activity Log",
  "tab.learning": "AI Learning",
  "tab.mind": "Mind",
  "tab.activity-log": "Activity Log (DB)",
  "sub.chat": "Talk directly with your AI assistant.",
  "sub.overview": "See how everything is running at a glance.",
  "sub.dashboard": "Real-time monitoring of nodes, agents, and tasks.",
  "sub.channels": "Connect messaging apps like WhatsApp, Telegram, and Discord.",
  "sub.instances": "See which devices and apps are currently connected.",
  "sub.sessions": "View and manage active conversations.",
  "sub.agents": "Your AI assistants and their configuration.",
  "sub.skills": "Enable or disable what your AI can do.",
  "sub.cron": "Set up tasks that run automatically on a schedule.",
  "sub.terminal": "Run commands on the server without SSH.",
  "sub.files": "Browse, view, and edit files on the server.",
  "sub.nodes": "Manage connected computers and permissions.",
  "sub.config": "Adjust all system preferences in one place.",
  "sub.debug": "Technical details for troubleshooting.",
  "sub.logs": "See what happened recently.",
  "sub.learning": "View AI memory, emotions and knowledge.",
  "sub.mind": "Cognitive architecture monitoring and analytics.",
  "mind.soul": "AI Soul",
  "mind.soul.emotions": "Emotional State",
  "mind.soul.lifecycle": "Lifecycle",
  "mind.soul.personality": "Personality",
  "mind.soul.values": "Core Values",
  "mind.disconnected": "Connect to the gateway to view cognitive state.",
  "mind.plugin_unavailable": "Cognitive Mind plugin is not active",
  "mind.plugin_unavailable_hint": "Enable the autonomous-mind extension to view data.",
  "mind.loading": "Loading cognitive state...",
  "mind.retry": "Retry",
  "mind.sec_overview": "Overview",
  "mind.sec_soul": "Soul",
  "mind.sec_depth": "Depth",
  "mind.sec_thoughts": "Thoughts",
  "mind.sec_workspace": "Workspace",
  "mind.sec_identity": "Identity",
  "mind.sec_analytics": "Analytics",
  "mind.emotion_love": "Love",
  "mind.emotion_anger": "Anger",
  "mind.emotion_sadness": "Sadness",
  "mind.emotion_joy": "Joy",
  "mind.emotion_fear": "Fear",
  "mind.emotion_trust": "Trust",
  "mind.emotion_greed": "Greed",
  "mind.emotion_delusion": "Delusion",
  "mind.mood_valence": "Mood Valence",
  "mind.mood_balanced": "Balanced",
  "mind.mood_unknown": "Unknown",
  "mind.loyalty": "Loyalty",
  "mind.stage_newborn": "Newborn",
  "mind.stage_child": "Child",
  "mind.stage_adolescent": "Adolescent",
  "mind.stage_adult": "Adult",
  "mind.stage_elder": "Elder",
  "mind.stage_transcendent": "Transcendent",
  "mind.meter_health": "Health",
  "mind.meter_energy": "Energy",
  "mind.meter_wisdom": "Wisdom",
  "mind.personality_developing": "Personality traits are developing through conversations...",
  "mind.overview_title": "Cognitive Overview",
  "mind.card_depth": "Cognitive Depth",
  "mind.card_mirror": "MIRROR Threads",
  "mind.card_workspace": "Workspace",
  "mind.card_identity": "Identity",
  "mind.card_goals": "Goals",
  "mind.card_thoughts": "Thoughts",
  "mind.inactive": "Inactive",
  "mind.no_data": "No data",
  "mind.active": "Active",
  "mind.idle": "Idle",
  "mind.waiting": "Waiting for input",
  "mind.entries": "entries",
  "mind.traits": "traits",
  "mind.values_count": "values",
  "mind.active_count": "active",
  "mind.recent_count": "recent",
  "mind.global_memory": "Global working memory",
  "mind.inner_monologue": "Inner monologue stream",
  "mind.no_goals": "No active goals",
  "mind.unavailable": "Unavailable",
  "mind.recent_thoughts": "Recent Thoughts",
  "mind.no_thoughts": "No thoughts yet. The mind is quiet.",
  "mind.depth_title": "Cognitive Depth Analysis",
  "mind.depth_current": "Current Depth",
  "mind.depth_instinctive": "Instinctive",
  "mind.depth_associative": "Associative",
  "mind.depth_analytical": "Analytical",
  "mind.depth_strategic": "Strategic",
  "mind.confidence": "confidence",
  "mind.depth_distribution": "Depth Distribution",
  "mind.recent_classifications": "Recent Classifications",
  "mind.col_depth": "Depth",
  "mind.col_confidence": "Confidence",
  "mind.col_phases": "Phases",
  "mind.col_time": "Time",
  "mind.loading_depth": "Loading depth data...",
  "mind.thoughts_title": "Thought Stream",
  "mind.no_thoughts_yet": "No thoughts recorded yet.",
  "mind.workspace_title": "Global Workspace",
  "mind.ws_entries": "Entries",
  "mind.ws_active_entries": "Active workspace entries",
  "mind.ws_attention": "Attention",
  "mind.ws_top5": "Top 5",
  "mind.ws_salience": "Salience-weighted selection",
  "mind.ws_description":
    "The global workspace holds the agent's current working memory. Entries are ranked by salience and recency.",
  "mind.identity_title": "Identity",
  "mind.id_continuity": "Continuity",
  "mind.id_consistent": "Identity is consistent",
  "mind.id_drift": "Identity drift detected",
  "mind.id_traits": "Self-Model Traits",
  "mind.id_values": "Values",
  "mind.id_no_traits": "No traits developed yet.",
  "mind.id_no_values": "No values established yet.",
  "mind.id_loading": "Loading identity data...",
  "mind.analytics_title": "Analytics",
  "mind.an_classifications": "Classifications",
  "mind.an_override_rate": "Override rate",
  "mind.an_loop_perf": "Loop Performance",
  "mind.an_avg": "avg",
  "mind.an_runs_max": "runs",
  "mind.an_dominant": "Dominant Depth",
  "mind.an_avg_confidence": "Avg confidence",
  "mind.an_phase_failures": "Phase Failures",
  "mind.an_failures": "failures",
  "mind.an_loading": "Loading analytics...",
  "mind.time_just_now": "just now",
  "mind.time_m_ago": "m ago",
  "mind.time_h_ago": "h ago",
  "mind.time_d_ago": "d ago",
  "sub.activity-log": "Full activity log stored in MongoDB (7-day retention).",
  "tab.spawn-monitor": "Spawn Monitor",
  "sub.spawn-monitor": "Monitor orchestrator dispatch and spawned sub-agents.",
  "spawn_monitor.title": "Spawn Monitor",
  "spawn_monitor.sub": "Real-time monitoring of orchestrator/spawn mode dispatches.",
  "spawn_monitor.active_now": "Active",
  "spawn_monitor.total_recent": "Recent",
  "spawn_monitor.avg_runtime": "Avg Runtime",
  "spawn_monitor.success_rate": "Success Rate",
  "spawn_monitor.active_title": "Active Spawns",
  "spawn_monitor.no_active": "No active spawned sub-agents.",
  "spawn_monitor.elapsed": "Elapsed",
  "spawn_monitor.recent_title": "Recent Completions",
  "spawn_monitor.recent_sub": "Completed spawn dispatches (last 30 minutes).",
  "spawn_monitor.no_recent": "No recent spawn completions.",
  "spawn_monitor.col_message": "Message",
  "spawn_monitor.col_status": "Status",
  "spawn_monitor.col_model": "Model",
  "spawn_monitor.col_runtime": "Runtime",
  "spawn_monitor.col_time": "Time",
  "spawn_monitor.events_title": "Live Events",
  "spawn_monitor.events_sub": "Real-time spawn lifecycle events.",
  "spawn_monitor.no_events": "No spawn events yet. Events appear when spawn mode is active.",
  "spawn_monitor.clear_events": "Clear",
  "spawn_monitor.auto_refresh": "Auto-refresh",
  "dashboard.nodes": "Nodes",
  "dashboard.nodes_online": "online",
  "dashboard.agents": "Agents",
  "dashboard.agents_registered": "registered",
  "dashboard.devices": "Devices",
  "dashboard.devices_connected": "connected now",
  "dashboard.realtime": "Live",
  "dashboard.refresh": "Refresh",
  "dashboard.connected_nodes": "Connected Nodes",
  "dashboard.disconnected_nodes": "Disconnected Nodes",
  "dashboard.loading": "Loading nodes...",
  "dashboard.no_nodes": "No nodes connected.",
  "dashboard.commands": "commands",
  "dashboard.node_capabilities": "Node Capabilities",
  "dashboard.node_capabilities_sub": "Available commands and capabilities per node.",
  "dashboard.node": "Node",
  "dashboard.status": "Status",
  "dashboard.capabilities": "Commands",
  "dashboard.uptime": "Connected",
  "dashboard.agents_list": "Agents",
  "dashboard.queue_title": "Command Queue",
  "dashboard.queue_sub": "Active and queued tasks across all processing lanes.",
  "dashboard.queue_active": "active",
  "dashboard.queue_queued": "queued",
  "dashboard.queue_lane": "Lane",
  "dashboard.queue_concurrency": "Max Concurrency",
  "dashboard.queue_idle": "All queues idle.",
  "dashboard.queue_idle_lanes": "Idle lanes",
  "learning.overview_title": "AI Learning Overview",
  "learning.memory_entries": "Memory Entries",
  "learning.emotions_active": "Active Emotions",
  "learning.user_messages": "Messages Analyzed",
  "learning.modules": "Learning Modules",
  "learning.mod_memory": "Memory",
  "learning.mod_memory_desc": "Core memory file for persistent knowledge",
  "learning.mod_soul": "Personality",
  "learning.mod_soul_desc": "Soul file defining persona and voice",
  "learning.mod_emotions": "Emotional Core",
  "learning.mod_emotions_desc": "Emotion tracking and sentiment analysis",
  "learning.mod_user": "User Modeling",
  "learning.mod_user_desc": "Adaptive user profile and preferences",
  "learning.mod_tiered": "Tiered Memory",
  "learning.mod_tiered_desc": "Short-term and long-term memory with forgetting curves",
  "learning.active": "Active",
  "learning.inactive": "Inactive",
  "learning.generate": "Generate",
  "learning.regenerate": "Regenerate",
  "learning.generating": "Generating...",
  "learning.init_all": "Generate All",
  "learning.generate_memory": "Generate Memory File",
  "learning.generate_soul": "Generate Personality",
  "learning.generate_emotions": "Initialize Emotions",
  "learning.generate_user": "Generate User Profile",
  "learning.generate_knowledge": "Organize Knowledge",
  "learning.current_mood": "Current Mood",
  "learning.mood_valence": "Valence",
  "learning.mood_stability": "Stability",
  "learning.mood_energy": "Energy",
  "learning.mood_warmth": "Warmth",
  "learning.memory_title": "Memory",
  "learning.tiered_memory": "Tiered Memory System",
  "learning.short_term": "Short-Term",
  "learning.long_term": "Long-Term",
  "learning.entities": "Entities",
  "learning.entity_graph": "Entity Graph",
  "learning.recent_memories": "Recent Memories",
  "learning.memory_file": "MEMORY.md",
  "learning.no_memory": "No memory data found. The AI has not stored any memories yet.",
  "learning.memory_history": "Memory History",
  "learning.load_history": "Load History",
  "learning.no_history": 'Click "Load History" to view past memory files.',
  "learning.emotions_title": "Emotional State",
  "learning.emotion_vector": "Emotion Vector",
  "learning.vec_valence": "Valence",
  "learning.vec_arousal": "Arousal",
  "learning.vec_dominance": "Dominance",
  "learning.vec_attachment": "Attachment",
  "learning.vec_desire": "Desire",
  "learning.vec_confusion": "Confusion",
  "learning.active_emotions": "Active Emotions",
  "learning.loyalty": "Loyalty & Bond",
  "learning.loyalty_level": "Loyalty Level",
  "learning.bond_strength": "Bond Strength",
  "learning.protective": "Protective Instinct",
  "learning.devotion_style": "Devotion Style",
  "learning.emotional_memories": "Emotional Memories",
  "learning.no_emotions": "Emotional core extension is not installed or has no data.",
  "learning.personality_title": "Personality & Identity",
  "learning.soul_file": "SOUL.md — Persona Definition",
  "learning.identity_file": "IDENTITY.md — Identity Config",
  "learning.no_soul": "No SOUL.md found. The AI is using the default personality.",
  "learning.user_title": "User Understanding",
  "learning.user_profile": "Learned User Profile",
  "learning.comm_style": "Communication Style",
  "learning.expertise": "Expertise Level",
  "learning.response_pref": "Response Preference",
  "learning.mental_state": "Mental State",
  "learning.interests": "Top Interests",
  "learning.interaction_stats": "Interaction Statistics",
  "learning.stat_messages": "Messages",
  "learning.stat_questions": "Questions",
  "learning.stat_commands": "Commands",
  "learning.stat_code": "Code Snippets",
  "learning.user_file": "USER.md",
  "learning.no_user": "No user data found.",
  "learning.knowledge_title": "Long-Term Knowledge",
  "learning.long_term_knowledge": "Consolidated Knowledge",
  "learning.no_knowledge": "No long-term knowledge found yet.",
  "learning.disconnected": "Connect to the gateway to view AI learning data.",
  "learning.loading": "Loading AI learning data...",
  "learning.refresh": "Refresh",
  "learning.sec_overview": "Overview",
  "learning.sec_memory": "Memory",
  "learning.sec_emotions": "Emotions",
  "learning.sec_personality": "Personality",
  "learning.sec_user": "User Model",
  "learning.sec_knowledge": "Knowledge",
  "ks.title": "Knowledge Sources",
  "ks.loading": "Loading...",
  "ks.refresh": "Refresh",
  "ks.add_source": "Add Source",
  "ks.edit_source": "Edit Source",
  "ks.empty_title": "No knowledge sources yet",
  "ks.empty_description":
    "Describe a data source and AI will figure out how to access and collect the data automatically.",
  "ks.tab_sources": "Sources",
  "ks.tab_collected": "Collected",
  "ks.status_idle": "Idle",
  "ks.status_collecting": "Collecting",
  "ks.status_success": "Active",
  "ks.status_error": "Error",
  "ks.disabled": "Disabled",
  "ks.schedule": "Schedule",
  "ks.last_collected": "Last collected",
  "ks.docs_ingested": "Documents",
  "ks.runs": "Runs",
  "ks.collect_now": "Collect now",
  "ks.edit": "Edit",
  "ks.enable": "Enable",
  "ks.disable": "Disable",
  "ks.delete": "Delete",
  "ks.delete_confirm": "Are you sure you want to delete this knowledge source?",
  "ks.field_name": "Source Name",
  "ks.field_name_placeholder": "e.g. HR Dashboard, Sales API, Finance Portal",
  "ks.field_description": "What data source is this?",
  "ks.field_description_placeholder":
    "e.g. Pull gold prices from goldprice.org every hour\ne.g. Login to HR system at hr.company.com and pull new employee list\ne.g. Fetch sales data from https://api.example.com/v1/sales",
  "ks.field_description_hint":
    "Describe the data source — what it is, its URL, and what to collect. AI will analyze and figure out how to access it.",
  "ks.field_credentials": "Credentials (optional)",
  "ks.field_credentials_hint":
    "If login or API key is required, enter them here. These are stored encrypted.",
  "ks.field_username": "Username",
  "ks.field_password": "Password",
  "ks.field_api_key": "API Key",
  "ks.field_interval": "Collect every",
  "ks.minutes": "minutes",
  "ks.cancel": "Cancel",
  "ks.save": "Save",
  "ks.create": "Create",
  "ks.just_now": "Just now",
  "login.title": "Nabe",
  "login.subtitle": "Enter your password to continue",
  "login.password": "Password",
  "login.signin": "Sign In",
  "login.hint": "Press Enter to sign in",
  "topbar.connected": "Connected",
  "topbar.offline": "Offline",
  "topbar.signout": "Sign Out",
  "overview.connection": "Connection",
  "overview.online": "Online",
  "overview.offline": "Offline",
  "overview.running_for": "Running for",
  "overview.not_connected": "Not connected to gateway",
  "overview.channels": "Channels",
  "overview.active_sessions": "Active Sessions",
  "overview.system_info": "System Info",
  "overview.getting_started": "Getting Started",
  "overview.getting_started_sub": "Here are some tips to help you get the most out of Nabe.",
  "overview.tip_connect": "Connect Your Apps",
  "overview.tip_connect_desc": "Go to Connections to link WhatsApp, Telegram, Discord, and more.",
  "overview.tip_chat": "Start a Conversation",
  "overview.tip_chat_desc": "Use the Chat tab to talk to your AI assistant directly.",
  "overview.tip_settings": "Customize Settings",
  "overview.tip_settings_desc": "Visit Settings to adjust your preferences and configuration.",
  "terminal.quick_commands": "Quick Commands",
  "terminal.quick_sub": "Common tasks you can run with one click.",
  "terminal.run_command": "Run a Command",
  "terminal.run_sub": "Type any command below and press Enter or click Run.",
  "terminal.run": "Run",
  "terminal.running": "Running...",
  "terminal.output": "Output",
  "terminal.clear": "Clear",
  "terminal.no_commands": "No commands yet",
  "terminal.no_commands_sub": "Use the Quick Commands above or type your own command.",
  "terminal.not_connected": "Not connected to gateway.",
  "terminal.welcome": "Welcome to Nabe Gateway Terminal",
  "terminal.welcome_hint": "Type a command below or use the quick buttons above.",
  "terminal.status": "Status",
  "terminal.config": "Config",
  "terminal.disk": "Disk",
  "terminal.memory": "Memory",
  "terminal.processes": "Processes",
  "terminal.network": "Network",
  "terminal.uptime": "Uptime",
  "terminal.logs_btn": "Logs",
  "files.quick_access": "Quick Access",
  "files.quick_sub": "Jump to common directories on the server.",
  "files.home": "Home",
  "files.nabe_config": "Nabe Config",
  "files.app_files": "App Files",
  "files.extensions": "Extensions",
  "files.temp": "Temp",
  "files.root": "Root",
  "files.go_up": "Go up one folder",
  "files.refresh": "Refresh",
  "files.show_hidden": "Show hidden files",
  "files.items": "items",
  "files.name": "Name",
  "files.size": "Size",
  "files.modified": "Modified",
  "files.empty": "This folder is empty.",
  "files.loading": "Loading...",
  "files.opening": "Opening file...",
  "files.save": "Save",
  "files.saving": "Saving...",
  "files.close": "Close",
  "files.unsaved": "unsaved changes",
  "files.delete": "Delete",
  "files.delete_confirm": "Delete",
  "files.not_connected": "Not connected to gateway.",
  "config.save": "Save Changes",
  "config.saving": "Saving...",
  "config.undo": "Undo All",
  "config.apply": "Save & Restart",
  "config.applying": "Applying...",
  "config.update": "Check Updates",
  "config.updating": "Checking...",
  "config.visual": "Visual",
  "config.advanced": "Advanced",
  "config.search": "Search settings...",
  "hint.gateway":
    "The central server that connects your AI to messaging apps. Think of it as the brain that routes messages between you and your AI.",
  "hint.websocket":
    "A real-time connection between your browser and the server. It keeps things updated live without refreshing the page.",
  "hint.session_key":
    "A unique name for each conversation. Like a chat room name — different keys keep conversations separate.",
  "hint.thinking_level":
    "How deeply the AI thinks before answering. Higher = slower but more thoughtful. Lower = faster but simpler answers.",
  "hint.block_streaming":
    "How the AI sends its response back. Instead of waiting for the full answer, it sends pieces as they're ready.",
  "hint.block_streaming_break":
    "When to send each piece of the response. 'text_end' sends after each paragraph. 'message_end' waits for the complete answer.",
  "hint.block_streaming_chunk":
    "How to split long responses into smaller pieces. Useful for messaging apps that have character limits.",
  "hint.exec_approvals":
    "Security rules for which commands the AI is allowed to run on your computer. Protects against dangerous operations.",
  "hint.bindings":
    "Which network address the server listens on. 'loopback' = only this computer. 'lan' = any device on your network.",
  "hint.auto_follow":
    "Automatically continue a conversation when new messages arrive, without manually clicking.",
  "hint.agent":
    "An AI assistant with its own personality, tools, and workspace. You can have multiple agents for different tasks.",
  "hint.skill":
    "A specific capability you can give to your AI — like web search, code execution, or image analysis. Enable or disable as needed.",
  "hint.cron":
    "A scheduled task that runs automatically at set times. Like an alarm clock for your AI — it wakes up and does something on schedule.",
  "hint.node":
    "A computer that's paired with the gateway. It can run commands, access files, or provide services to the AI.",
  "hint.instance":
    "A device or app currently connected to the gateway. Shows what's online right now.",
  "hint.channel":
    "A messaging platform connected to your AI — like Telegram, Discord, WhatsApp, or Slack.",
  "hint.tls":
    "Encryption for secure connections (HTTPS). Like a lock on your front door — keeps others from reading your data.",
  "hint.tailscale":
    "A VPN service that creates a private network between your devices. Makes remote access easy and secure.",
  "hint.rate_limit":
    "Limits how many login attempts can be made in a time period. Prevents hackers from guessing your password.",
  "hint.trusted_proxy":
    "A reverse proxy (like Nginx or Cloudflare) that sits in front of the gateway and handles authentication.",
  "hint.sandbox":
    "An isolated container where the AI runs commands safely. Like a playground — nothing it does can harm your real system.",
  "hint.tool_policy":
    "Rules about which tools the AI can use. You can allow or block specific capabilities for security.",
  "hint.subagent":
    "A helper AI spawned by the main agent for parallel work. Like delegating a sub-task to an assistant.",
  "hint.memory_search":
    "The AI's long-term memory. It stores important facts from conversations and can search them later using AI-powered similarity matching.",
  "hint.tts":
    "Text-to-Speech: converts written text into spoken audio. The AI can 'talk' to you with a voice.",
  "hint.vision":
    "The ability for AI to understand and describe images. Send a photo and the AI can tell you what's in it.",
  "hint.ollama":
    "A tool that runs AI models locally on your computer. No cloud needed — your data stays private.",
  "hint.api_key":
    "A secret password that lets the AI connect to a service (like OpenAI or Google). Keep it private — never share it publicly.",
  "hint.model":
    "The AI brain being used. Different models have different strengths — some are fast, some are smart, some can see images.",
  "hint.token":
    "A secret key used to authenticate with the gateway. Like a password but generated automatically.",
  "hint.password_auth":
    "Simple password-based login. Anyone with the password can access the gateway.",
  "hint.webhook":
    "A URL that receives notifications when something happens. Like a doorbell — the service rings it when there's a message.",
  "hint.presence":
    "A signal showing a device is online and connected. Like the green dot on a chat app.",
  "hint.compaction":
    "Cleaning up old conversation history to save space. Keeps the important parts, removes the noise.",
  "hint.funnel":
    "Tailscale Funnel exposes your gateway to the public internet. Anyone with the URL can reach it.",
  "hint.basepath":
    "The URL prefix for the web interface. Use this when running behind a reverse proxy at a sub-path like /nabe.",
  "guide.connect_app_title": "How to Connect a Messaging App",
  "guide.connect_app_steps":
    "1. Go to the Connections page\n2. Click the app you want to connect (e.g. Telegram)\n3. Follow the setup instructions for that app\n4. Enter the required token or credentials\n5. Click Save — the connection will start automatically",
  "guide.setup_agent_title": "How to Set Up an AI Agent",
  "guide.setup_agent_steps":
    "1. Go to the Agents page\n2. Your default agent 'main' is already created\n3. Click it to edit its personality (IDENTITY.md)\n4. Choose which AI model it should use\n5. Enable the tools/skills it needs",
  "guide.add_skill_title": "How to Enable a Skill",
  "guide.add_skill_steps":
    "1. Go to the Skills page\n2. Browse available skills\n3. Toggle the switch to enable one\n4. Some skills need an API key — enter it when prompted\n5. The AI can now use this skill in conversations",
  "guide.schedule_task_title": "How to Schedule a Task",
  "guide.schedule_task_steps":
    "1. Go to Scheduled Tasks\n2. Click 'Add Task'\n3. Set a cron expression (e.g. '0 9 * * *' = every day at 9 AM)\n4. Write what the AI should do\n5. Save — it will run automatically at the scheduled time",
  // Overview page
  "overview.sessions_now": "Open chat sessions right now",
  "overview.scheduled_tasks": "Scheduled Tasks",
  "overview.scheduled_none": "None",
  "overview.scheduled_active": "Active",
  "overview.scheduled_paused": "Paused",
  "overview.attention": "Something Needs Attention",
  "overview.auth_required":
    "This gateway requires auth. Add a token or password, then click Connect.",
  "overview.auth_hint_dashboard": "open the Control UI",
  "overview.auth_hint_token": "set token",
  "overview.auth_failed":
    "Auth failed. Update the token or password in Control UI settings, then click Connect.",
  "overview.docs_auth": "Docs: Control UI auth",
  "overview.insecure_context":
    "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open",
  "overview.insecure_stay": "If you must stay on HTTP, set",
  "overview.docs_tailscale": "Docs: Tailscale Serve",
  "overview.docs_insecure": "Docs: Insecure HTTP",
  "overview.connection_settings": "Connection Settings",
  "overview.connection_sub": "How this dashboard connects to your gateway.",
  "overview.server_address": "Server Address",
  "overview.password": "Password",
  "overview.enter_password": "Enter your password",
  "overview.session_name": "Session Name",
  "overview.reconnect": "Reconnect",
  "overview.connect_btn": "Connect",
  "overview.refresh_data": "Refresh Data",
  "overview.system_sub": "Current status of your gateway.",
  "overview.running_time": "Running Time",
  "overview.connected_devices": "Connected Devices",
  "overview.last_refresh": "Last Refresh",
  "overview.never": "Never",
  "overview.all_good":
    "Everything is running smoothly. Go to Connections to set up messaging apps.",
  // Channels page
  "channels.details": "Connection Details",
  "channels.details_sub": "Detailed status from each connected messaging app.",
  "channels.never_checked": "Never checked",
  "channels.no_snapshot": "No snapshot yet.",
  "channels.status_sub": "Status and settings for this connection.",
  "channels.configured": "Configured",
  "channels.running": "Running",
  "channels.connected": "Connected",
  "channels.last_inbound": "Last inbound",
  "channels.form_unavailable": "Settings form not available.",
  "channels.form_unavailable_sub":
    "The configuration form could not be loaded. You can edit settings manually in the Settings tab.",
  "channels.plugin_needed": "This connection's settings form is not yet available.",
  "channels.plugin_needed_sub":
    "The plugin for this connection may need to be enabled first. Check the Settings tab or restart the gateway.",
  "channels.loading_schema": "Loading config schema…",
  "channels.save_changes": "Save Changes",
  "channels.undo_changes": "Undo Changes",
  "channels.saving": "Saving...",

  // Channel picker
  "picker.title": "Choose a Messaging App",
  "picker.subtitle": "Select a messaging app to connect with your AI.",
  "picker.add": "Add",
  "picker.add_channel": "Add Channel",
  "picker.all_added": "All available channels have been added.",
  "picker.already_added": "Already added",
  "picker.empty_state": 'No channels configured yet. Click "Add Channel" to get started.',
  "picker.easy": "Easy",
  "picker.medium": "Medium",
  "picker.hard": "Advanced",
  "picker.enabled": "Enabled",

  // Channel setup guide
  "guide.title": "Setup Guide —",
  "guide.full_docs": "Read full documentation",
  // Config page
  "config.general": "General",
  "config.updates": "Updates",
  "config.ai_agents": "AI Agents",
  "config.security": "Security",
  "config.messaging_apps": "Messaging Apps",
  "config.messages": "Messages",
  "config.commands": "Commands",
  "config.hooks": "Automation Hooks",
  "config.skills_section": "Skills",
  "config.tools": "Tools",
  "config.server": "Server",
  "config.setup_guide": "Setup Guide",
  "config.settings": "Settings",
  "config.all_settings": "All Settings",
  "config.unsaved": "Unsaved changes",
  "config.no_changes": "No changes",
  "config.discard_title": "Discard changes and reload from file",
  "config.save_title": "Save changes to the configuration file",
  "config.restart_title": "Save and restart affected services",
  "config.update_title": "Check for software updates",
  "config.loading_schema": "Loading schema…",
  "config.advanced_warning": "",
  "config.raw_json5": "Raw JSON5",
  "config.all": "All",
  // Agents page
  "agents.title": "Agents",
  "agents.configured": "configured",
  "agents.no_agents": "No agents found.",
  "agents.select": "Select an agent",
  "agents.select_sub": "Pick an agent to inspect its workspace and tools.",
  "agents.tab_overview": "Overview",
  "agents.tab_files": "Files",
  "agents.tab_tools": "Tools",
  "agents.tab_skills": "Skills",
  "agents.tab_channels": "Channels",
  "agents.tab_cron": "Cron Jobs",
  "agents.tab_runtime": "Runtime",
  "agents.runtime_title": "Runtime Activity",
  "agents.runtime_sub": "Active sub-agents, queue state, and recent completions.",
  "agents.runtime_active": "Active Sub-Agents",
  "agents.runtime_no_active": "No sub-agents running.",
  "agents.runtime_elapsed": "Elapsed",
  "agents.runtime_queue": "Queue State",
  "agents.runtime_queue_unavailable": "Queue status unavailable.",
  "agents.runtime_slots": "slots",
  "agents.runtime_recent": "Recent Completions",
  "agents.runtime_recent_sub": "Sub-agent runs completed in the last 30 minutes.",
  "agents.runtime_no_recent": "No recent completions.",
  "agents.runtime_col_label": "Label",
  "agents.runtime_col_status": "Status",
  "agents.runtime_col_model": "Model",
  "agents.runtime_col_runtime": "Runtime",
  "agents.runtime_watchdog": "Watchdog",
  "agents.runtime_watchdog_on": "running",
  "agents.runtime_watchdog_off": "stopped",
  "agents.runtime_scans": "Scans",
  "agents.runtime_aborted": "Aborted",
  "agents.runtime_registered": "Registered",
  "agents.overview_sub": "Workspace paths and identity metadata.",
  "agents.workspace": "Workspace",
  "agents.primary_model": "Primary Model",
  "agents.identity_name": "Identity Name",
  "agents.default": "Default",
  "agents.identity_emoji": "Identity Emoji",
  "agents.skills_filter": "Skills Filter",
  "agents.selected": "selected",
  "agents.all_skills": "all skills",
  "agents.model_selection": "Model Selection",
  "agents.primary_default": "Primary model",
  "agents.inherit_default": "Inherit default",
  "agents.fallbacks": "Fallbacks (comma-separated)",
  "agents.fallbacks_placeholder": "provider/model, provider/model",
  "agents.reload_config": "Reload Config",
  "agents.api_keys_title": "Provider API Keys",
  "agents.api_keys_sub":
    "API keys for LLM providers. Keys are stored encrypted and shared across all agents.",
  "agents.api_key_set": "Key is set (hidden)",
  "agents.api_key_placeholder": "Enter API key...",
  "agents.catalog_title": "Model Catalog",
  "agents.catalog_sub": "Models available for selection as primary or fallback.",
  "agents.catalog_model_id": "Model ID",
  "agents.catalog_alias": "Alias",
  "agents.api_key_label": "API Key",
  "agents.catalog_actions": "Actions",
  "agents.catalog_add": "Add model",
  "agents.catalog_add_placeholder": "provider/model-name",
  "agents.catalog_alias_placeholder": "Short alias (optional)",
  "agents.catalog_empty": "No models configured. Add one below.",
  "agents.catalog_confirm_remove": "This model is used as primary or fallback. Remove anyway?",
  "agents.catalog_provider": "Provider",
  "agents.catalog_model": "Model",
  "agents.catalog_no_providers": "No providers available. Add an API key first.",
  "agents.catalog_select_provider": "Select provider…",
  "agents.catalog_select_model": "Select model…",
  "agents.fallback_title": "Fallback Models",
  "agents.fallback_sub": "Tried in order if the primary model fails.",
  "agents.fallback_add": "Add fallback",
  "agents.fallback_empty": "No fallback models configured.",
  "agents.custom_env_title": "Custom Environment Variables",
  "agents.custom_env_sub": "Additional API keys or env vars for custom providers.",
  "agents.custom_env_add": "Add variable",
  "agents.custom_env_name_placeholder": "VARIABLE_NAME",
  "agents.custom_env_value_placeholder": "Value...",
  // Sessions page
  "sessions.title": "Sessions",
  "sessions.title_sub": "Active session keys and per-session overrides.",
  "sessions.active_within": "Active within (minutes)",
  "sessions.limit": "Limit",
  "sessions.include_global": "Include global",
  "sessions.include_unknown": "Include unknown",
  "sessions.store": "Store",
  "sessions.col_key": "Key",
  "sessions.col_label": "Label",
  "sessions.col_kind": "Kind",
  "sessions.col_updated": "Updated",
  "sessions.col_tokens": "Tokens",
  "sessions.col_thinking": "Thinking",
  "sessions.col_verbose": "Verbose",
  "sessions.col_reasoning": "Reasoning",
  "sessions.col_actions": "Actions",
  "sessions.no_sessions": "No sessions found.",
  "sessions.optional": "(optional)",
  "sessions.inherit": "inherit",
  "sessions.task_log": "Task Log",
  "sessions.no_messages": "No messages in this session.",
  "sessions.messages_label": "messages",
  // Cron page
  "cron.title": "Scheduler",
  "cron.title_sub": "Gateway-owned cron scheduler status.",
  "cron.enabled": "Enabled",
  "cron.jobs": "Jobs",
  "cron.next_wake": "Next wake",
  "cron.new_job": "New Job",
  "cron.new_job_sub": "Create a scheduled wakeup or agent run.",
  "cron.name": "Name",
  "cron.description": "Description",
  "cron.agent_id": "Agent ID",
  "cron.schedule": "Schedule",
  "cron.every": "Every",
  "cron.at": "At",
  "cron.cron_expr": "Cron",
  "cron.session": "Session",
  "cron.wake_mode": "Wake mode",
  "cron.payload": "Payload",
  "cron.main": "Main",
  "cron.isolated": "Isolated",
  "cron.now": "Now",
  "cron.next_heartbeat": "Next heartbeat",
  "cron.system_event": "System event",
  "cron.agent_turn": "Agent turn",
  "cron.delivery": "Delivery",
  "cron.timeout": "Timeout (seconds)",
  "cron.channel": "Channel",
  "cron.to": "To",
  "cron.announce": "Announce summary (default)",
  "cron.none_internal": "None (internal)",
  "cron.add_job": "Add job",
  "cron.jobs_title": "Jobs",
  "cron.jobs_sub": "All scheduled jobs stored in the gateway.",
  "cron.no_jobs": "No jobs yet.",
  "cron.run_history": "Run history",
  "cron.run_history_sub": "Latest runs for",
  "cron.select_job": "Select a job to inspect run history.",
  "cron.no_runs": "No runs yet.",
  "cron.run_at": "Run at",
  "cron.expression": "Expression",
  "cron.timezone": "Timezone (optional)",
  "cron.unit": "Unit",
  "cron.unit_minutes": "Minutes",
  "cron.unit_hours": "Hours",
  "cron.unit_days": "Days",
  "cron.system": "System",
  "cron.system_text": "System text",
  "cron.agent_message": "Agent message",
  "cron.prompt": "Prompt",
  "cron.run_btn": "Run",
  "cron.history_btn": "History",
  "cron.remove": "Remove",
  "cron.status": "Status",
  "cron.next": "Next",
  "cron.last": "Last",
  "cron.open_run_chat": "Open run chat",
  // Skills page
  "skills.title": "Skills",
  "skills.title_sub": "Bundled, managed, and workspace skills.",
  "skills.filter": "Filter",
  "skills.search": "Search skills",
  "skills.shown": "shown",
  "skills.no_skills": "No skills found.",
  "skills.missing": "Missing",
  "skills.reason": "Reason",
  "skills.api_key": "API key",
  "skills.save_key": "Save key",
  // Nodes page
  "nodes.title": "Nodes",
  "nodes.title_sub": "Paired devices and live links.",
  "nodes.no_nodes": "No nodes found.",
  "nodes.devices": "Devices",
  "nodes.devices_sub": "Pairing requests + role tokens.",
  "nodes.pending": "Pending",
  "nodes.paired": "Paired",
  "nodes.no_paired": "No paired devices.",
  "nodes.role": "role",
  "nodes.roles": "roles",
  "nodes.scopes": "scopes",
  "nodes.approve": "Approve",
  "nodes.reject": "Reject",
  "nodes.tokens_none": "Tokens: none",
  "nodes.tokens": "Tokens",
  "nodes.revoked": "revoked",
  "nodes.active_token": "active",
  "nodes.rotate": "Rotate",
  "nodes.revoke": "Revoke",
  "nodes.exec_binding": "Exec node binding",
  "nodes.exec_binding_sub": "Pin agents to a specific node when using exec host=node.",
  "nodes.switch_form": "Switch the Config tab to Form mode to edit bindings here.",
  "nodes.load_config": "Load config",
  "nodes.default_binding": "Default binding",
  "nodes.default_binding_sub": "Used when agents do not override a node binding.",
  "nodes.any_node": "Any node",
  "nodes.no_exec_nodes": "No nodes with system.run available.",
  "nodes.no_agents_found": "No agents found.",
  "nodes.default_agent": "default agent",
  "nodes.uses_default": "uses default",
  "nodes.node": "Node",
  "nodes.override": "override",
  "nodes.binding": "Binding",
  "nodes.use_default": "Use default",
  "nodes.status_paired": "paired",
  "nodes.status_unpaired": "unpaired",
  "nodes.status_connected": "connected",
  "nodes.status_offline": "offline",
  // Instances page
  "instances.title": "Connected Instances",
  "instances.title_sub": "Presence beacons from the gateway and clients.",
  "instances.no_instances": "No instances reported yet.",
  "instances.last_input": "Last input",
  "instances.reason": "Reason",
  // Debug page
  "debug.snapshots": "Snapshots",
  "debug.snapshots_sub": "Status, health, and heartbeat data.",
  "debug.status": "Status",
  "debug.health": "Health",
  "debug.last_heartbeat": "Last heartbeat",
  "debug.security_audit": "Security audit",
  "debug.for_details": "for details.",
  "debug.manual_rpc": "Manual RPC",
  "debug.manual_rpc_sub": "Send a raw gateway method with JSON params.",
  "debug.method": "Method",
  "debug.params": "Params (JSON)",
  "debug.call": "Call",
  "debug.models": "Models",
  "debug.models_sub": "Catalog from models.list.",
  "debug.event_log": "Event Log",
  "debug.event_log_sub": "Latest gateway events.",
  "debug.no_events": "No events yet.",
  // Logs page
  "logs.title": "Logs",
  "logs.title_sub": "Gateway file logs (JSONL).",
  "logs.export": "Export",
  "logs.filter": "Filter",
  "logs.search": "Search logs",
  "logs.auto_follow": "Auto-follow",
  "logs.truncated": "Log output truncated; showing latest chunk.",
  "logs.no_entries": "No log entries.",
  // Chat page
  "chat.compacting": "Compacting context...",
  "chat.compacted": "Context compacted",
  "chat.attachment_preview": "Attachment preview",
  "chat.remove_attachment": "Remove attachment",
  "chat.placeholder": "Add a message or paste more images...",
  "chat.placeholder_shift": "Message (↩ to send, Shift+↩ for line breaks, paste images)",
  "chat.not_connected": "Connect to the gateway to start chatting…",
  "chat.loading": "Loading chat…",
  "chat.exit_focus": "Exit focus mode",
  "chat.queued": "Queued",
  "chat.remove_queued": "Remove queued message",
  "chat.image_count": "Image",
  "chat.new_messages": "New messages",
  "chat.message": "Message",
  "chat.stop": "Stop",
  "chat.new_session": "New session",
  "chat.queue_btn": "Queue",
  "chat.queued_batch": "Will send together",
  "chat.send": "Send",
  "chat.history_hidden": "messages hidden",
  "chat.compaction_divider": "Compaction",
  // App chrome
  "app.brand": "NABE",
  "app.subtitle": "Gateway Dashboard",
  "app.expand_sidebar": "Expand sidebar",
  "app.collapse_sidebar": "Collapse sidebar",
  "app.resources": "Resources",
  "app.docs": "Docs",
  "app.refresh_chat": "Refresh chat data",
  "app.disabled_onboarding": "Disabled during onboarding",
  "app.toggle_thinking": "Toggle assistant thinking/working output",
  "app.toggle_focus": "Toggle focus mode (hide sidebar + page header)",
  "app.theme_system": "System",
  "app.theme_light": "Light",
  "app.theme_dark": "Dark",
  "app.theme": "Theme",
  // Common
  "common.loading": "Loading...",
  "common.error": "Error",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.yes": "Yes",
  "common.no": "No",
  "common.na": "n/a",
  "common.save": "Save",
  "common.saving": "Saving...",
  "common.refresh": "Refresh",
  "common.refreshing": "Refreshing...",
  "common.delete": "Delete",
  "common.enable": "Enable",
  "common.disable": "Disable",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",
  "common.language": "Language",
  "common.what_is_this": "What is this?",
  "common.learn_more": "Learn more",
  "common.step": "Step",
  "common.docs": "Docs",
  "common.resources": "Resources",
  "common.agent": "agent",
  // Quick Settings
  "qs.title": "Quick Settings",
  "qs.subtitle": "Commonly used settings. For all options, choose a section from the sidebar.",
  "qs.ai": "AI Assistant",
  "qs.ai.model": "AI Model",
  "qs.ai.model_help": "The AI model that powers your assistant's responses",
  "qs.ai.name": "Assistant Name",
  "qs.ai.name_help": "How your assistant introduces itself",
  "qs.ai.workspace": "Workspace Folder",
  "qs.ai.workspace_help": "Folder where files and projects are stored",
  "qs.conn": "Connection",
  "qs.conn.port": "Port Number",
  "qs.conn.port_help": "Network port for connecting to the gateway",
  "qs.conn.bind": "Who Can Connect",
  "qs.conn.bind_help": "Controls which devices can reach the gateway",
  "qs.conn.bind.loopback": "This computer only",
  "qs.conn.bind.lan": "Devices on same network",
  "qs.conn.bind.tailnet": "Internet (via Tailscale)",
  "qs.conn.bind.auto": "Automatic",
  "qs.conn.auth": "Password Protection",
  "qs.conn.auth_help": "Require a password to connect",
  "qs.conn.password": "Password",
  "qs.apps": "Messaging Apps",
  "qs.apps.subtitle": "Connect your assistant to messaging platforms",
  "qs.apps.configure": "Configure",
  "qs.updates": "Updates",
  "qs.updates.channel": "Release Channel",
  "qs.updates.channel_help": "How quickly you receive new versions",
  "qs.updates.auto": "Check Automatically",
  "qs.updates.auto_help": "Look for updates when the gateway starts",
  "qs.log": "Logging",
  "qs.log.level": "Detail Level",
  "qs.log.level_help": "How much detail to record in logs",
  "qs.all_settings": "View All Settings",
};

const th: TranslationKeys = {
  "nav.chat": "แชท",
  "nav.dashboard": "แดชบอร์ด",
  "nav.automation": "อัตโนมัติ",
  "nav.system": "ระบบ",
  "nav.simple.chat": "คุยกัน",
  "nav.simple.status": "สถานะ",
  "nav.simple.settings": "ตั้งค่า",
  "nav.simple.tools": "เครื่องมือ",
  "nav.simple.help": "ช่วยเหลือ",
  "nav.mode.simple": "ง่าย",
  "nav.mode.advanced": "ขั้นสูง",
  "nav.mode.toggle_label": "โหมดเมนู",
  "tab.chat": "แชท",
  "tab.overview": "หน้าหลัก",
  "tab.dashboard": "แดชบอร์ด",
  "tab.channels": "การเชื่อมต่อ",
  "tab.instances": "อุปกรณ์",
  "tab.sessions": "การสนทนา",
  "tab.usage": "การใช้งาน",
  "tab.agents": "เอเจนต์",
  "tab.skills": "ความสามารถ",
  "tab.cron": "งานตั้งเวลา",
  "tab.terminal": "เทอร์มินัล",
  "tab.files": "ไฟล์",
  "tab.nodes": "คอมพิวเตอร์",
  "tab.config": "ตั้งค่า",
  "tab.debug": "ขั้นสูง",
  "tab.heartbeat": "Heartbeat",
  "sub.heartbeat": "วิเคราะห์พฤติกรรม Heartbeat แบบเรียลไทม์",
  "hb.title": "Heartbeat Analytics",
  "hb.subtitle": "ตรวจสอบและวิเคราะห์พฤติกรรม Heartbeat แบบเรียลไทม์",
  "hb.timeline": "ไทม์ไลน์",
  "hb.event_log": "บันทึกเหตุการณ์",
  "hb.no_events": "ยังไม่มีเหตุการณ์",
  "hb.refresh": "รีเฟรช",
  "hb.pause": "หยุด",
  "hb.resume": "เริ่มใหม่",
  "hb.loading": "กำลังโหลด...",
  "hb.new": "ใหม่",
  "hb.stat_total": "รันทั้งหมด",
  "hb.stat_ok_rate": "อัตรา OK",
  "hb.stat_alert_rate": "อัตราแจ้งเตือน",
  "hb.stat_avg_duration": "ระยะเวลาเฉลี่ย",
  "hb.stat_skipped": "ข้าม",
  "hb.stat_failed": "ล้มเหลว",
  "hb.stat_duplicates": "จับซ้ำได้",
  "hb.stat_api_saved": "ประหยัด API calls",
  "hb.status_sent": "ส่งแล้ว",
  "hb.status_ok-empty": "OK (ว่าง)",
  "hb.status_ok-token": "OK (token)",
  "hb.status_skipped": "ข้าม",
  "hb.status_failed": "ล้มเหลว",
  "tab.logs": "บันทึกกิจกรรม",
  "tab.learning": "การเรียนรู้ AI",
  "tab.mind": "สมอง",
  "tab.activity-log": "บันทึกกิจกรรม (DB)",
  "sub.chat": "พูดคุยกับ AI ผู้ช่วยของคุณโดยตรง",
  "sub.overview": "ดูภาพรวมการทำงานทั้งหมดในที่เดียว",
  "sub.dashboard": "ตรวจสอบ Node, Agent และงานแบบเรียลไทม์",
  "sub.channels": "เชื่อมต่อแอปแชท เช่น WhatsApp, Telegram, Discord",
  "sub.instances": "ดูอุปกรณ์และแอปที่เชื่อมต่ออยู่",
  "sub.sessions": "ดูและจัดการการสนทนาที่กำลังดำเนินอยู่",
  "sub.agents": "AI ผู้ช่วยและการตั้งค่าของพวกเขา",
  "sub.skills": "เปิด/ปิดความสามารถของ AI",
  "sub.cron": "ตั้งเวลางานให้ทำงานอัตโนมัติ",
  "sub.terminal": "รันคำสั่งบนเซิร์ฟเวอร์โดยไม่ต้อง SSH",
  "sub.files": "เรียกดู แก้ไข ไฟล์บนเซิร์ฟเวอร์",
  "sub.nodes": "จัดการคอมพิวเตอร์ที่เชื่อมต่อ",
  "sub.config": "ปรับแต่งการตั้งค่าทั้งหมดในที่เดียว",
  "sub.debug": "รายละเอียดทางเทคนิคสำหรับแก้ปัญหา",
  "sub.logs": "ดูสิ่งที่เกิดขึ้นล่าสุด",
  "sub.learning": "ดูหน่วยความจำ อารมณ์ และความรู้ของ AI",
  "sub.mind": "ติดตามและวิเคราะห์สถาปัตยกรรมความคิด",
  "mind.soul": "จิตวิญญาณ AI",
  "mind.soul.emotions": "สภาวะอารมณ์",
  "mind.soul.lifecycle": "วงจรชีวิต",
  "mind.soul.personality": "บุคลิกภาพ",
  "mind.soul.values": "ค่านิยมหลัก",
  "mind.disconnected": "เชื่อมต่อ gateway เพื่อดูสถานะความคิด",
  "mind.plugin_unavailable": "ปลั๊กอิน Cognitive Mind ยังไม่ทำงาน",
  "mind.plugin_unavailable_hint": "เปิดใช้งาน autonomous-mind extension เพื่อดูข้อมูล",
  "mind.loading": "กำลังโหลดสถานะความคิด...",
  "mind.retry": "ลองอีกครั้ง",
  "mind.sec_overview": "ภาพรวม",
  "mind.sec_soul": "จิตวิญญาณ",
  "mind.sec_depth": "ความลึก",
  "mind.sec_thoughts": "ความคิด",
  "mind.sec_workspace": "พื้นที่ทำงาน",
  "mind.sec_identity": "ตัวตน",
  "mind.sec_analytics": "วิเคราะห์",
  "mind.emotion_love": "รัก",
  "mind.emotion_anger": "โกรธ",
  "mind.emotion_sadness": "เศร้า",
  "mind.emotion_joy": "สุข",
  "mind.emotion_fear": "กลัว",
  "mind.emotion_trust": "ไว้ใจ",
  "mind.emotion_greed": "โลภ",
  "mind.emotion_delusion": "หลง",
  "mind.mood_valence": "ทิศทางอารมณ์",
  "mind.mood_balanced": "สมดุล",
  "mind.mood_unknown": "ไม่ทราบ",
  "mind.loyalty": "ความจงรักภักดี",
  "mind.stage_newborn": "เกิด",
  "mind.stage_child": "เด็ก",
  "mind.stage_adolescent": "วัยรุ่น",
  "mind.stage_adult": "ผู้ใหญ่",
  "mind.stage_elder": "ผู้เฒ่า",
  "mind.stage_transcendent": "อมตะ",
  "mind.meter_health": "สุขภาพ",
  "mind.meter_energy": "พลังงาน",
  "mind.meter_wisdom": "ปัญญา",
  "mind.personality_developing": "บุคลิกภาพกำลังพัฒนาผ่านบทสนทนา...",
  "mind.overview_title": "ภาพรวมความคิด",
  "mind.card_depth": "ความลึกความคิด",
  "mind.card_mirror": "MIRROR Threads",
  "mind.card_workspace": "พื้นที่ทำงาน",
  "mind.card_identity": "ตัวตน",
  "mind.card_goals": "เป้าหมาย",
  "mind.card_thoughts": "ความคิด",
  "mind.inactive": "ไม่ทำงาน",
  "mind.no_data": "ไม่มีข้อมูล",
  "mind.active": "ทำงาน",
  "mind.idle": "พัก",
  "mind.waiting": "รอข้อมูล",
  "mind.entries": "รายการ",
  "mind.traits": "ลักษณะ",
  "mind.values_count": "ค่านิยม",
  "mind.active_count": "ใช้งาน",
  "mind.recent_count": "ล่าสุด",
  "mind.global_memory": "หน่วยความจำทำงาน",
  "mind.inner_monologue": "กระแสบทสนทนาภายใน",
  "mind.no_goals": "ไม่มีเป้าหมาย",
  "mind.unavailable": "ไม่พร้อมใช้งาน",
  "mind.recent_thoughts": "ความคิดล่าสุด",
  "mind.no_thoughts": "ยังไม่มีความคิด จิตใจสงบ",
  "mind.depth_title": "วิเคราะห์ความลึกความคิด",
  "mind.depth_current": "ความลึกปัจจุบัน",
  "mind.depth_instinctive": "สัญชาตญาณ",
  "mind.depth_associative": "เชื่อมโยง",
  "mind.depth_analytical": "วิเคราะห์",
  "mind.depth_strategic": "กลยุทธ์",
  "mind.confidence": "ความมั่นใจ",
  "mind.depth_distribution": "การกระจายความลึก",
  "mind.recent_classifications": "การจำแนกล่าสุด",
  "mind.col_depth": "ความลึก",
  "mind.col_confidence": "ความมั่นใจ",
  "mind.col_phases": "ขั้นตอน",
  "mind.col_time": "เวลา",
  "mind.loading_depth": "กำลังโหลดข้อมูลความลึก...",
  "mind.thoughts_title": "กระแสความคิด",
  "mind.no_thoughts_yet": "ยังไม่มีบันทึกความคิด",
  "mind.workspace_title": "พื้นที่ทำงานรวม",
  "mind.ws_entries": "รายการ",
  "mind.ws_active_entries": "รายการในพื้นที่ทำงาน",
  "mind.ws_attention": "ความสนใจ",
  "mind.ws_top5": "5 อันดับ",
  "mind.ws_salience": "คัดเลือกตามความสำคัญ",
  "mind.ws_description": "พื้นที่ทำงานรวมเก็บหน่วยความจำทำงานปัจจุบันของ AI จัดอันดับตามความสำคัญและความใหม่",
  "mind.identity_title": "ตัวตน",
  "mind.id_continuity": "ความต่อเนื่อง",
  "mind.id_consistent": "ตัวตนสอดคล้อง",
  "mind.id_drift": "ตรวจพบการเปลี่ยนแปลงตัวตน",
  "mind.id_traits": "ลักษณะตัวตน",
  "mind.id_values": "ค่านิยม",
  "mind.id_no_traits": "ยังไม่มีลักษณะที่พัฒนา",
  "mind.id_no_values": "ยังไม่มีค่านิยมที่กำหนด",
  "mind.id_loading": "กำลังโหลดข้อมูลตัวตน...",
  "mind.analytics_title": "วิเคราะห์",
  "mind.an_classifications": "การจำแนก",
  "mind.an_override_rate": "อัตราแทนที่",
  "mind.an_loop_perf": "ประสิทธิภาพลูป",
  "mind.an_avg": "เฉลี่ย",
  "mind.an_runs_max": "ครั้ง",
  "mind.an_dominant": "ความลึกหลัก",
  "mind.an_avg_confidence": "ความมั่นใจเฉลี่ย",
  "mind.an_phase_failures": "ขั้นตอนล้มเหลว",
  "mind.an_failures": "ล้มเหลว",
  "mind.an_loading": "กำลังโหลดการวิเคราะห์...",
  "mind.time_just_now": "เมื่อสักครู่",
  "mind.time_m_ago": "นาทีที่แล้ว",
  "mind.time_h_ago": "ชม.ที่แล้ว",
  "mind.time_d_ago": "วันที่แล้ว",
  "sub.activity-log": "บันทึกกิจกรรมทั้งหมดจาก MongoDB (เก็บ 7 วัน)",
  "tab.spawn-monitor": "ตัวติดตาม Spawn",
  "sub.spawn-monitor": "ติดตามการทำงานของ orchestrator และ sub-agent ที่ถูก spawn",
  "spawn_monitor.title": "ตัวติดตาม Spawn",
  "spawn_monitor.sub": "ติดตามการทำงานของ orchestrator/spawn mode แบบเรียลไทม์",
  "spawn_monitor.active_now": "กำลังทำงาน",
  "spawn_monitor.total_recent": "ล่าสุด",
  "spawn_monitor.avg_runtime": "เวลาเฉลี่ย",
  "spawn_monitor.success_rate": "อัตราสำเร็จ",
  "spawn_monitor.active_title": "Spawn ที่กำลังทำงาน",
  "spawn_monitor.no_active": "ไม่มี sub-agent ที่กำลังทำงาน",
  "spawn_monitor.elapsed": "เวลาผ่านไป",
  "spawn_monitor.recent_title": "เสร็จสิ้นล่าสุด",
  "spawn_monitor.recent_sub": "การ dispatch แบบ spawn ที่เสร็จสิ้นแล้ว (30 นาทีล่าสุด)",
  "spawn_monitor.no_recent": "ยังไม่มีการ spawn เสร็จสิ้น",
  "spawn_monitor.col_message": "ข้อความ",
  "spawn_monitor.col_status": "สถานะ",
  "spawn_monitor.col_model": "โมเดล",
  "spawn_monitor.col_runtime": "เวลาทำงาน",
  "spawn_monitor.col_time": "เวลา",
  "spawn_monitor.events_title": "เหตุการณ์สด",
  "spawn_monitor.events_sub": "เหตุการณ์ spawn lifecycle แบบเรียลไทม์",
  "spawn_monitor.no_events": "ยังไม่มีเหตุการณ์ spawn จะปรากฏเมื่อ spawn mode เปิดใช้งาน",
  "spawn_monitor.clear_events": "ล้าง",
  "spawn_monitor.auto_refresh": "รีเฟรชอัตโนมัติ",
  "dashboard.nodes": "โหนด",
  "dashboard.nodes_online": "ออนไลน์",
  "dashboard.agents": "เอเจนต์",
  "dashboard.agents_registered": "ลงทะเบียนแล้ว",
  "dashboard.devices": "อุปกรณ์",
  "dashboard.devices_connected": "เชื่อมต่ออยู่",
  "dashboard.realtime": "เรียลไทม์",
  "dashboard.refresh": "รีเฟรช",
  "dashboard.connected_nodes": "โหนดที่เชื่อมต่อ",
  "dashboard.disconnected_nodes": "โหนดที่ไม่ได้เชื่อมต่อ",
  "dashboard.loading": "กำลังโหลดโหนด...",
  "dashboard.no_nodes": "ไม่มีโหนดเชื่อมต่อ",
  "dashboard.commands": "คำสั่ง",
  "dashboard.node_capabilities": "ความสามารถของโหนด",
  "dashboard.node_capabilities_sub": "คำสั่งและความสามารถที่พร้อมใช้งานของแต่ละโหนด",
  "dashboard.node": "โหนด",
  "dashboard.status": "สถานะ",
  "dashboard.capabilities": "คำสั่ง",
  "dashboard.uptime": "เชื่อมต่อ",
  "dashboard.agents_list": "เอเจนต์",
  "dashboard.queue_title": "คิวคำสั่ง",
  "dashboard.queue_sub": "งานที่กำลังทำและรอคิวในทุกเลน",
  "dashboard.queue_active": "กำลังทำ",
  "dashboard.queue_queued": "รอคิว",
  "dashboard.queue_lane": "เลน",
  "dashboard.queue_concurrency": "ทำพร้อมกันสูงสุด",
  "dashboard.queue_idle": "ทุกคิวว่าง",
  "dashboard.queue_idle_lanes": "เลนว่าง",
  "learning.overview_title": "ภาพรวมการเรียนรู้ AI",
  "learning.memory_entries": "รายการความจำ",
  "learning.emotions_active": "อารมณ์ที่ใช้งาน",
  "learning.user_messages": "ข้อความที่วิเคราะห์",
  "learning.modules": "โมดูลการเรียนรู้",
  "learning.mod_memory": "หน่วยความจำ",
  "learning.mod_memory_desc": "ไฟล์ความจำหลักสำหรับความรู้ถาวร",
  "learning.mod_soul": "บุคลิกภาพ",
  "learning.mod_soul_desc": "ไฟล์จิตวิญญาณกำหนดบุคลิกและน้ำเสียง",
  "learning.mod_emotions": "แกนอารมณ์",
  "learning.mod_emotions_desc": "การติดตามอารมณ์และวิเคราะห์ความรู้สึก",
  "learning.mod_user": "โมเดลผู้ใช้",
  "learning.mod_user_desc": "โปรไฟล์ผู้ใช้ปรับตัวและความชอบ",
  "learning.mod_tiered": "ความจำหลายชั้น",
  "learning.mod_tiered_desc": "ความจำระยะสั้นและยาวพร้อมเส้นโค้งการลืม",
  "learning.active": "ใช้งาน",
  "learning.inactive": "ไม่ใช้งาน",
  "learning.generate": "สร้าง",
  "learning.regenerate": "สร้างใหม่",
  "learning.generating": "กำลังสร้าง...",
  "learning.init_all": "สร้างทั้งหมด",
  "learning.generate_memory": "สร้างไฟล์ความจำ",
  "learning.generate_soul": "สร้างบุคลิกภาพ",
  "learning.generate_emotions": "เริ่มต้นอารมณ์",
  "learning.generate_user": "สร้างโปรไฟล์ผู้ใช้",
  "learning.generate_knowledge": "จัดระเบียบความรู้",
  "learning.current_mood": "อารมณ์ปัจจุบัน",
  "learning.mood_valence": "ความรู้สึก",
  "learning.mood_stability": "ความมั่นคง",
  "learning.mood_energy": "พลังงาน",
  "learning.mood_warmth": "ความอบอุ่น",
  "learning.memory_title": "หน่วยความจำ",
  "learning.tiered_memory": "ระบบความจำหลายชั้น",
  "learning.short_term": "ระยะสั้น",
  "learning.long_term": "ระยะยาว",
  "learning.entities": "สิ่งที่รู้จัก",
  "learning.entity_graph": "กราฟสิ่งที่รู้จัก",
  "learning.recent_memories": "ความจำล่าสุด",
  "learning.memory_file": "MEMORY.md",
  "learning.no_memory": "ไม่พบข้อมูลความจำ AI ยังไม่ได้จดจำอะไรเลย",
  "learning.memory_history": "ประวัติความทรงจำ",
  "learning.load_history": "โหลดประวัติ",
  "learning.no_history": 'กด "โหลดประวัติ" เพื่อดูไฟล์ความทรงจำทั้งหมด',
  "learning.emotions_title": "สถานะอารมณ์",
  "learning.emotion_vector": "เวกเตอร์อารมณ์",
  "learning.vec_valence": "ความรู้สึก",
  "learning.vec_arousal": "ความตื่นตัว",
  "learning.vec_dominance": "ความเด่น",
  "learning.vec_attachment": "ความผูกพัน",
  "learning.vec_desire": "ความปรารถนา",
  "learning.vec_confusion": "ความสับสน",
  "learning.active_emotions": "อารมณ์ที่ใช้งาน",
  "learning.loyalty": "ความจงรักภักดี",
  "learning.loyalty_level": "ระดับความจงรักภักดี",
  "learning.bond_strength": "ความแข็งแกร่งของสายสัมพันธ์",
  "learning.protective": "สัญชาตญาณปกป้อง",
  "learning.devotion_style": "รูปแบบความทุ่มเท",
  "learning.emotional_memories": "ความทรงจำทางอารมณ์",
  "learning.no_emotions": "ส่วนขยายอารมณ์ไม่ได้ติดตั้งหรือไม่มีข้อมูล",
  "learning.personality_title": "บุคลิกภาพและตัวตน",
  "learning.soul_file": "SOUL.md — คำนิยามบุคลิกภาพ",
  "learning.identity_file": "IDENTITY.md — การตั้งค่าตัวตน",
  "learning.no_soul": "ไม่พบ SOUL.md AI ใช้บุคลิกภาพเริ่มต้น",
  "learning.user_title": "ความเข้าใจผู้ใช้",
  "learning.user_profile": "โปรไฟล์ผู้ใช้ที่เรียนรู้",
  "learning.comm_style": "รูปแบบการสื่อสาร",
  "learning.expertise": "ระดับความเชี่ยวชาญ",
  "learning.response_pref": "ความชอบการตอบ",
  "learning.mental_state": "สถานะจิตใจ",
  "learning.interests": "ความสนใจหลัก",
  "learning.interaction_stats": "สถิติการโต้ตอบ",
  "learning.stat_messages": "ข้อความ",
  "learning.stat_questions": "คำถาม",
  "learning.stat_commands": "คำสั่ง",
  "learning.stat_code": "โค้ด",
  "learning.user_file": "USER.md",
  "learning.no_user": "ไม่พบข้อมูลผู้ใช้",
  "learning.knowledge_title": "ความรู้ระยะยาว",
  "learning.long_term_knowledge": "ความรู้ที่รวบรวม",
  "learning.no_knowledge": "ยังไม่พบความรู้ระยะยาว",
  "learning.disconnected": "เชื่อมต่อ gateway เพื่อดูข้อมูลการเรียนรู้ AI",
  "learning.loading": "กำลังโหลดข้อมูลการเรียนรู้ AI...",
  "learning.refresh": "รีเฟรช",
  "learning.sec_overview": "ภาพรวม",
  "learning.sec_memory": "ความจำ",
  "learning.sec_emotions": "อารมณ์",
  "learning.sec_personality": "บุคลิกภาพ",
  "learning.sec_user": "โมเดลผู้ใช้",
  "learning.sec_knowledge": "ความรู้",
  "ks.title": "แหล่งข้อมูลความรู้",
  "ks.loading": "กำลังโหลด...",
  "ks.refresh": "รีเฟรช",
  "ks.add_source": "เพิ่มแหล่งข้อมูล",
  "ks.edit_source": "แก้ไขแหล่งข้อมูล",
  "ks.empty_title": "ยังไม่มีแหล่งข้อมูล",
  "ks.empty_description": "อธิบายแหล่งข้อมูลที่ต้องการ แล้ว AI จะวิเคราะห์และเข้าถึงข้อมูลให้อัตโนมัติ",
  "ks.tab_sources": "แหล่งข้อมูล",
  "ks.tab_collected": "ความรู้ที่รวบรวม",
  "ks.status_idle": "รอ",
  "ks.status_collecting": "กำลังรวบรวม",
  "ks.status_success": "ใช้งาน",
  "ks.status_error": "ผิดพลาด",
  "ks.disabled": "ปิดใช้งาน",
  "ks.schedule": "กำหนดการ",
  "ks.last_collected": "รวบรวมล่าสุด",
  "ks.docs_ingested": "เอกสาร",
  "ks.runs": "ครั้ง",
  "ks.collect_now": "รวบรวมเดี๋ยวนี้",
  "ks.edit": "แก้ไข",
  "ks.enable": "เปิดใช้งาน",
  "ks.disable": "ปิดใช้งาน",
  "ks.delete": "ลบ",
  "ks.delete_confirm": "คุณแน่ใจหรือว่าต้องการลบแหล่งข้อมูลนี้?",
  "ks.field_name": "ชื่อแหล่งข้อมูล",
  "ks.field_name_placeholder": "เช่น หลังบ้าน HR, API ยอดขาย, ระบบการเงิน",
  "ks.field_description": "แหล่งข้อมูลนี้คืออะไร?",
  "ks.field_description_placeholder":
    "เช่น ดึงราคาทองจาก goldprice.org ทุกชั่วโมง\nเช่น เข้าระบบ HR ที่ hr.company.com แล้วดึงรายชื่อพนักงานใหม่\nเช่น ดึงข้อมูลยอดขายจาก https://api.example.com/v1/sales",
  "ks.field_description_hint":
    "อธิบายแหล่งข้อมูล — คืออะไร, URL, และต้องการเก็บอะไร AI จะวิเคราะห์วิธีเข้าถึงเอง",
  "ks.field_credentials": "ข้อมูลรับรอง (ไม่บังคับ)",
  "ks.field_credentials_hint": "ถ้าต้องล็อกอินหรือใช้ API key ให้กรอกที่นี่ ข้อมูลจะถูกเข้ารหัส",
  "ks.field_username": "ชื่อผู้ใช้",
  "ks.field_password": "รหัสผ่าน",
  "ks.field_api_key": "API Key",
  "ks.field_interval": "รวบรวมทุก",
  "ks.minutes": "นาที",
  "ks.cancel": "ยกเลิก",
  "ks.save": "บันทึก",
  "ks.create": "สร้าง",
  "ks.just_now": "เมื่อสักครู่",
  "login.title": "Nabe",
  "login.subtitle": "กรอกรหัสผ่านเพื่อเข้าใช้งาน",
  "login.password": "รหัสผ่าน",
  "login.signin": "เข้าสู่ระบบ",
  "login.hint": "กด Enter เพื่อเข้าสู่ระบบ",
  "topbar.connected": "เชื่อมต่อแล้ว",
  "topbar.offline": "ออฟไลน์",
  "topbar.signout": "ออกจากระบบ",
  "overview.connection": "การเชื่อมต่อ",
  "overview.online": "ออนไลน์",
  "overview.offline": "ออฟไลน์",
  "overview.running_for": "ทำงานมาแล้ว",
  "overview.not_connected": "ไม่ได้เชื่อมต่อกับ gateway",
  "overview.channels": "ช่องทาง",
  "overview.active_sessions": "เซสชันที่ใช้งาน",
  "overview.system_info": "ข้อมูลระบบ",
  "overview.getting_started": "เริ่มต้นใช้งาน",
  "overview.getting_started_sub": "เคล็ดลับเพื่อให้ใช้งาน Nabe ได้อย่างเต็มที่",
  "overview.tip_connect": "เชื่อมต่อแอป",
  "overview.tip_connect_desc": "ไปที่ การเชื่อมต่อ เพื่อเชื่อม WhatsApp, Telegram, Discord และอื่นๆ",
  "overview.tip_chat": "เริ่มสนทนา",
  "overview.tip_chat_desc": "ใช้แท็บ แชท เพื่อพูดคุยกับ AI ผู้ช่วยโดยตรง",
  "overview.tip_settings": "ปรับแต่งการตั้งค่า",
  "overview.tip_settings_desc": "ไปที่ ตั้งค่า เพื่อปรับแต่งตามต้องการ",
  "terminal.quick_commands": "คำสั่งด่วน",
  "terminal.quick_sub": "งานที่ใช้บ่อย กดเดียวเสร็จ",
  "terminal.run_command": "รันคำสั่ง",
  "terminal.run_sub": "พิมพ์คำสั่งแล้วกด Enter หรือกด รัน",
  "terminal.run": "รัน",
  "terminal.running": "กำลังทำงาน...",
  "terminal.output": "ผลลัพธ์",
  "terminal.clear": "ล้าง",
  "terminal.no_commands": "ยังไม่มีคำสั่ง",
  "terminal.no_commands_sub": "ใช้คำสั่งด่วนด้านบน หรือพิมพ์คำสั่งของคุณเอง",
  "terminal.not_connected": "ไม่ได้เชื่อมต่อกับ gateway",
  "terminal.welcome": "ยินดีต้อนรับสู่ Nabe Gateway Terminal",
  "terminal.welcome_hint": "พิมพ์คำสั่งด้านล่าง หรือใช้ปุ่มด่วนด้านบน",
  "terminal.status": "สถานะ",
  "terminal.config": "ค่าตั้ง",
  "terminal.disk": "ดิสก์",
  "terminal.memory": "หน่วยความจำ",
  "terminal.processes": "โปรเซส",
  "terminal.network": "เครือข่าย",
  "terminal.uptime": "อัพไทม์",
  "terminal.logs_btn": "ล็อก",
  "files.quick_access": "เข้าถึงด่วน",
  "files.quick_sub": "ไปยังโฟลเดอร์ที่ใช้บ่อยบนเซิร์ฟเวอร์",
  "files.home": "โฮม",
  "files.nabe_config": "ค่าตั้ง Nabe",
  "files.app_files": "ไฟล์แอป",
  "files.extensions": "ส่วนเสริม",
  "files.temp": "ชั่วคราว",
  "files.root": "รูท",
  "files.go_up": "ขึ้นไปโฟลเดอร์บน",
  "files.refresh": "รีเฟรช",
  "files.show_hidden": "แสดงไฟล์ซ่อน",
  "files.items": "รายการ",
  "files.name": "ชื่อ",
  "files.size": "ขนาด",
  "files.modified": "แก้ไขล่าสุด",
  "files.empty": "โฟลเดอร์นี้ว่าง",
  "files.loading": "กำลังโหลด...",
  "files.opening": "กำลังเปิดไฟล์...",
  "files.save": "บันทึก",
  "files.saving": "กำลังบันทึก...",
  "files.close": "ปิด",
  "files.unsaved": "มีการแก้ไขที่ยังไม่บันทึก",
  "files.delete": "ลบ",
  "files.delete_confirm": "ลบ",
  "files.not_connected": "ไม่ได้เชื่อมต่อกับ gateway",
  "config.save": "บันทึก",
  "config.saving": "กำลังบันทึก...",
  "config.undo": "เลิกทำทั้งหมด",
  "config.apply": "บันทึกแล้วรีสตาร์ท",
  "config.applying": "กำลังใช้งาน...",
  "config.update": "ตรวจอัปเดต",
  "config.updating": "กำลังตรวจ...",
  "config.visual": "แบบง่าย",
  "config.advanced": "ขั้นสูง",
  "config.search": "ค้นหาการตั้งค่า...",
  "hint.gateway": "เซิร์ฟเวอร์กลางที่เชื่อมต่อ AI กับแอปแชท เปรียบเหมือนสมองที่ส่งต่อข้อความระหว่างคุณกับ AI",
  "hint.websocket": "การเชื่อมต่อแบบเรียลไทม์ระหว่างเบราว์เซอร์กับเซิร์ฟเวอร์ ทำให้ข้อมูลอัปเดตทันทีโดยไม่ต้องรีเฟรชหน้า",
  "hint.session_key": "ชื่อเฉพาะสำหรับแต่ละบทสนทนา เหมือนชื่อห้องแชท — key ต่างกันจะแยกบทสนทนาออกจากกัน",
  "hint.thinking_level":
    "ระดับความลึกในการคิดของ AI ก่อนตอบ สูง = ช้ากว่าแต่คิดรอบคอบกว่า ต่ำ = เร็วกว่าแต่คำตอบง่ายกว่า",
  "hint.block_streaming": "วิธีที่ AI ส่งคำตอบกลับ แทนที่จะรอคำตอบทั้งหมด จะส่งเป็นชิ้นๆ ตามที่พร้อม",
  "hint.block_streaming_break":
    "จะส่งแต่ละชิ้นเมื่อไหร่ 'text_end' ส่งหลังจบแต่ละย่อหน้า 'message_end' รอจนตอบเสร็จทั้งหมด",
  "hint.block_streaming_chunk": "วิธีแบ่งคำตอบยาวๆ เป็นชิ้นเล็กลง มีประโยชน์สำหรับแอปแชทที่จำกัดจำนวนตัวอักษร",
  "hint.exec_approvals": "กฎความปลอดภัยว่า AI สามารถรันคำสั่งอะไรบนคอมพิวเตอร์ได้ ป้องกันการทำงานที่อันตราย",
  "hint.bindings": "ที่อยู่เครือข่ายที่เซิร์ฟเวอร์รับฟัง 'loopback' = เฉพาะเครื่องนี้ 'lan' = อุปกรณ์ใดๆ ในเครือข่าย",
  "hint.auto_follow": "สานต่อบทสนทนาอัตโนมัติเมื่อมีข้อความใหม่เข้ามา โดยไม่ต้องกดเอง",
  "hint.agent": "ผู้ช่วย AI ที่มีบุคลิก เครื่องมือ และพื้นที่ทำงานเฉพาะ คุณมีหลายเอเจนต์สำหรับงานต่างๆ ได้",
  "hint.skill": "ความสามารถเฉพาะที่ให้ AI ได้ เช่น ค้นเว็บ รันโค้ด วิเคราะห์รูป เปิด/ปิดได้ตามต้องการ",
  "hint.cron": "งานที่ตั้งเวลาให้ทำอัตโนมัติ เหมือนนาฬิกาปลุกสำหรับ AI — ปลุกขึ้นมาทำงานตามเวลาที่กำหนด",
  "hint.node": "คอมพิวเตอร์ที่จับคู่กับ gateway สามารถรันคำสั่ง เข้าถึงไฟล์ หรือให้บริการกับ AI ได้",
  "hint.instance": "อุปกรณ์หรือแอปที่เชื่อมต่อกับ gateway อยู่ตอนนี้ แสดงว่าอะไรออนไลน์อยู่",
  "hint.channel": "แพลตฟอร์มแชทที่เชื่อมกับ AI เช่น Telegram, Discord, WhatsApp, Slack",
  "hint.tls": "การเข้ารหัสสำหรับการเชื่อมต่อที่ปลอดภัย (HTTPS) เหมือนกุญแจล็อคประตู — ป้องกันคนอื่นอ่านข้อมูล",
  "hint.tailscale": "บริการ VPN ที่สร้างเครือข่ายส่วนตัวระหว่างอุปกรณ์ ทำให้เข้าถึงระยะไกลง่ายและปลอดภัย",
  "hint.rate_limit": "จำกัดจำนวนครั้งที่ล็อกอินผิดได้ในช่วงเวลาหนึ่ง ป้องกันแฮกเกอร์เดารหัสผ่าน",
  "hint.trusted_proxy": "Reverse proxy (เช่น Nginx, Cloudflare) ที่อยู่หน้า gateway และจัดการการยืนยันตัวตน",
  "hint.sandbox": "กล่องแยกที่ AI รันคำสั่งอย่างปลอดภัย เหมือนสนามเด็กเล่น — ทำอะไรก็ไม่กระทบระบบจริง",
  "hint.tool_policy": "กฎว่า AI ใช้เครื่องมืออะไรได้บ้าง อนุญาตหรือบล็อคความสามารถเฉพาะเพื่อความปลอดภัย",
  "hint.subagent": "AI ลูกที่ถูกสร้างโดย AI หลักสำหรับทำงานพร้อมกัน เหมือนมอบหมายงานย่อยให้ผู้ช่วย",
  "hint.memory_search": "ความจำระยะยาวของ AI เก็บข้อมูลสำคัญจากบทสนทนาและค้นหาได้ภายหลังด้วย AI",
  "hint.tts": "Text-to-Speech: แปลงข้อความเป็นเสียงพูด AI สามารถ 'พูด' กับคุณด้วยเสียงได้",
  "hint.vision": "ความสามารถของ AI ในการเข้าใจรูปภาพ ส่งรูปมาแล้ว AI บอกได้ว่ามีอะไรในรูป",
  "hint.ollama": "เครื่องมือรัน AI โมเดลบนเครื่องคุณเอง ไม่ต้องใช้คลาวด์ — ข้อมูลอยู่กับคุณ",
  "hint.api_key": "รหัสลับที่ให้ AI เชื่อมต่อกับบริการ (เช่น OpenAI, Google) เก็บเป็นความลับ อย่าแชร์สาธารณะ",
  "hint.model": "สมอง AI ที่ใช้ โมเดลต่างกันมีจุดเด่นต่างกัน — บางตัวเร็ว บางตัวฉลาด บางตัวเข้าใจรูปภาพ",
  "hint.token": "กุญแจลับที่ใช้ยืนยันตัวตนกับ gateway เหมือนรหัสผ่านแต่สร้างอัตโนมัติ",
  "hint.password_auth": "การล็อกอินด้วยรหัสผ่านแบบง่าย ใครมีรหัสผ่านก็เข้าถึง gateway ได้",
  "hint.webhook": "URL ที่รับแจ้งเตือนเมื่อมีอะไรเกิดขึ้น เหมือนกริ่งประตู — บริการกดกริ่งเมื่อมีข้อความ",
  "hint.presence": "สัญญาณแสดงว่าอุปกรณ์ออนไลน์อยู่ เหมือนจุดสีเขียวบนแอปแชท",
  "hint.compaction": "ทำความสะอาดประวัติสนทนาเก่าเพื่อประหยัดพื้นที่ เก็บส่วนสำคัญ ลบส่วนที่ไม่จำเป็น",
  "hint.funnel": "Tailscale Funnel เปิด gateway ให้อินเทอร์เน็ตภายนอกเข้าถึงได้ ใครมี URL ก็เข้าถึงได้",
  "hint.basepath": "คำนำหน้า URL ของหน้าเว็บ ใช้เมื่อรันหลัง reverse proxy ที่ sub-path เช่น /nabe",
  "guide.connect_app_title": "วิธีเชื่อมต่อแอปแชท",
  "guide.connect_app_steps":
    "1. ไปที่หน้า การเชื่อมต่อ\n2. กดแอปที่ต้องการเชื่อม (เช่น Telegram)\n3. ทำตามขั้นตอนการตั้งค่าของแอปนั้น\n4. ใส่ token หรือข้อมูลที่ต้องการ\n5. กด บันทึก — การเชื่อมต่อจะเริ่มอัตโนมัติ",
  "guide.setup_agent_title": "วิธีตั้งค่า AI Agent",
  "guide.setup_agent_steps":
    "1. ไปที่หน้า เอเจนต์\n2. เอเจนต์เริ่มต้น 'main' ถูกสร้างไว้แล้ว\n3. กดเพื่อแก้ไขบุคลิก (IDENTITY.md)\n4. เลือกโมเดล AI ที่ต้องการใช้\n5. เปิดเครื่องมือ/ความสามารถที่ต้องการ",
  "guide.add_skill_title": "วิธีเปิดความสามารถ (Skill)",
  "guide.add_skill_steps":
    "1. ไปที่หน้า ความสามารถ\n2. เรียกดู skill ที่มี\n3. กดสวิตช์เพื่อเปิดใช้งาน\n4. บาง skill ต้องใช้ API key — ใส่เมื่อถูกถาม\n5. ตอนนี้ AI ใช้ skill นี้ในบทสนทนาได้แล้ว",
  "guide.schedule_task_title": "วิธีตั้งเวลางาน",
  "guide.schedule_task_steps":
    "1. ไปที่ งานตั้งเวลา\n2. กด 'เพิ่มงาน'\n3. ตั้ง cron expression (เช่น '0 9 * * *' = ทุกวันตอน 9 โมงเช้า)\n4. เขียนว่า AI ควรทำอะไร\n5. บันทึก — จะทำงานอัตโนมัติตามเวลาที่กำหนด",
  "overview.sessions_now": "เซสชันแชทที่เปิดอยู่ตอนนี้",
  "overview.scheduled_tasks": "งานตั้งเวลา",
  "overview.scheduled_none": "ไม่มี",
  "overview.scheduled_active": "ทำงานอยู่",
  "overview.scheduled_paused": "หยุดชั่วคราว",
  "overview.attention": "มีสิ่งที่ต้องดูแล",
  "overview.auth_required": "Gateway นี้ต้องยืนยันตัวตน เพิ่ม token หรือรหัสผ่าน แล้วกด เชื่อมต่อ",
  "overview.auth_hint_dashboard": "เปิดแดชบอร์ด",
  "overview.auth_hint_token": "ตั้ง token",
  "overview.auth_failed": "ยืนยันตัวตนล้มเหลว อัปเดต token หรือรหัสผ่านในตั้งค่า แล้วกด เชื่อมต่อ",
  "overview.docs_auth": "เอกสาร: การยืนยันตัวตน",
  "overview.insecure_context":
    "หน้านี้เป็น HTTP เบราว์เซอร์จะบล็อกการระบุตัวตน ใช้ HTTPS (Tailscale Serve) หรือเปิด",
  "overview.insecure_stay": "ถ้าต้องใช้ HTTP ให้ตั้ง",
  "overview.docs_tailscale": "เอกสาร: Tailscale Serve",
  "overview.docs_insecure": "เอกสาร: HTTP ไม่ปลอดภัย",
  "overview.connection_settings": "ตั้งค่าการเชื่อมต่อ",
  "overview.connection_sub": "วิธีที่แดชบอร์ดนี้เชื่อมต่อกับ gateway",
  "overview.server_address": "ที่อยู่เซิร์ฟเวอร์",
  "overview.password": "รหัสผ่าน",
  "overview.enter_password": "กรอกรหัสผ่าน",
  "overview.session_name": "ชื่อเซสชัน",
  "overview.reconnect": "เชื่อมต่อใหม่",
  "overview.connect_btn": "เชื่อมต่อ",
  "overview.refresh_data": "รีเฟรชข้อมูล",
  "overview.system_sub": "สถานะปัจจุบันของ gateway",
  "overview.running_time": "เวลาทำงาน",
  "overview.connected_devices": "อุปกรณ์ที่เชื่อมต่อ",
  "overview.last_refresh": "รีเฟรชล่าสุด",
  "overview.never": "ยังไม่เคย",
  "overview.all_good": "ทุกอย่างทำงานเรียบร้อย ไปที่ การเชื่อมต่อ เพื่อตั้งค่าแอปแชท",
  "channels.details": "รายละเอียดการเชื่อมต่อ",
  "channels.details_sub": "สถานะโดยละเอียดจากแอปแชทที่เชื่อมต่อ",
  "channels.never_checked": "ยังไม่เคยตรวจสอบ",
  "channels.no_snapshot": "ยังไม่มีข้อมูล",
  "channels.status_sub": "สถานะและตั้งค่าของการเชื่อมต่อนี้",
  "channels.configured": "ตั้งค่าแล้ว",
  "channels.running": "ทำงานอยู่",
  "channels.connected": "เชื่อมต่อแล้ว",
  "channels.last_inbound": "ข้อความล่าสุด",
  "channels.form_unavailable": "แบบฟอร์มตั้งค่าไม่พร้อมใช้งาน",
  "channels.form_unavailable_sub": "ไม่สามารถโหลดแบบฟอร์มได้ คุณสามารถแก้ไขในแท็บตั้งค่าด้วยตนเอง",
  "channels.plugin_needed": "แบบฟอร์มของการเชื่อมต่อนี้ยังไม่พร้อม",
  "channels.plugin_needed_sub": "อาจต้องเปิดใช้ plugin ก่อน ตรวจสอบในแท็บตั้งค่าหรือรีสตาร์ท gateway",
  "channels.loading_schema": "กำลังโหลดแบบฟอร์ม…",
  "channels.save_changes": "บันทึก",
  "channels.undo_changes": "เลิกทำ",
  "channels.saving": "กำลังบันทึก...",

  // Channel picker
  "picker.title": "เลือกแอปแชท",
  "picker.subtitle": "เลือกแอปที่ต้องการเชื่อมต่อกับ AI ของคุณ",
  "picker.add": "เพิ่ม",
  "picker.add_channel": "เพิ่มแอปแชท",
  "picker.all_added": "เพิ่มแอปแชททั้งหมดแล้ว",
  "picker.already_added": "เพิ่มแล้ว",
  "picker.empty_state": 'ยังไม่มีแอปแชท กด "เพิ่มแอปแชท" เพื่อเริ่มต้น',
  "picker.easy": "ง่าย",
  "picker.medium": "ปานกลาง",
  "picker.hard": "ขั้นสูง",
  "picker.enabled": "เปิดใช้งาน",

  // Channel setup guide
  "guide.title": "คู่มือตั้งค่า —",
  "guide.full_docs": "อ่านคู่มือฉบับเต็ม",

  "config.general": "ทั่วไป",
  "config.updates": "อัปเดต",
  "config.ai_agents": "AI เอเจนต์",
  "config.security": "ความปลอดภัย",
  "config.messaging_apps": "แอปแชท",
  "config.messages": "ข้อความ",
  "config.commands": "คำสั่ง",
  "config.hooks": "Hook อัตโนมัติ",
  "config.skills_section": "ความสามารถ",
  "config.tools": "เครื่องมือ",
  "config.server": "เซิร์ฟเวอร์",
  "config.setup_guide": "คู่มือติดตั้ง",
  "config.settings": "ตั้งค่า",
  "config.all_settings": "ตั้งค่าทั้งหมด",
  "config.unsaved": "มีการเปลี่ยนแปลงที่ยังไม่บันทึก",
  "config.no_changes": "ไม่มีการเปลี่ยนแปลง",
  "config.discard_title": "ยกเลิกและโหลดจากไฟล์ใหม่",
  "config.save_title": "บันทึกลงไฟล์ตั้งค่า",
  "config.restart_title": "บันทึกและรีสตาร์ทบริการ",
  "config.update_title": "ตรวจสอบอัปเดต",
  "config.loading_schema": "กำลังโหลดแบบฟอร์ม…",
  "config.advanced_warning": "",
  "config.raw_json5": "Raw JSON5",
  "config.all": "ทั้งหมด",
  "agents.title": "เอเจนต์",
  "agents.configured": "ตั้งค่าแล้ว",
  "agents.no_agents": "ไม่พบเอเจนต์",
  "agents.select": "เลือกเอเจนต์",
  "agents.select_sub": "เลือกเอเจนต์เพื่อดูรายละเอียด",
  "agents.tab_overview": "ภาพรวม",
  "agents.tab_files": "ไฟล์",
  "agents.tab_tools": "เครื่องมือ",
  "agents.tab_skills": "ความสามารถ",
  "agents.tab_channels": "ช่องทาง",
  "agents.tab_cron": "งานตั้งเวลา",
  "agents.tab_runtime": "รันไทม์",
  "agents.runtime_title": "สถานะรันไทม์",
  "agents.runtime_sub": "ซับเอเจนต์ที่กำลังทำงาน สถานะคิว และผลลัพธ์ล่าสุด",
  "agents.runtime_active": "ซับเอเจนต์ที่กำลังทำงาน",
  "agents.runtime_no_active": "ไม่มีซับเอเจนต์ที่กำลังทำงาน",
  "agents.runtime_elapsed": "เวลาที่ผ่านไป",
  "agents.runtime_queue": "สถานะคิว",
  "agents.runtime_queue_unavailable": "ไม่สามารถดึงสถานะคิวได้",
  "agents.runtime_slots": "สล็อต",
  "agents.runtime_recent": "เสร็จสิ้นล่าสุด",
  "agents.runtime_recent_sub": "ซับเอเจนต์ที่เสร็จสิ้นใน 30 นาทีล่าสุด",
  "agents.runtime_no_recent": "ไม่มีผลลัพธ์ล่าสุด",
  "agents.runtime_col_label": "ชื่อ",
  "agents.runtime_col_status": "สถานะ",
  "agents.runtime_col_model": "โมเดล",
  "agents.runtime_col_runtime": "เวลา",
  "agents.runtime_watchdog": "วอชด็อก",
  "agents.runtime_watchdog_on": "ทำงาน",
  "agents.runtime_watchdog_off": "หยุด",
  "agents.runtime_scans": "สแกน",
  "agents.runtime_aborted": "ยกเลิก",
  "agents.runtime_registered": "ลงทะเบียน",
  "agents.overview_sub": "พาธและข้อมูลเอเจนต์",
  "agents.workspace": "พื้นที่ทำงาน",
  "agents.primary_model": "โมเดลหลัก",
  "agents.identity_name": "ชื่อตัวตน",
  "agents.default": "ค่าเริ่มต้น",
  "agents.identity_emoji": "อีโมจิตัวตน",
  "agents.skills_filter": "ตัวกรองความสามารถ",
  "agents.selected": "ที่เลือก",
  "agents.all_skills": "ทั้งหมด",
  "agents.model_selection": "เลือกโมเดล",
  "agents.primary_default": "โมเดลหลัก",
  "agents.inherit_default": "ใช้ค่าเริ่มต้น",
  "agents.fallbacks": "สำรอง (คั่นด้วยจุลภาค)",
  "agents.fallbacks_placeholder": "provider/model, provider/model",
  "agents.reload_config": "โหลดค่าตั้งใหม่",
  "agents.api_keys_title": "API Keys ของ Provider",
  "agents.api_keys_sub": "คีย์ API สำหรับ LLM providers — เก็บแบบเข้ารหัสและใช้ร่วมกันทุก agent",
  "agents.api_key_set": "ตั้งค่าแล้ว (ซ่อนไว้)",
  "agents.api_key_placeholder": "ใส่ API key...",
  "agents.catalog_title": "รายการโมเดล",
  "agents.catalog_sub": "โมเดลที่สามารถเลือกใช้เป็นตัวหลักหรือตัวสำรอง",
  "agents.catalog_model_id": "Model ID",
  "agents.catalog_alias": "ชื่อย่อ",
  "agents.api_key_label": "API Key",
  "agents.catalog_actions": "จัดการ",
  "agents.catalog_add": "เพิ่มโมเดล",
  "agents.catalog_add_placeholder": "provider/model-name",
  "agents.catalog_alias_placeholder": "ชื่อย่อ (ไม่บังคับ)",
  "agents.catalog_empty": "ยังไม่มีโมเดล เพิ่มได้ด้านล่าง",
  "agents.catalog_confirm_remove": "โมเดลนี้ใช้เป็นตัวหลักหรือสำรองอยู่ ลบหรือไม่?",
  "agents.catalog_provider": "ผู้ให้บริการ",
  "agents.catalog_model": "โมเดล",
  "agents.catalog_no_providers": "ยังไม่มีผู้ให้บริการ ใส่ API key ก่อน",
  "agents.catalog_select_provider": "เลือกผู้ให้บริการ…",
  "agents.catalog_select_model": "เลือกโมเดล…",
  "agents.fallback_title": "โมเดลสำรอง",
  "agents.fallback_sub": "ลองใช้ตามลำดับถ้าตัวหลักล้มเหลว",
  "agents.fallback_add": "เพิ่มโมเดลสำรอง",
  "agents.fallback_empty": "ยังไม่มีโมเดลสำรอง",
  "agents.custom_env_title": "ตัวแปร Environment เพิ่มเติม",
  "agents.custom_env_sub": "API key หรือตัวแปรอื่นๆ สำหรับ provider เพิ่มเติม",
  "agents.custom_env_add": "เพิ่มตัวแปร",
  "agents.custom_env_name_placeholder": "VARIABLE_NAME",
  "agents.custom_env_value_placeholder": "ค่า...",
  "sessions.title": "เซสชัน",
  "sessions.title_sub": "คีย์เซสชันและการตั้งค่าเฉพาะ",
  "sessions.active_within": "ใช้งานภายใน (นาที)",
  "sessions.limit": "จำกัด",
  "sessions.include_global": "รวม global",
  "sessions.include_unknown": "รวมที่ไม่รู้จัก",
  "sessions.store": "ที่เก็บ",
  "sessions.col_key": "คีย์",
  "sessions.col_label": "ป้าย",
  "sessions.col_kind": "ประเภท",
  "sessions.col_updated": "อัปเดต",
  "sessions.col_tokens": "โทเค็น",
  "sessions.col_thinking": "การคิด",
  "sessions.col_verbose": "รายละเอียด",
  "sessions.col_reasoning": "การให้เหตุผล",
  "sessions.col_actions": "การดำเนินการ",
  "sessions.no_sessions": "ไม่พบเซสชัน",
  "sessions.optional": "(ไม่บังคับ)",
  "sessions.inherit": "สืบทอด",
  "sessions.task_log": "บันทึกงาน",
  "sessions.no_messages": "ไม่มีข้อความในเซสชันนี้",
  "sessions.messages_label": "ข้อความ",
  "cron.title": "ตัวจัดเวลา",
  "cron.title_sub": "สถานะตัวจัดเวลาของ gateway",
  "cron.enabled": "เปิดใช้",
  "cron.jobs": "งาน",
  "cron.next_wake": "ปลุกถัดไป",
  "cron.new_job": "งานใหม่",
  "cron.new_job_sub": "สร้างงานตั้งเวลาหรือเรียก agent",
  "cron.name": "ชื่อ",
  "cron.description": "คำอธิบาย",
  "cron.agent_id": "ID เอเจนต์",
  "cron.schedule": "กำหนดเวลา",
  "cron.every": "ทุก",
  "cron.at": "ตอน",
  "cron.cron_expr": "Cron",
  "cron.session": "เซสชัน",
  "cron.wake_mode": "โหมดปลุก",
  "cron.payload": "ข้อมูล",
  "cron.main": "หลัก",
  "cron.isolated": "แยก",
  "cron.now": "ตอนนี้",
  "cron.next_heartbeat": "Heartbeat ถัดไป",
  "cron.system_event": "เหตุการณ์ระบบ",
  "cron.agent_turn": "รอบเอเจนต์",
  "cron.delivery": "การส่ง",
  "cron.timeout": "หมดเวลา (วินาที)",
  "cron.channel": "ช่องทาง",
  "cron.to": "ถึง",
  "cron.announce": "ประกาศสรุป (ค่าเริ่มต้น)",
  "cron.none_internal": "ไม่มี (ภายใน)",
  "cron.add_job": "เพิ่มงาน",
  "cron.jobs_title": "งาน",
  "cron.jobs_sub": "งานตั้งเวลาทั้งหมดใน gateway",
  "cron.no_jobs": "ยังไม่มีงาน",
  "cron.run_history": "ประวัติการรัน",
  "cron.run_history_sub": "การรันล่าสุดของ",
  "cron.select_job": "เลือกงานเพื่อดูประวัติ",
  "cron.no_runs": "ยังไม่มีการรัน",
  "cron.run_at": "รันเมื่อ",
  "cron.expression": "นิพจน์",
  "cron.timezone": "เขตเวลา (ไม่บังคับ)",
  "cron.unit": "หน่วย",
  "cron.unit_minutes": "นาที",
  "cron.unit_hours": "ชั่วโมง",
  "cron.unit_days": "วัน",
  "cron.system": "ระบบ",
  "cron.system_text": "ข้อความระบบ",
  "cron.agent_message": "ข้อความเอเจนต์",
  "cron.prompt": "พรอมต์",
  "cron.run_btn": "รัน",
  "cron.history_btn": "ประวัติ",
  "cron.remove": "ลบ",
  "cron.status": "สถานะ",
  "cron.next": "ถัดไป",
  "cron.last": "ล่าสุด",
  "cron.open_run_chat": "เปิดแชทรอบนี้",
  "skills.title": "ความสามารถ",
  "skills.title_sub": "ความสามารถในตัว จัดการ และในพื้นที่ทำงาน",
  "skills.filter": "กรอง",
  "skills.search": "ค้นหาความสามารถ",
  "skills.shown": "แสดง",
  "skills.no_skills": "ไม่พบความสามารถ",
  "skills.missing": "ขาดหาย",
  "skills.reason": "เหตุผล",
  "skills.api_key": "API key",
  "skills.save_key": "บันทึก key",
  "nodes.title": "โหนด",
  "nodes.title_sub": "อุปกรณ์ที่จับคู่และลิงก์สด",
  "nodes.no_nodes": "ไม่พบโหนด",
  "nodes.devices": "อุปกรณ์",
  "nodes.devices_sub": "คำขอจับคู่ + โทเค็นบทบาท",
  "nodes.pending": "รอดำเนินการ",
  "nodes.paired": "จับคู่แล้ว",
  "nodes.no_paired": "ไม่มีอุปกรณ์ที่จับคู่",
  "nodes.role": "บทบาท",
  "nodes.roles": "บทบาท",
  "nodes.scopes": "ขอบเขต",
  "nodes.approve": "อนุมัติ",
  "nodes.reject": "ปฏิเสธ",
  "nodes.tokens_none": "โทเค็น: ไม่มี",
  "nodes.tokens": "โทเค็น",
  "nodes.revoked": "เพิกถอนแล้ว",
  "nodes.active_token": "ใช้งานอยู่",
  "nodes.rotate": "หมุนเวียน",
  "nodes.revoke": "เพิกถอน",
  "nodes.exec_binding": "การผูกโหนดสำหรับ exec",
  "nodes.exec_binding_sub": "ผูกเอเจนต์กับโหนดเฉพาะเมื่อใช้ exec host=node",
  "nodes.switch_form": "สลับไปโหมดแบบฟอร์มเพื่อแก้ไข",
  "nodes.load_config": "โหลดค่าตั้ง",
  "nodes.default_binding": "การผูกเริ่มต้น",
  "nodes.default_binding_sub": "ใช้เมื่อเอเจนต์ไม่ระบุการผูกโหนด",
  "nodes.any_node": "โหนดใดก็ได้",
  "nodes.no_exec_nodes": "ไม่มีโหนดที่รัน system.run ได้",
  "nodes.no_agents_found": "ไม่พบเอเจนต์",
  "nodes.default_agent": "เอเจนต์เริ่มต้น",
  "nodes.uses_default": "ใช้ค่าเริ่มต้น",
  "nodes.node": "โหนด",
  "nodes.override": "แทนที่",
  "nodes.binding": "การผูก",
  "nodes.use_default": "ใช้ค่าเริ่มต้น",
  "nodes.status_paired": "จับคู่แล้ว",
  "nodes.status_unpaired": "ยังไม่จับคู่",
  "nodes.status_connected": "เชื่อมต่อ",
  "nodes.status_offline": "ออฟไลน์",
  "instances.title": "อินสแตนซ์ที่เชื่อมต่อ",
  "instances.title_sub": "สัญญาณจาก gateway และไคลเอนต์",
  "instances.no_instances": "ยังไม่มีอินสแตนซ์",
  "instances.last_input": "อินพุตล่าสุด",
  "instances.reason": "เหตุผล",
  "debug.snapshots": "สแนปช็อต",
  "debug.snapshots_sub": "ข้อมูลสถานะ สุขภาพ และ heartbeat",
  "debug.status": "สถานะ",
  "debug.health": "สุขภาพ",
  "debug.last_heartbeat": "Heartbeat ล่าสุด",
  "debug.security_audit": "ตรวจสอบความปลอดภัย",
  "debug.for_details": "เพื่อดูรายละเอียด",
  "debug.manual_rpc": "RPC ด้วยตนเอง",
  "debug.manual_rpc_sub": "ส่งคำสั่ง gateway ด้วย JSON params",
  "debug.method": "เมธอด",
  "debug.params": "พารามิเตอร์ (JSON)",
  "debug.call": "เรียก",
  "debug.models": "โมเดล",
  "debug.models_sub": "แคตตาล็อกจาก models.list",
  "debug.event_log": "บันทึกเหตุการณ์",
  "debug.event_log_sub": "เหตุการณ์ล่าสุดของ gateway",
  "debug.no_events": "ยังไม่มีเหตุการณ์",
  "logs.title": "บันทึก",
  "logs.title_sub": "ไฟล์บันทึกของ gateway (JSONL)",
  "logs.export": "ส่งออก",
  "logs.filter": "กรอง",
  "logs.search": "ค้นหาบันทึก",
  "logs.auto_follow": "ติดตามอัตโนมัติ",
  "logs.truncated": "เนื้อหาถูกตัด แสดงส่วนล่าสุด",
  "logs.no_entries": "ไม่มีรายการบันทึก",
  "chat.compacting": "กำลังบีบอัดบริบท...",
  "chat.compacted": "บีบอัดบริบทแล้ว",
  "chat.attachment_preview": "ดูตัวอย่างไฟล์แนบ",
  "chat.remove_attachment": "ลบไฟล์แนบ",
  "chat.placeholder": "เพิ่มข้อความหรือวางรูปภาพ...",
  "chat.placeholder_shift": "ข้อความ (↩ ส่ง, Shift+↩ ขึ้นบรรทัด, วางรูปได้)",
  "chat.not_connected": "เชื่อมต่อ gateway เพื่อเริ่มแชท…",
  "chat.loading": "กำลังโหลดแชท…",
  "chat.exit_focus": "ออกจากโหมดโฟกัส",
  "chat.queued": "คิว",
  "chat.remove_queued": "ลบข้อความในคิว",
  "chat.image_count": "รูปภาพ",
  "chat.new_messages": "ข้อความใหม่",
  "chat.message": "ข้อความ",
  "chat.stop": "หยุด",
  "chat.new_session": "เซสชันใหม่",
  "chat.queue_btn": "คิว",
  "chat.queued_batch": "จะส่งรวมกัน",
  "chat.send": "ส่ง",
  "chat.history_hidden": "ข้อความที่ซ่อน",
  "chat.compaction_divider": "การบีบอัด",
  "app.brand": "NABE",
  "app.subtitle": "แดชบอร์ด Gateway",
  "app.expand_sidebar": "ขยายแถบด้านข้าง",
  "app.collapse_sidebar": "ย่อแถบด้านข้าง",
  "app.resources": "แหล่งข้อมูล",
  "app.docs": "เอกสาร",
  "app.refresh_chat": "รีเฟรชข้อมูลแชท",
  "app.disabled_onboarding": "ปิดใช้ระหว่างการตั้งค่าเริ่มต้น",
  "app.toggle_thinking": "สลับแสดง/ซ่อนกระบวนการคิดของ AI",
  "app.toggle_focus": "สลับโหมดโฟกัส (ซ่อนแถบด้านข้าง)",
  "app.theme_system": "ระบบ",
  "app.theme_light": "สว่าง",
  "app.theme_dark": "มืด",
  "app.theme": "ธีม",
  "common.loading": "กำลังโหลด...",
  "common.error": "ข้อผิดพลาด",
  "common.cancel": "ยกเลิก",
  "common.confirm": "ยืนยัน",
  "common.yes": "ใช่",
  "common.no": "ไม่",
  "common.na": "ไม่มี",
  "common.save": "บันทึก",
  "common.saving": "กำลังบันทึก...",
  "common.refresh": "รีเฟรช",
  "common.refreshing": "กำลังรีเฟรช...",
  "common.delete": "ลบ",
  "common.enable": "เปิดใช้",
  "common.disable": "ปิดใช้",
  "common.enabled": "เปิดใช้แล้ว",
  "common.disabled": "ปิดใช้แล้ว",
  "common.language": "ภาษา",
  "common.what_is_this": "นี่คืออะไร?",
  "common.learn_more": "เรียนรู้เพิ่มเติม",
  "common.step": "ขั้นตอน",
  "common.docs": "เอกสาร",
  "common.resources": "แหล่งข้อมูล",
  "common.agent": "เอเจนต์",
  // Quick Settings
  "qs.title": "ตั้งค่าด่วน",
  "qs.subtitle": "การตั้งค่าที่ใช้บ่อย หากต้องการตัวเลือกทั้งหมด เลือกหมวดจากแถบด้านซ้าย",
  "qs.ai": "ผู้ช่วย AI",
  "qs.ai.model": "โมเดล AI",
  "qs.ai.model_help": "โมเดล AI ที่ใช้ตอบคำถาม",
  "qs.ai.name": "ชื่อผู้ช่วย",
  "qs.ai.name_help": "ชื่อที่ผู้ช่วยใช้แนะนำตัว",
  "qs.ai.workspace": "โฟลเดอร์ทำงาน",
  "qs.ai.workspace_help": "โฟลเดอร์เก็บไฟล์และโปรเจกต์",
  "qs.conn": "การเชื่อมต่อ",
  "qs.conn.port": "หมายเลขพอร์ต",
  "qs.conn.port_help": "พอร์ตสำหรับเชื่อมต่อกับ gateway",
  "qs.conn.bind": "ใครเชื่อมต่อได้",
  "qs.conn.bind_help": "กำหนดว่าอุปกรณ์ใดเข้าถึง gateway ได้",
  "qs.conn.bind.loopback": "เครื่องนี้เท่านั้น",
  "qs.conn.bind.lan": "อุปกรณ์ในเครือข่ายเดียวกัน",
  "qs.conn.bind.tailnet": "อินเทอร์เน็ต (ผ่าน Tailscale)",
  "qs.conn.bind.auto": "อัตโนมัติ",
  "qs.conn.auth": "ป้องกันด้วยรหัสผ่าน",
  "qs.conn.auth_help": "ต้องใส่รหัสผ่านเพื่อเชื่อมต่อ",
  "qs.conn.password": "รหัสผ่าน",
  "qs.apps": "แอปส่งข้อความ",
  "qs.apps.subtitle": "เชื่อมต่อผู้ช่วยกับแอปส่งข้อความ",
  "qs.apps.configure": "ตั้งค่า",
  "qs.updates": "อัปเดต",
  "qs.updates.channel": "ช่องทางอัปเดต",
  "qs.updates.channel_help": "ความถี่ในการรับเวอร์ชันใหม่",
  "qs.updates.auto": "ตรวจสอบอัตโนมัติ",
  "qs.updates.auto_help": "ตรวจหาอัปเดตเมื่อ gateway เริ่มทำงาน",
  "qs.log": "บันทึกเหตุการณ์",
  "qs.log.level": "ระดับรายละเอียด",
  "qs.log.level_help": "ปริมาณรายละเอียดที่บันทึกใน log",
  "qs.all_settings": "ดูการตั้งค่าทั้งหมด",
};

const ja: TranslationKeys = {
  "nav.chat": "チャット",
  "nav.dashboard": "ダッシュボード",
  "nav.automation": "自動化",
  "nav.system": "システム",
  "nav.simple.chat": "チャット",
  "nav.simple.status": "ステータス",
  "nav.simple.settings": "設定",
  "nav.simple.tools": "ツール",
  "nav.simple.help": "ヘルプ",
  "nav.mode.simple": "シンプル",
  "nav.mode.advanced": "詳細",
  "nav.mode.toggle_label": "ナビモード",
  "tab.chat": "チャット",
  "tab.overview": "ホーム",
  "tab.dashboard": "ダッシュボード",
  "tab.channels": "接続",
  "tab.instances": "デバイス",
  "tab.sessions": "会話",
  "tab.usage": "使用状況",
  "tab.agents": "エージェント",
  "tab.skills": "スキル",
  "tab.cron": "スケジュール",
  "tab.terminal": "ターミナル",
  "tab.files": "ファイル",
  "tab.nodes": "コンピュータ",
  "tab.config": "設定",
  "tab.debug": "詳細",
  "tab.heartbeat": "ハートビート",
  "sub.heartbeat": "ハートビート分析とモニタリング",
  "hb.title": "ハートビート分析",
  "hb.subtitle": "リアルタイムハートビートモニタリング",
  "hb.timeline": "タイムライン",
  "hb.event_log": "イベントログ",
  "hb.no_events": "イベントなし",
  "hb.refresh": "更新",
  "hb.pause": "一時停止",
  "hb.resume": "再開",
  "hb.loading": "読み込み中...",
  "hb.new": "NEW",
  "hb.stat_total": "合計実行",
  "hb.stat_ok_rate": "OK率",
  "hb.stat_alert_rate": "アラート率",
  "hb.stat_avg_duration": "平均時間",
  "hb.stat_skipped": "スキップ",
  "hb.stat_failed": "失敗",
  "hb.stat_duplicates": "重複検出",
  "hb.stat_api_saved": "API節約",
  "hb.status_sent": "送信済み",
  "hb.status_ok-empty": "OK (空)",
  "hb.status_ok-token": "OK (トークン)",
  "hb.status_skipped": "スキップ",
  "hb.status_failed": "失敗",
  "tab.logs": "アクティビティ",
  "tab.learning": "AI学習",
  "tab.mind": "マインド",
  "tab.activity-log": "アクティビティログ (DB)",
  "sub.chat": "AIアシスタントと直接会話できます。",
  "sub.overview": "すべての状況を一目で確認できます。",
  "sub.dashboard": "ノード、エージェント、タスクのリアルタイム監視。",
  "sub.channels": "WhatsApp、Telegram、Discordなどのメッセージアプリを接続します。",
  "sub.instances": "現在接続されているデバイスとアプリを確認します。",
  "sub.sessions": "アクティブな会話を表示・管理します。",
  "sub.agents": "AIアシスタントとその設定。",
  "sub.skills": "AIの機能を有効/無効にします。",
  "sub.cron": "定期的に自動実行するタスクを設定します。",
  "sub.terminal": "SSHなしでサーバー上でコマンドを実行します。",
  "sub.files": "サーバー上のファイルを閲覧・編集します。",
  "sub.nodes": "接続されたコンピュータと権限を管理します。",
  "sub.config": "すべてのシステム設定を一箇所で調整します。",
  "sub.debug": "トラブルシューティング用の技術詳細。",
  "sub.logs": "最近の出来事を確認します。",
  "sub.learning": "AIのメモリ、感情、知識を表示します。",
  "sub.mind": "認知アーキテクチャの監視と分析。",
  "mind.soul": "AIの魂",
  "mind.soul.emotions": "感情状態",
  "mind.soul.lifecycle": "ライフサイクル",
  "mind.soul.personality": "個性",
  "mind.soul.values": "コアバリュー",
  "mind.disconnected": "ゲートウェイに接続して認知状態を表示",
  "mind.plugin_unavailable": "Cognitive Mindプラグインが無効です",
  "mind.plugin_unavailable_hint": "autonomous-mind拡張を有効にしてデータを表示",
  "mind.loading": "認知状態を読み込み中...",
  "mind.retry": "再試行",
  "mind.sec_overview": "概要",
  "mind.sec_soul": "魂",
  "mind.sec_depth": "深度",
  "mind.sec_thoughts": "思考",
  "mind.sec_workspace": "ワークスペース",
  "mind.sec_identity": "アイデンティティ",
  "mind.sec_analytics": "分析",
  "mind.emotion_love": "愛",
  "mind.emotion_anger": "怒り",
  "mind.emotion_sadness": "悲しみ",
  "mind.emotion_joy": "喜び",
  "mind.emotion_fear": "恐怖",
  "mind.emotion_trust": "信頼",
  "mind.emotion_greed": "欲",
  "mind.emotion_delusion": "迷い",
  "mind.mood_valence": "感情の方向性",
  "mind.mood_balanced": "バランス",
  "mind.mood_unknown": "不明",
  "mind.loyalty": "忠誠",
  "mind.stage_newborn": "誕生",
  "mind.stage_child": "幼年",
  "mind.stage_adolescent": "青年",
  "mind.stage_adult": "成人",
  "mind.stage_elder": "老年",
  "mind.stage_transcendent": "超越",
  "mind.meter_health": "健康",
  "mind.meter_energy": "エネルギー",
  "mind.meter_wisdom": "知恵",
  "mind.personality_developing": "会話を通じて個性が発達中...",
  "mind.overview_title": "認知概要",
  "mind.card_depth": "認知深度",
  "mind.card_mirror": "MIRRORスレッド",
  "mind.card_workspace": "ワークスペース",
  "mind.card_identity": "アイデンティティ",
  "mind.card_goals": "目標",
  "mind.card_thoughts": "思考",
  "mind.inactive": "非アクティブ",
  "mind.no_data": "データなし",
  "mind.active": "アクティブ",
  "mind.idle": "待機",
  "mind.waiting": "入力待ち",
  "mind.entries": "エントリ",
  "mind.traits": "特性",
  "mind.values_count": "価値観",
  "mind.active_count": "アクティブ",
  "mind.recent_count": "最近",
  "mind.global_memory": "グローバルワーキングメモリ",
  "mind.inner_monologue": "内的独白ストリーム",
  "mind.no_goals": "目標なし",
  "mind.unavailable": "利用不可",
  "mind.recent_thoughts": "最近の思考",
  "mind.no_thoughts": "まだ思考がありません。静かな状態です。",
  "mind.depth_title": "認知深度分析",
  "mind.depth_current": "現在の深度",
  "mind.depth_instinctive": "本能的",
  "mind.depth_associative": "連想的",
  "mind.depth_analytical": "分析的",
  "mind.depth_strategic": "戦略的",
  "mind.confidence": "確信度",
  "mind.depth_distribution": "深度分布",
  "mind.recent_classifications": "最近の分類",
  "mind.col_depth": "深度",
  "mind.col_confidence": "確信度",
  "mind.col_phases": "フェーズ",
  "mind.col_time": "時間",
  "mind.loading_depth": "深度データを読み込み中...",
  "mind.thoughts_title": "思考ストリーム",
  "mind.no_thoughts_yet": "思考の記録はまだありません。",
  "mind.workspace_title": "グローバルワークスペース",
  "mind.ws_entries": "エントリ",
  "mind.ws_active_entries": "アクティブなエントリ",
  "mind.ws_attention": "注意",
  "mind.ws_top5": "トップ5",
  "mind.ws_salience": "重要度による選択",
  "mind.ws_description":
    "グローバルワークスペースはAIの現在のワーキングメモリを保持します。重要度と新しさでランク付けされます。",
  "mind.identity_title": "アイデンティティ",
  "mind.id_continuity": "連続性",
  "mind.id_consistent": "アイデンティティは一貫しています",
  "mind.id_drift": "アイデンティティの変動を検出",
  "mind.id_traits": "自己モデル特性",
  "mind.id_values": "価値観",
  "mind.id_no_traits": "まだ特性が発達していません。",
  "mind.id_no_values": "まだ価値観が確立されていません。",
  "mind.id_loading": "アイデンティティデータを読み込み中...",
  "mind.analytics_title": "分析",
  "mind.an_classifications": "分類数",
  "mind.an_override_rate": "オーバーライド率",
  "mind.an_loop_perf": "ループ性能",
  "mind.an_avg": "平均",
  "mind.an_runs_max": "回",
  "mind.an_dominant": "主要深度",
  "mind.an_avg_confidence": "平均確信度",
  "mind.an_phase_failures": "フェーズ失敗",
  "mind.an_failures": "失敗",
  "mind.an_loading": "分析データを読み込み中...",
  "mind.time_just_now": "たった今",
  "mind.time_m_ago": "分前",
  "mind.time_h_ago": "時間前",
  "mind.time_d_ago": "日前",
  "sub.activity-log": "MongoDBに保存された全アクティビティログ（7日間保持）。",
  "tab.spawn-monitor": "Spawnモニター",
  "sub.spawn-monitor": "オーケストレーターディスパッチとサブエージェントを監視。",
  "spawn_monitor.title": "Spawnモニター",
  "spawn_monitor.sub": "オーケストレーター/spawnモードのリアルタイム監視。",
  "spawn_monitor.active_now": "実行中",
  "spawn_monitor.total_recent": "最近",
  "spawn_monitor.avg_runtime": "平均時間",
  "spawn_monitor.success_rate": "成功率",
  "spawn_monitor.active_title": "実行中のSpawn",
  "spawn_monitor.no_active": "実行中のサブエージェントはありません。",
  "spawn_monitor.elapsed": "経過",
  "spawn_monitor.recent_title": "最近の完了",
  "spawn_monitor.recent_sub": "完了したSpawnディスパッチ（過去30分）。",
  "spawn_monitor.no_recent": "最近のSpawn完了はありません。",
  "spawn_monitor.col_message": "メッセージ",
  "spawn_monitor.col_status": "ステータス",
  "spawn_monitor.col_model": "モデル",
  "spawn_monitor.col_runtime": "実行時間",
  "spawn_monitor.col_time": "時刻",
  "spawn_monitor.events_title": "ライブイベント",
  "spawn_monitor.events_sub": "リアルタイムSpawnライフサイクルイベント。",
  "spawn_monitor.no_events": "Spawnイベントはまだありません。Spawnモード時に表示されます。",
  "spawn_monitor.clear_events": "クリア",
  "spawn_monitor.auto_refresh": "自動更新",
  "dashboard.nodes": "ノード",
  "dashboard.nodes_online": "オンライン",
  "dashboard.agents": "エージェント",
  "dashboard.agents_registered": "登録済み",
  "dashboard.devices": "デバイス",
  "dashboard.devices_connected": "接続中",
  "dashboard.realtime": "リアルタイム",
  "dashboard.refresh": "更新",
  "dashboard.connected_nodes": "接続中のノード",
  "dashboard.disconnected_nodes": "切断中のノード",
  "dashboard.loading": "ノードを読み込み中...",
  "dashboard.no_nodes": "接続中のノードはありません。",
  "dashboard.commands": "コマンド",
  "dashboard.node_capabilities": "ノードの機能",
  "dashboard.node_capabilities_sub": "各ノードで利用可能なコマンドと機能。",
  "dashboard.node": "ノード",
  "dashboard.status": "ステータス",
  "dashboard.capabilities": "コマンド",
  "dashboard.uptime": "接続時間",
  "dashboard.agents_list": "エージェント",
  "dashboard.queue_title": "コマンドキュー",
  "dashboard.queue_sub": "全レーンのアクティブなタスクとキュー待ちタスク",
  "dashboard.queue_active": "アクティブ",
  "dashboard.queue_queued": "キュー待ち",
  "dashboard.queue_lane": "レーン",
  "dashboard.queue_concurrency": "最大同時実行数",
  "dashboard.queue_idle": "すべてのキューがアイドル中",
  "dashboard.queue_idle_lanes": "アイドルレーン",
  "learning.overview_title": "AI学習の概要",
  "learning.memory_entries": "メモリエントリ",
  "learning.emotions_active": "アクティブな感情",
  "learning.user_messages": "分析済みメッセージ",
  "learning.modules": "学習モジュール",
  "learning.mod_memory": "メモリ",
  "learning.mod_memory_desc": "永続的知識のコアメモリファイル",
  "learning.mod_soul": "パーソナリティ",
  "learning.mod_soul_desc": "ペルソナと声を定義するソウルファイル",
  "learning.mod_emotions": "感情コア",
  "learning.mod_emotions_desc": "感情追跡とセンチメント分析",
  "learning.mod_user": "ユーザーモデリング",
  "learning.mod_user_desc": "適応型ユーザープロファイルと設定",
  "learning.mod_tiered": "階層メモリ",
  "learning.mod_tiered_desc": "忘却曲線付き短期・長期メモリ",
  "learning.active": "アクティブ",
  "learning.inactive": "非アクティブ",
  "learning.generate": "生成",
  "learning.regenerate": "再生成",
  "learning.generating": "生成中...",
  "learning.init_all": "すべて生成",
  "learning.generate_memory": "メモリファイルを生成",
  "learning.generate_soul": "パーソナリティを生成",
  "learning.generate_emotions": "感情を初期化",
  "learning.generate_user": "ユーザープロファイルを生成",
  "learning.generate_knowledge": "知識を整理",
  "learning.current_mood": "現在の気分",
  "learning.mood_valence": "感情価",
  "learning.mood_stability": "安定性",
  "learning.mood_energy": "エネルギー",
  "learning.mood_warmth": "温かさ",
  "learning.memory_title": "メモリ",
  "learning.tiered_memory": "階層メモリシステム",
  "learning.short_term": "短期",
  "learning.long_term": "長期",
  "learning.entities": "エンティティ",
  "learning.entity_graph": "エンティティグラフ",
  "learning.recent_memories": "最近のメモリ",
  "learning.memory_file": "MEMORY.md",
  "learning.no_memory": "メモリデータが見つかりません。",
  "learning.memory_history": "メモリ履歴",
  "learning.load_history": "履歴を読み込む",
  "learning.no_history": "「履歴を読み込む」をクリックして過去のメモリを表示",
  "learning.emotions_title": "感情状態",
  "learning.emotion_vector": "感情ベクトル",
  "learning.vec_valence": "感情価",
  "learning.vec_arousal": "覚醒度",
  "learning.vec_dominance": "支配性",
  "learning.vec_attachment": "愛着",
  "learning.vec_desire": "欲求",
  "learning.vec_confusion": "混乱",
  "learning.active_emotions": "アクティブな感情",
  "learning.loyalty": "忠誠と絆",
  "learning.loyalty_level": "忠誠レベル",
  "learning.bond_strength": "絆の強さ",
  "learning.protective": "保護本能",
  "learning.devotion_style": "献身スタイル",
  "learning.emotional_memories": "感情的記憶",
  "learning.no_emotions": "感情拡張がインストールされていません。",
  "learning.personality_title": "パーソナリティとアイデンティティ",
  "learning.soul_file": "SOUL.md — ペルソナ定義",
  "learning.identity_file": "IDENTITY.md — アイデンティティ設定",
  "learning.no_soul": "SOUL.mdが見つかりません。",
  "learning.user_title": "ユーザー理解",
  "learning.user_profile": "学習済みユーザープロファイル",
  "learning.comm_style": "コミュニケーションスタイル",
  "learning.expertise": "専門レベル",
  "learning.response_pref": "応答の好み",
  "learning.mental_state": "精神状態",
  "learning.interests": "主な関心",
  "learning.interaction_stats": "インタラクション統計",
  "learning.stat_messages": "メッセージ",
  "learning.stat_questions": "質問",
  "learning.stat_commands": "コマンド",
  "learning.stat_code": "コードスニペット",
  "learning.user_file": "USER.md",
  "learning.no_user": "ユーザーデータが見つかりません。",
  "learning.knowledge_title": "長期知識",
  "learning.long_term_knowledge": "統合された知識",
  "learning.no_knowledge": "長期知識はまだありません。",
  "learning.disconnected": "ゲートウェイに接続してAI学習データを表示します。",
  "learning.loading": "AI学習データを読み込み中...",
  "learning.refresh": "更新",
  "learning.sec_overview": "概要",
  "learning.sec_memory": "メモリ",
  "learning.sec_emotions": "感情",
  "learning.sec_personality": "パーソナリティ",
  "learning.sec_user": "ユーザーモデル",
  "learning.sec_knowledge": "知識",
  "ks.title": "知識ソース",
  "ks.loading": "読み込み中...",
  "ks.refresh": "更新",
  "ks.add_source": "ソースを追加",
  "ks.edit_source": "ソースを編集",
  "ks.empty_title": "知識ソースがありません",
  "ks.empty_description": "データソースを説明すると、AIが自動的にアクセスしてデータを収集します。",
  "ks.tab_sources": "ソース",
  "ks.tab_collected": "収集済み",
  "ks.status_idle": "待機",
  "ks.status_collecting": "収集中",
  "ks.status_success": "稼働中",
  "ks.status_error": "エラー",
  "ks.disabled": "無効",
  "ks.schedule": "スケジュール",
  "ks.last_collected": "最終収集",
  "ks.docs_ingested": "ドキュメント",
  "ks.runs": "実行回数",
  "ks.collect_now": "今すぐ収集",
  "ks.edit": "編集",
  "ks.enable": "有効化",
  "ks.disable": "無効化",
  "ks.delete": "削除",
  "ks.delete_confirm": "この知識ソースを削除してもよろしいですか？",
  "ks.field_name": "ソース名",
  "ks.field_name_placeholder": "例：HR管理画面、売上API、財務ポータル",
  "ks.field_description": "これはどんなデータソースですか？",
  "ks.field_description_placeholder":
    "例：goldprice.orgから1時間ごとに金価格を取得\n例：hr.company.comのHRシステムにログインして新入社員リストを取得\n例：https://api.example.com/v1/salesから売上データを取得",
  "ks.field_description_hint":
    "データソースを説明してください — 何か、URL、何を収集するか。AIがアクセス方法を分析します。",
  "ks.field_credentials": "認証情報（オプション）",
  "ks.field_credentials_hint":
    "ログインやAPIキーが必要な場合はここに入力。暗号化して保存されます。",
  "ks.field_username": "ユーザー名",
  "ks.field_password": "パスワード",
  "ks.field_api_key": "APIキー",
  "ks.field_interval": "収集間隔",
  "ks.minutes": "分",
  "ks.cancel": "キャンセル",
  "ks.save": "保存",
  "ks.create": "作成",
  "ks.just_now": "たった今",
  "login.title": "Nabe",
  "login.subtitle": "パスワードを入力してください",
  "login.password": "パスワード",
  "login.signin": "サインイン",
  "login.hint": "Enterキーでサインイン",
  "topbar.connected": "接続済み",
  "topbar.offline": "オフライン",
  "topbar.signout": "サインアウト",
  "overview.connection": "接続",
  "overview.online": "オンライン",
  "overview.offline": "オフライン",
  "overview.running_for": "稼働時間",
  "overview.not_connected": "ゲートウェイに未接続",
  "overview.channels": "チャンネル",
  "overview.active_sessions": "アクティブセッション",
  "overview.system_info": "システム情報",
  "overview.getting_started": "はじめに",
  "overview.getting_started_sub": "Nabeを最大限活用するためのヒント。",
  "overview.tip_connect": "アプリを接続",
  "overview.tip_connect_desc": "接続ページでWhatsApp、Telegram、Discordなどを連携します。",
  "overview.tip_chat": "会話を開始",
  "overview.tip_chat_desc": "チャットタブからAIアシスタントと直接会話できます。",
  "overview.tip_settings": "設定をカスタマイズ",
  "overview.tip_settings_desc": "設定ページでお好みに調整してください。",
  "terminal.quick_commands": "クイックコマンド",
  "terminal.quick_sub": "ワンクリックで実行できるよくあるタスク。",
  "terminal.run_command": "コマンドを実行",
  "terminal.run_sub": "コマンドを入力してEnterまたは実行をクリック。",
  "terminal.run": "実行",
  "terminal.running": "実行中...",
  "terminal.output": "出力",
  "terminal.clear": "クリア",
  "terminal.no_commands": "まだコマンドがありません",
  "terminal.no_commands_sub": "上のクイックコマンドを使うか、自分でコマンドを入力してください。",
  "terminal.not_connected": "ゲートウェイに未接続。",
  "terminal.welcome": "Nabe Gateway ターミナルへようこそ",
  "terminal.welcome_hint": "下にコマンドを入力するか、上のボタンを使用してください。",
  "terminal.status": "状態",
  "terminal.config": "設定",
  "terminal.disk": "ディスク",
  "terminal.memory": "メモリ",
  "terminal.processes": "プロセス",
  "terminal.network": "ネットワーク",
  "terminal.uptime": "稼働時間",
  "terminal.logs_btn": "ログ",
  "files.quick_access": "クイックアクセス",
  "files.quick_sub": "サーバー上のよく使うディレクトリへジャンプ。",
  "files.home": "ホーム",
  "files.nabe_config": "Nabe設定",
  "files.app_files": "アプリファイル",
  "files.extensions": "拡張機能",
  "files.temp": "一時",
  "files.root": "ルート",
  "files.go_up": "上のフォルダへ",
  "files.refresh": "更新",
  "files.show_hidden": "隠しファイルを表示",
  "files.items": "件",
  "files.name": "名前",
  "files.size": "サイズ",
  "files.modified": "更新日時",
  "files.empty": "このフォルダは空です。",
  "files.loading": "読み込み中...",
  "files.opening": "ファイルを開いています...",
  "files.save": "保存",
  "files.saving": "保存中...",
  "files.close": "閉じる",
  "files.unsaved": "未保存の変更",
  "files.delete": "削除",
  "files.delete_confirm": "削除",
  "files.not_connected": "ゲートウェイに未接続。",
  "config.save": "保存",
  "config.saving": "保存中...",
  "config.undo": "すべて元に戻す",
  "config.apply": "保存して再起動",
  "config.applying": "適用中...",
  "config.update": "更新を確認",
  "config.updating": "確認中...",
  "config.visual": "ビジュアル",
  "config.advanced": "詳細",
  "config.search": "設定を検索...",
  "hint.gateway":
    "AIとメッセージアプリを接続する中央サーバー。あなたとAI間のメッセージを中継する「頭脳」です。",
  "hint.websocket":
    "ブラウザとサーバー間のリアルタイム接続。ページを更新せずにデータが即座に反映されます。",
  "hint.session_key": "各会話の固有名。チャットルーム名のように、異なるキーで会話を分離します。",
  "hint.thinking_level": "AIが回答前に考える深さ。高い＝遅いが慎重、低い＝速いがシンプルな回答。",
  "hint.block_streaming": "AIが応答を返す方法。全体を待たず、準備できた部分から送信します。",
  "hint.block_streaming_break":
    "応答の各部分をいつ送信するか。'text_end'は段落ごと、'message_end'は完全な回答を待ちます。",
  "hint.block_streaming_chunk":
    "長い応答を小さく分割する方法。文字数制限のあるメッセージアプリに便利です。",
  "hint.exec_approvals":
    "AIがコンピュータ上で実行できるコマンドのセキュリティルール。危険な操作を防ぎます。",
  "hint.bindings":
    "サーバーがリッスンするネットワークアドレス。'loopback'＝このPC のみ、'lan'＝ネットワーク上の全デバイス。",
  "hint.auto_follow": "新しいメッセージが届いたら自動的に会話を続けます。手動クリック不要。",
  "hint.agent":
    "独自の個性、ツール、ワークスペースを持つAIアシスタント。異なるタスク用に複数のエージェントを持てます。",
  "hint.skill":
    "AIに与える特定の能力。ウェブ検索、コード実行、画像分析など。必要に応じて有効/無効にできます。",
  "hint.cron":
    "設定した時間に自動実行されるタスク。AIの目覚まし時計のように、スケジュール通りに起動して作業します。",
  "hint.node":
    "ゲートウェイとペアリングされたコンピュータ。コマンド実行、ファイルアクセス、AIへのサービス提供が可能。",
  "hint.instance":
    "現在ゲートウェイに接続しているデバイスやアプリ。今オンラインのものを表示します。",
  "hint.channel":
    "AIに接続されたメッセージプラットフォーム。Telegram、Discord、WhatsApp、Slackなど。",
  "hint.tls":
    "安全な接続のための暗号化（HTTPS）。玄関の鍵のように、他人がデータを読むのを防ぎます。",
  "hint.tailscale":
    "デバイス間にプライベートネットワークを作るVPNサービス。リモートアクセスを簡単かつ安全にします。",
  "hint.rate_limit": "一定期間内のログイン試行回数を制限。ハッカーのパスワード推測を防ぎます。",
  "hint.trusted_proxy":
    "ゲートウェイの前に置かれるリバースプロキシ（Nginx、Cloudflareなど）が認証を処理します。",
  "hint.sandbox":
    "AIがコマンドを安全に実行する隔離コンテナ。遊び場のように、何をしても実際のシステムに影響しません。",
  "hint.tool_policy":
    "AIが使用できるツールのルール。セキュリティのために特定の機能を許可またはブロックできます。",
  "hint.subagent":
    "メインエージェントが並行作業のために生成するヘルパーAI。アシスタントにサブタスクを委任するようなもの。",
  "hint.memory_search": "AIの長期記憶。会話から重要な事実を保存し、後でAI検索で見つけられます。",
  "hint.tts": "テキスト読み上げ：文字を音声に変換。AIが「声」であなたに話しかけられます。",
  "hint.vision": "AIが画像を理解・説明する能力。写真を送るとAIが内容を教えてくれます。",
  "hint.ollama": "AIモデルをローカルで実行するツール。クラウド不要 — データはあなたの手元に。",
  "hint.api_key":
    "AIがサービスに接続するための秘密鍵。非公開にしてください。絶対に公開しないでください。",
  "hint.model":
    "使用するAIの頭脳。モデルごとに特長が異なり、速いもの、賢いもの、画像理解可能なものがあります。",
  "hint.token": "ゲートウェイとの認証に使う秘密鍵。パスワードに似ていますが自動生成されます。",
  "hint.password_auth":
    "シンプルなパスワード認証。パスワードを知っている人は誰でもゲートウェイにアクセスできます。",
  "hint.webhook":
    "イベント発生時に通知を受けるURL。玄関のチャイムのように、メッセージが来ると鳴ります。",
  "hint.presence":
    "デバイスがオンラインであることを示すシグナル。チャットアプリの緑の点のようなもの。",
  "hint.compaction":
    "古い会話履歴をクリーンアップしてスペースを節約。重要な部分は保持、不要なものは削除。",
  "hint.funnel":
    "Tailscale Funnelはゲートウェイを公開インターネットに公開します。URLを知る人は誰でもアクセス可能。",
  "hint.basepath":
    "Webインターフェースの接頭辞URL。/nabeのようなサブパスでリバースプロキシの後ろで実行する際に使用。",
  "guide.connect_app_title": "メッセージアプリの接続方法",
  "guide.connect_app_steps":
    "1. 接続ページに移動\n2. 接続したいアプリをクリック（例：Telegram）\n3. そのアプリの設定手順に従う\n4. 必要なトークンや認証情報を入力\n5. 保存をクリック — 接続が自動的に開始",
  "guide.setup_agent_title": "AIエージェントの設定方法",
  "guide.setup_agent_steps":
    "1. エージェントページに移動\n2. デフォルトの 'main' エージェントは作成済み\n3. クリックして個性を編集（IDENTITY.md）\n4. 使用するAIモデルを選択\n5. 必要なツール/スキルを有効化",
  "guide.add_skill_title": "スキルの有効化方法",
  "guide.add_skill_steps":
    "1. スキルページに移動\n2. 利用可能なスキルを閲覧\n3. スイッチを切り替えて有効化\n4. 一部のスキルにはAPIキーが必要 — 求められたら入力\n5. AIが会話でこのスキルを使用可能に",
  "guide.schedule_task_title": "タスクのスケジュール方法",
  "guide.schedule_task_steps":
    "1. スケジュールタスクに移動\n2. 'タスクを追加' をクリック\n3. cron式を設定（例：'0 9 * * *' = 毎日午前9時）\n4. AIに何をすべきか記述\n5. 保存 — 設定時刻に自動実行",
  "overview.sessions_now": "現在開いているチャットセッション",
  "overview.scheduled_tasks": "スケジュールタスク",
  "overview.scheduled_none": "なし",
  "overview.scheduled_active": "稼働中",
  "overview.scheduled_paused": "一時停止",
  "overview.attention": "注意が必要です",
  "overview.auth_required":
    "このゲートウェイは認証が必要です。トークンまたはパスワードを追加して接続をクリック。",
  "overview.auth_hint_dashboard": "コントロールUIを開く",
  "overview.auth_hint_token": "トークンを設定",
  "overview.auth_failed":
    "認証に失敗しました。設定でトークンまたはパスワードを更新して接続してください。",
  "overview.docs_auth": "ドキュメント: 認証設定",
  "overview.insecure_context":
    "このページはHTTPのためブラウザがデバイス識別をブロック。HTTPS (Tailscale Serve) を使用するか、以下を開いてください",
  "overview.insecure_stay": "HTTPのままにする場合は、以下を設定",
  "overview.docs_tailscale": "ドキュメント: Tailscale Serve",
  "overview.docs_insecure": "ドキュメント: 非セキュアHTTP",
  "overview.connection_settings": "接続設定",
  "overview.connection_sub": "このダッシュボードがゲートウェイに接続する方法。",
  "overview.server_address": "サーバーアドレス",
  "overview.password": "パスワード",
  "overview.enter_password": "パスワードを入力",
  "overview.session_name": "セッション名",
  "overview.reconnect": "再接続",
  "overview.connect_btn": "接続",
  "overview.refresh_data": "データを更新",
  "overview.system_sub": "ゲートウェイの現在の状態。",
  "overview.running_time": "稼働時間",
  "overview.connected_devices": "接続デバイス",
  "overview.last_refresh": "最終更新",
  "overview.never": "未実行",
  "overview.all_good": "すべて正常に稼働中。接続ページでメッセージアプリを設定しましょう。",
  "channels.details": "接続の詳細",
  "channels.details_sub": "各メッセージアプリの詳細ステータス。",
  "channels.never_checked": "未確認",
  "channels.no_snapshot": "スナップショットなし。",
  "channels.status_sub": "この接続のステータスと設定。",
  "channels.configured": "設定済み",
  "channels.running": "実行中",
  "channels.connected": "接続済み",
  "channels.last_inbound": "最終受信",
  "channels.form_unavailable": "設定フォームが利用できません。",
  "channels.form_unavailable_sub":
    "フォームをロードできませんでした。設定タブで手動編集してください。",
  "channels.plugin_needed": "この接続のフォームはまだ利用できません。",
  "channels.plugin_needed_sub":
    "プラグインの有効化が必要かもしれません。設定タブを確認するかゲートウェイを再起動してください。",
  "channels.loading_schema": "スキーマを読み込み中…",
  "channels.save_changes": "保存",
  "channels.undo_changes": "元に戻す",
  "channels.saving": "保存中...",

  "picker.title": "メッセージアプリを選択",
  "picker.subtitle": "AIと接続するアプリを選んでください。",
  "picker.add": "追加",
  "picker.add_channel": "チャンネル追加",
  "picker.all_added": "利用可能なチャンネルはすべて追加済みです。",
  "picker.already_added": "追加済み",
  "picker.empty_state":
    "チャンネルがまだ設定されていません。「チャンネル追加」をクリックしてください。",
  "picker.easy": "簡単",
  "picker.medium": "普通",
  "picker.hard": "上級",
  "picker.enabled": "有効",
  "guide.title": "セットアップガイド —",
  "guide.full_docs": "完全なドキュメントを読む",

  "config.general": "一般",
  "config.updates": "更新",
  "config.ai_agents": "AIエージェント",
  "config.security": "セキュリティ",
  "config.messaging_apps": "メッセージアプリ",
  "config.messages": "メッセージ",
  "config.commands": "コマンド",
  "config.hooks": "自動化フック",
  "config.skills_section": "スキル",
  "config.tools": "ツール",
  "config.server": "サーバー",
  "config.setup_guide": "セットアップガイド",
  "config.settings": "設定",
  "config.all_settings": "すべての設定",
  "config.unsaved": "未保存の変更あり",
  "config.no_changes": "変更なし",
  "config.discard_title": "変更を破棄してファイルから再読み込み",
  "config.save_title": "設定ファイルに保存",
  "config.restart_title": "保存して関連サービスを再起動",
  "config.update_title": "ソフトウェア更新を確認",
  "config.loading_schema": "スキーマを読み込み中…",
  "config.advanced_warning": "",
  "config.raw_json5": "Raw JSON5",
  "config.all": "すべて",
  "agents.title": "エージェント",
  "agents.configured": "設定済み",
  "agents.no_agents": "エージェントが見つかりません。",
  "agents.select": "エージェントを選択",
  "agents.select_sub": "エージェントを選んでワークスペースとツールを確認。",
  "agents.tab_overview": "概要",
  "agents.tab_files": "ファイル",
  "agents.tab_tools": "ツール",
  "agents.tab_skills": "スキル",
  "agents.tab_channels": "チャンネル",
  "agents.tab_cron": "スケジュール",
  "agents.tab_runtime": "ランタイム",
  "agents.runtime_title": "ランタイムアクティビティ",
  "agents.runtime_sub": "実行中のサブエージェント、キュー状態、最近の完了。",
  "agents.runtime_active": "実行中のサブエージェント",
  "agents.runtime_no_active": "実行中のサブエージェントはありません。",
  "agents.runtime_elapsed": "経過時間",
  "agents.runtime_queue": "キュー状態",
  "agents.runtime_queue_unavailable": "キュー状態を取得できません。",
  "agents.runtime_slots": "スロット",
  "agents.runtime_recent": "最近の完了",
  "agents.runtime_recent_sub": "過去30分以内に完了したサブエージェント。",
  "agents.runtime_no_recent": "最近の完了はありません。",
  "agents.runtime_col_label": "ラベル",
  "agents.runtime_col_status": "状態",
  "agents.runtime_col_model": "モデル",
  "agents.runtime_col_runtime": "実行時間",
  "agents.runtime_watchdog": "ウォッチドッグ",
  "agents.runtime_watchdog_on": "実行中",
  "agents.runtime_watchdog_off": "停止",
  "agents.runtime_scans": "スキャン",
  "agents.runtime_aborted": "中断",
  "agents.runtime_registered": "登録済み",
  "agents.overview_sub": "ワークスペースパスとメタデータ。",
  "agents.workspace": "ワークスペース",
  "agents.primary_model": "メインモデル",
  "agents.identity_name": "アイデンティティ名",
  "agents.default": "デフォルト",
  "agents.identity_emoji": "絵文字",
  "agents.skills_filter": "スキルフィルター",
  "agents.selected": "選択済み",
  "agents.all_skills": "すべてのスキル",
  "agents.model_selection": "モデル選択",
  "agents.primary_default": "メインモデル",
  "agents.inherit_default": "デフォルトを継承",
  "agents.fallbacks": "フォールバック（カンマ区切り）",
  "agents.fallbacks_placeholder": "provider/model, provider/model",
  "agents.reload_config": "設定を再読み込み",
  "agents.api_keys_title": "プロバイダーAPIキー",
  "agents.api_keys_sub": "LLMプロバイダーのAPIキー。暗号化して保存され、全エージェント共有。",
  "agents.api_key_set": "設定済み（非表示）",
  "agents.api_key_placeholder": "APIキーを入力...",
  "agents.catalog_title": "モデルカタログ",
  "agents.catalog_sub": "プライマリまたはフォールバックとして選択可能なモデル。",
  "agents.catalog_model_id": "モデルID",
  "agents.catalog_alias": "エイリアス",
  "agents.api_key_label": "APIキー",
  "agents.catalog_actions": "操作",
  "agents.catalog_add": "モデルを追加",
  "agents.catalog_add_placeholder": "provider/model-name",
  "agents.catalog_alias_placeholder": "短縮名（任意）",
  "agents.catalog_empty": "モデルが設定されていません。下から追加してください。",
  "agents.catalog_confirm_remove":
    "このモデルはプライマリまたはフォールバックで使用中です。削除しますか？",
  "agents.catalog_provider": "プロバイダー",
  "agents.catalog_model": "モデル",
  "agents.catalog_no_providers": "プロバイダーがありません。先にAPIキーを追加してください。",
  "agents.catalog_select_provider": "プロバイダーを選択…",
  "agents.catalog_select_model": "モデルを選択…",
  "agents.fallback_title": "フォールバックモデル",
  "agents.fallback_sub": "プライマリモデルが失敗した場合、順番に試行されます。",
  "agents.fallback_add": "フォールバックを追加",
  "agents.fallback_empty": "フォールバックモデルが設定されていません。",
  "agents.custom_env_title": "カスタム環境変数",
  "agents.custom_env_sub": "カスタムプロバイダー用の追加APIキーや環境変数。",
  "agents.custom_env_add": "変数を追加",
  "agents.custom_env_name_placeholder": "VARIABLE_NAME",
  "agents.custom_env_value_placeholder": "値...",
  "sessions.title": "セッション",
  "sessions.title_sub": "アクティブなセッションキーとオーバーライド。",
  "sessions.active_within": "アクティブ期間（分）",
  "sessions.limit": "上限",
  "sessions.include_global": "グローバルを含む",
  "sessions.include_unknown": "不明を含む",
  "sessions.store": "ストア",
  "sessions.col_key": "キー",
  "sessions.col_label": "ラベル",
  "sessions.col_kind": "種類",
  "sessions.col_updated": "更新",
  "sessions.col_tokens": "トークン",
  "sessions.col_thinking": "思考",
  "sessions.col_verbose": "詳細",
  "sessions.col_reasoning": "推論",
  "sessions.col_actions": "操作",
  "sessions.no_sessions": "セッションが見つかりません。",
  "sessions.optional": "（任意）",
  "sessions.inherit": "継承",
  "sessions.task_log": "タスクログ",
  "sessions.no_messages": "このセッションにメッセージはありません。",
  "sessions.messages_label": "件",
  "cron.title": "スケジューラ",
  "cron.title_sub": "ゲートウェイのスケジューラ状態。",
  "cron.enabled": "有効",
  "cron.jobs": "ジョブ",
  "cron.next_wake": "次回起動",
  "cron.new_job": "新規ジョブ",
  "cron.new_job_sub": "スケジュール起動やエージェント実行を作成。",
  "cron.name": "名前",
  "cron.description": "説明",
  "cron.agent_id": "エージェントID",
  "cron.schedule": "スケジュール",
  "cron.every": "毎",
  "cron.at": "時刻",
  "cron.cron_expr": "Cron",
  "cron.session": "セッション",
  "cron.wake_mode": "起動モード",
  "cron.payload": "ペイロード",
  "cron.main": "メイン",
  "cron.isolated": "分離",
  "cron.now": "今すぐ",
  "cron.next_heartbeat": "次回ハートビート",
  "cron.system_event": "システムイベント",
  "cron.agent_turn": "エージェントターン",
  "cron.delivery": "配信",
  "cron.timeout": "タイムアウト（秒）",
  "cron.channel": "チャンネル",
  "cron.to": "宛先",
  "cron.announce": "サマリー通知（デフォルト）",
  "cron.none_internal": "なし（内部）",
  "cron.add_job": "ジョブ追加",
  "cron.jobs_title": "ジョブ",
  "cron.jobs_sub": "ゲートウェイに保存された全ジョブ。",
  "cron.no_jobs": "ジョブなし。",
  "cron.run_history": "実行履歴",
  "cron.run_history_sub": "最新の実行:",
  "cron.select_job": "ジョブを選んで実行履歴を確認。",
  "cron.no_runs": "実行なし。",
  "cron.run_at": "実行時刻",
  "cron.expression": "式",
  "cron.timezone": "タイムゾーン（任意）",
  "cron.unit_minutes": "分",
  "cron.unit_hours": "時間",
  "cron.unit_days": "日",
  "cron.system": "システム",
  "cron.prompt": "プロンプト",
  "cron.run_btn": "実行",
  "cron.history_btn": "履歴",
  "cron.remove": "削除",
  "cron.status": "状態",
  "cron.next": "次回",
  "cron.last": "前回",
  "cron.open_run_chat": "実行チャットを開く",
  "skills.title": "スキル",
  "skills.title_sub": "内蔵、管理、ワークスペースのスキル。",
  "skills.filter": "フィルター",
  "skills.search": "スキルを検索",
  "skills.shown": "表示中",
  "skills.no_skills": "スキルが見つかりません。",
  "skills.missing": "不足",
  "skills.reason": "理由",
  "skills.api_key": "APIキー",
  "skills.save_key": "キーを保存",
  "nodes.title": "ノード",
  "nodes.title_sub": "ペアリングされたデバイスとライブリンク。",
  "nodes.no_nodes": "ノードが見つかりません。",
  "nodes.devices": "デバイス",
  "nodes.devices_sub": "ペアリング要求 + ロールトークン。",
  "nodes.pending": "保留中",
  "nodes.paired": "ペアリング済み",
  "nodes.no_paired": "ペアリングされたデバイスなし。",
  "nodes.role": "ロール",
  "nodes.roles": "ロール",
  "nodes.scopes": "スコープ",
  "nodes.approve": "承認",
  "nodes.reject": "拒否",
  "nodes.tokens_none": "トークン: なし",
  "nodes.tokens": "トークン",
  "nodes.revoked": "失効",
  "nodes.active_token": "有効",
  "nodes.rotate": "ローテーション",
  "nodes.revoke": "失効させる",
  "nodes.exec_binding": "実行ノードバインディング",
  "nodes.exec_binding_sub": "exec host=node使用時にエージェントを特定ノードに固定。",
  "nodes.switch_form": "フォームモードに切り替えて編集してください。",
  "nodes.load_config": "設定を読み込み",
  "nodes.default_binding": "デフォルトバインディング",
  "nodes.default_binding_sub": "エージェントがノードを指定しない場合に使用。",
  "nodes.any_node": "任意のノード",
  "nodes.no_exec_nodes": "system.run可能なノードなし。",
  "nodes.no_agents_found": "エージェントなし。",
  "nodes.default_agent": "デフォルトエージェント",
  "nodes.uses_default": "デフォルトを使用",
  "nodes.node": "ノード",
  "nodes.override": "オーバーライド",
  "nodes.binding": "バインディング",
  "nodes.use_default": "デフォルトを使用",
  "nodes.status_paired": "ペアリング済み",
  "nodes.status_unpaired": "未ペアリング",
  "nodes.status_connected": "接続中",
  "nodes.status_offline": "オフライン",
  "instances.title": "接続インスタンス",
  "instances.title_sub": "ゲートウェイとクライアントからのプレゼンスビーコン。",
  "instances.no_instances": "インスタンスなし。",
  "instances.last_input": "最終入力",
  "instances.reason": "理由",
  "debug.snapshots": "スナップショット",
  "debug.snapshots_sub": "ステータス、ヘルス、ハートビートデータ。",
  "debug.status": "ステータス",
  "debug.health": "ヘルス",
  "debug.last_heartbeat": "最終ハートビート",
  "debug.security_audit": "セキュリティ監査",
  "debug.for_details": "で詳細を確認。",
  "debug.manual_rpc": "手動RPC",
  "debug.manual_rpc_sub": "JSONパラメータでゲートウェイメソッドを送信。",
  "debug.method": "メソッド",
  "debug.params": "パラメータ (JSON)",
  "debug.call": "呼び出し",
  "debug.models": "モデル",
  "debug.models_sub": "models.listからのカタログ。",
  "debug.event_log": "イベントログ",
  "debug.event_log_sub": "最新のゲートウェイイベント。",
  "debug.no_events": "イベントなし。",
  "logs.title": "ログ",
  "logs.title_sub": "ゲートウェイファイルログ (JSONL)。",
  "logs.export": "エクスポート",
  "logs.filter": "フィルター",
  "logs.search": "ログを検索",
  "logs.auto_follow": "自動追尾",
  "logs.truncated": "ログが切り詰められました。最新部分を表示中。",
  "logs.no_entries": "ログエントリなし。",
  "chat.compacting": "コンテキストを圧縮中...",
  "chat.compacted": "コンテキスト圧縮完了",
  "chat.attachment_preview": "添付ファイルプレビュー",
  "chat.remove_attachment": "添付ファイルを削除",
  "chat.placeholder": "メッセージを追加または画像を貼り付け...",
  "chat.placeholder_shift": "メッセージ (↩ 送信, Shift+↩ 改行, 画像貼り付け可)",
  "chat.not_connected": "ゲートウェイに接続してチャットを開始…",
  "chat.loading": "チャットを読み込み中…",
  "chat.exit_focus": "フォーカスモードを終了",
  "chat.queued": "キュー",
  "chat.remove_queued": "キューからメッセージを削除",
  "chat.image_count": "画像",
  "chat.new_messages": "新しいメッセージ",
  "chat.message": "メッセージ",
  "chat.stop": "停止",
  "chat.new_session": "新規セッション",
  "chat.queue_btn": "キュー",
  "chat.queued_batch": "まとめて送信",
  "chat.send": "送信",
  "chat.history_hidden": "件のメッセージを非表示",
  "chat.compaction_divider": "圧縮",
  "app.brand": "NABE",
  "app.subtitle": "ゲートウェイダッシュボード",
  "app.expand_sidebar": "サイドバーを展開",
  "app.collapse_sidebar": "サイドバーを折りたたむ",
  "app.resources": "リソース",
  "app.docs": "ドキュメント",
  "app.refresh_chat": "チャットデータを更新",
  "app.disabled_onboarding": "オンボーディング中は無効",
  "app.toggle_thinking": "AI思考プロセスの表示を切り替え",
  "app.toggle_focus": "フォーカスモードを切り替え（サイドバー非表示）",
  "app.theme_system": "システム",
  "app.theme_light": "ライト",
  "app.theme_dark": "ダーク",
  "app.theme": "テーマ",
  "common.loading": "読み込み中...",
  "common.error": "エラー",
  "common.cancel": "キャンセル",
  "common.confirm": "確認",
  "common.yes": "はい",
  "common.no": "いいえ",
  "common.na": "なし",
  "common.save": "保存",
  "common.saving": "保存中...",
  "common.refresh": "更新",
  "common.refreshing": "更新中...",
  "common.delete": "削除",
  "common.enable": "有効化",
  "common.disable": "無効化",
  "common.enabled": "有効",
  "common.disabled": "無効",
  "common.language": "言語",
  "common.what_is_this": "これは何？",
  "common.learn_more": "詳細を見る",
  "common.step": "ステップ",
  "common.docs": "ドキュメント",
  "common.resources": "リソース",
  "common.agent": "エージェント",
  // Quick Settings
  "qs.title": "クイック設定",
  "qs.subtitle": "よく使う設定です。すべての項目はサイドバーから選択できます。",
  "qs.ai": "AIアシスタント",
  "qs.ai.model": "AIモデル",
  "qs.ai.model_help": "アシスタントの回答に使用するAIモデル",
  "qs.ai.name": "アシスタント名",
  "qs.ai.name_help": "アシスタントが自己紹介するときの名前",
  "qs.ai.workspace": "作業フォルダ",
  "qs.ai.workspace_help": "ファイルやプロジェクトを保存するフォルダ",
  "qs.conn": "接続",
  "qs.conn.port": "ポート番号",
  "qs.conn.port_help": "ゲートウェイに接続するポート",
  "qs.conn.bind": "接続できる相手",
  "qs.conn.bind_help": "ゲートウェイにアクセスできるデバイスを制御します",
  "qs.conn.bind.loopback": "このコンピュータのみ",
  "qs.conn.bind.lan": "同じネットワーク上のデバイス",
  "qs.conn.bind.tailnet": "インターネット（Tailscale経由）",
  "qs.conn.bind.auto": "自動",
  "qs.conn.auth": "パスワード保護",
  "qs.conn.auth_help": "接続時にパスワードを要求する",
  "qs.conn.password": "パスワード",
  "qs.apps": "メッセージアプリ",
  "qs.apps.subtitle": "メッセージプラットフォームにアシスタントを接続",
  "qs.apps.configure": "設定する",
  "qs.updates": "アップデート",
  "qs.updates.channel": "リリースチャンネル",
  "qs.updates.channel_help": "新しいバージョンを受け取る頻度",
  "qs.updates.auto": "自動チェック",
  "qs.updates.auto_help": "ゲートウェイ起動時にアップデートを確認する",
  "qs.log": "ログ",
  "qs.log.level": "詳細レベル",
  "qs.log.level_help": "ログに記録する詳細の量",
  "qs.all_settings": "すべての設定を表示",
};

const ko: TranslationKeys = {
  "nav.chat": "채팅",
  "nav.dashboard": "대시보드",
  "nav.automation": "자동화",
  "nav.system": "시스템",
  "nav.simple.chat": "채팅",
  "nav.simple.status": "상태",
  "nav.simple.settings": "설정",
  "nav.simple.tools": "도구",
  "nav.simple.help": "도움말",
  "nav.mode.simple": "간단",
  "nav.mode.advanced": "고급",
  "nav.mode.toggle_label": "내비게이션 모드",
  "tab.chat": "채팅",
  "tab.overview": "홈",
  "tab.dashboard": "대시보드",
  "tab.channels": "연결",
  "tab.instances": "기기",
  "tab.sessions": "대화",
  "tab.usage": "사용량",
  "tab.agents": "에이전트",
  "tab.skills": "스킬",
  "tab.cron": "예약 작업",
  "tab.terminal": "터미널",
  "tab.files": "파일",
  "tab.nodes": "컴퓨터",
  "tab.config": "설정",
  "tab.debug": "고급",
  "tab.heartbeat": "하트비트",
  "sub.heartbeat": "하트비트 분석 및 모니터링",
  "hb.title": "하트비트 분석",
  "hb.subtitle": "실시간 하트비트 모니터링",
  "hb.timeline": "타임라인",
  "hb.event_log": "이벤트 로그",
  "hb.no_events": "이벤트 없음",
  "hb.refresh": "새로고침",
  "hb.pause": "일시정지",
  "hb.resume": "재개",
  "hb.loading": "로딩 중...",
  "hb.new": "NEW",
  "hb.stat_total": "총 실행",
  "hb.stat_ok_rate": "OK 비율",
  "hb.stat_alert_rate": "알림 비율",
  "hb.stat_avg_duration": "평균 시간",
  "hb.stat_skipped": "스킵",
  "hb.stat_failed": "실패",
  "hb.stat_duplicates": "중복 감지",
  "hb.stat_api_saved": "API 절약",
  "hb.status_sent": "전송됨",
  "hb.status_ok-empty": "OK (비어있음)",
  "hb.status_ok-token": "OK (토큰)",
  "hb.status_skipped": "건너뜀",
  "hb.status_failed": "실패",
  "tab.logs": "활동 로그",
  "tab.learning": "AI 학습",
  "tab.mind": "마인드",
  "tab.activity-log": "활동 로그 (DB)",
  "sub.chat": "AI 어시스턴트와 직접 대화하세요.",
  "sub.overview": "모든 상태를 한눈에 확인하세요.",
  "sub.dashboard": "노드, 에이전트, 작업의 실시간 모니터링.",
  "sub.channels": "WhatsApp, Telegram, Discord 등의 메신저를 연결하세요.",
  "sub.instances": "현재 연결된 기기와 앱을 확인하세요.",
  "sub.sessions": "진행 중인 대화를 보고 관리하세요.",
  "sub.agents": "AI 어시스턴트와 설정을 관리합니다.",
  "sub.skills": "AI의 기능을 활성화/비활성화합니다.",
  "sub.cron": "자동으로 실행되는 예약 작업을 설정합니다.",
  "sub.terminal": "SSH 없이 서버에서 명령을 실행합니다.",
  "sub.files": "서버의 파일을 탐색하고 편집합니다.",
  "sub.nodes": "연결된 컴퓨터와 권한을 관리합니다.",
  "sub.config": "모든 시스템 설정을 한곳에서 조정합니다.",
  "sub.debug": "문제 해결을 위한 기술 세부 정보.",
  "sub.logs": "최근 활동을 확인합니다.",
  "sub.learning": "AI 메모리, 감정 및 지식을 봅니다.",
  "sub.mind": "인지 아키텍처 모니터링 및 분석.",
  "mind.soul": "AI 영혼",
  "mind.soul.emotions": "감정 상태",
  "mind.soul.lifecycle": "생명 주기",
  "mind.soul.personality": "성격",
  "mind.soul.values": "핵심 가치",
  "mind.disconnected": "게이트웨이에 연결하여 인지 상태를 확인하세요.",
  "mind.plugin_unavailable": "Cognitive Mind 플러그인이 비활성 상태입니다",
  "mind.plugin_unavailable_hint": "autonomous-mind 확장을 활성화하여 데이터를 확인하세요.",
  "mind.loading": "인지 상태 로딩 중...",
  "mind.retry": "재시도",
  "mind.sec_overview": "개요",
  "mind.sec_soul": "영혼",
  "mind.sec_depth": "깊이",
  "mind.sec_thoughts": "생각",
  "mind.sec_workspace": "워크스페이스",
  "mind.sec_identity": "정체성",
  "mind.sec_analytics": "분석",
  "mind.emotion_love": "사랑",
  "mind.emotion_anger": "분노",
  "mind.emotion_sadness": "슬픔",
  "mind.emotion_joy": "기쁨",
  "mind.emotion_fear": "두려움",
  "mind.emotion_trust": "신뢰",
  "mind.emotion_greed": "탐욕",
  "mind.emotion_delusion": "미혹",
  "mind.mood_valence": "감정 방향",
  "mind.mood_balanced": "균형",
  "mind.mood_unknown": "알 수 없음",
  "mind.loyalty": "충성",
  "mind.stage_newborn": "탄생",
  "mind.stage_child": "유년",
  "mind.stage_adolescent": "청소년",
  "mind.stage_adult": "성인",
  "mind.stage_elder": "노년",
  "mind.stage_transcendent": "초월",
  "mind.meter_health": "건강",
  "mind.meter_energy": "에너지",
  "mind.meter_wisdom": "지혜",
  "mind.personality_developing": "대화를 통해 성격이 발달 중...",
  "mind.overview_title": "인지 개요",
  "mind.card_depth": "인지 깊이",
  "mind.card_mirror": "MIRROR 스레드",
  "mind.card_workspace": "워크스페이스",
  "mind.card_identity": "정체성",
  "mind.card_goals": "목표",
  "mind.card_thoughts": "생각",
  "mind.inactive": "비활성",
  "mind.no_data": "데이터 없음",
  "mind.active": "활성",
  "mind.idle": "대기",
  "mind.waiting": "입력 대기",
  "mind.entries": "항목",
  "mind.traits": "특성",
  "mind.values_count": "가치관",
  "mind.active_count": "활성",
  "mind.recent_count": "최근",
  "mind.global_memory": "글로벌 워킹 메모리",
  "mind.inner_monologue": "내면 독백 스트림",
  "mind.no_goals": "활성 목표 없음",
  "mind.unavailable": "사용 불가",
  "mind.recent_thoughts": "최근 생각",
  "mind.no_thoughts": "아직 생각이 없습니다. 마음이 고요합니다.",
  "mind.depth_title": "인지 깊이 분석",
  "mind.depth_current": "현재 깊이",
  "mind.depth_instinctive": "본능적",
  "mind.depth_associative": "연상적",
  "mind.depth_analytical": "분석적",
  "mind.depth_strategic": "전략적",
  "mind.confidence": "확신도",
  "mind.depth_distribution": "깊이 분포",
  "mind.recent_classifications": "최근 분류",
  "mind.col_depth": "깊이",
  "mind.col_confidence": "확신도",
  "mind.col_phases": "단계",
  "mind.col_time": "시간",
  "mind.loading_depth": "깊이 데이터 로딩 중...",
  "mind.thoughts_title": "생각 스트림",
  "mind.no_thoughts_yet": "아직 기록된 생각이 없습니다.",
  "mind.workspace_title": "글로벌 워크스페이스",
  "mind.ws_entries": "항목",
  "mind.ws_active_entries": "활성 워크스페이스 항목",
  "mind.ws_attention": "주의",
  "mind.ws_top5": "상위 5",
  "mind.ws_salience": "중요도 기반 선택",
  "mind.ws_description":
    "글로벌 워크스페이스는 AI의 현재 워킹 메모리를 보유합니다. 중요도와 최신성으로 순위가 매겨집니다.",
  "mind.identity_title": "정체성",
  "mind.id_continuity": "연속성",
  "mind.id_consistent": "정체성이 일관됩니다",
  "mind.id_drift": "정체성 변동 감지",
  "mind.id_traits": "자기 모델 특성",
  "mind.id_values": "가치관",
  "mind.id_no_traits": "아직 발달된 특성이 없습니다.",
  "mind.id_no_values": "아직 확립된 가치관이 없습니다.",
  "mind.id_loading": "정체성 데이터 로딩 중...",
  "mind.analytics_title": "분석",
  "mind.an_classifications": "분류",
  "mind.an_override_rate": "오버라이드율",
  "mind.an_loop_perf": "루프 성능",
  "mind.an_avg": "평균",
  "mind.an_runs_max": "회",
  "mind.an_dominant": "주요 깊이",
  "mind.an_avg_confidence": "평균 확신도",
  "mind.an_phase_failures": "단계 실패",
  "mind.an_failures": "실패",
  "mind.an_loading": "분석 데이터 로딩 중...",
  "mind.time_just_now": "방금",
  "mind.time_m_ago": "분 전",
  "mind.time_h_ago": "시간 전",
  "mind.time_d_ago": "일 전",
  "sub.activity-log": "MongoDB에 저장된 전체 활동 로그 (7일 보관).",
  "tab.spawn-monitor": "Spawn 모니터",
  "sub.spawn-monitor": "오케스트레이터 디스패치 및 서브에이전트 모니터링.",
  "spawn_monitor.title": "Spawn 모니터",
  "spawn_monitor.sub": "오케스트레이터/spawn 모드의 실시간 모니터링.",
  "spawn_monitor.active_now": "활성",
  "spawn_monitor.total_recent": "최근",
  "spawn_monitor.avg_runtime": "평균 시간",
  "spawn_monitor.success_rate": "성공률",
  "spawn_monitor.active_title": "활성 Spawn",
  "spawn_monitor.no_active": "활성 서브에이전트가 없습니다.",
  "spawn_monitor.elapsed": "경과",
  "spawn_monitor.recent_title": "최근 완료",
  "spawn_monitor.recent_sub": "완료된 Spawn 디스패치 (최근 30분).",
  "spawn_monitor.no_recent": "최근 Spawn 완료 없음.",
  "spawn_monitor.col_message": "메시지",
  "spawn_monitor.col_status": "상태",
  "spawn_monitor.col_model": "모델",
  "spawn_monitor.col_runtime": "실행 시간",
  "spawn_monitor.col_time": "시간",
  "spawn_monitor.events_title": "실시간 이벤트",
  "spawn_monitor.events_sub": "실시간 Spawn 라이프사이클 이벤트.",
  "spawn_monitor.no_events": "아직 Spawn 이벤트가 없습니다. Spawn 모드가 활성화되면 표시됩니다.",
  "spawn_monitor.clear_events": "지우기",
  "spawn_monitor.auto_refresh": "자동 새로고침",
  "dashboard.nodes": "노드",
  "dashboard.nodes_online": "온라인",
  "dashboard.agents": "에이전트",
  "dashboard.agents_registered": "등록됨",
  "dashboard.devices": "기기",
  "dashboard.devices_connected": "연결됨",
  "dashboard.realtime": "실시간",
  "dashboard.refresh": "새로고침",
  "dashboard.connected_nodes": "연결된 노드",
  "dashboard.disconnected_nodes": "연결 끊긴 노드",
  "dashboard.loading": "노드 로딩 중...",
  "dashboard.no_nodes": "연결된 노드가 없습니다.",
  "dashboard.commands": "명령",
  "dashboard.node_capabilities": "노드 기능",
  "dashboard.node_capabilities_sub": "각 노드에서 사용 가능한 명령과 기능.",
  "dashboard.node": "노드",
  "dashboard.status": "상태",
  "dashboard.capabilities": "명령",
  "dashboard.uptime": "연결됨",
  "dashboard.agents_list": "에이전트",
  "dashboard.queue_title": "명령 큐",
  "dashboard.queue_sub": "모든 레인의 활성 및 대기 중인 작업",
  "dashboard.queue_active": "활성",
  "dashboard.queue_queued": "대기",
  "dashboard.queue_lane": "레인",
  "dashboard.queue_concurrency": "최대 동시실행",
  "dashboard.queue_idle": "모든 큐가 유휴 상태입니다.",
  "dashboard.queue_idle_lanes": "유휴 레인",
  "learning.overview_title": "AI 학습 개요",
  "learning.memory_entries": "메모리 항목",
  "learning.emotions_active": "활성 감정",
  "learning.user_messages": "분석된 메시지",
  "learning.modules": "학습 모듈",
  "learning.mod_memory": "메모리",
  "learning.mod_memory_desc": "영구 지식을 위한 핵심 메모리 파일",
  "learning.mod_soul": "성격",
  "learning.mod_soul_desc": "페르소나와 음성을 정의하는 소울 파일",
  "learning.mod_emotions": "감정 코어",
  "learning.mod_emotions_desc": "감정 추적 및 감성 분석",
  "learning.mod_user": "사용자 모델링",
  "learning.mod_user_desc": "적응형 사용자 프로필 및 환경설정",
  "learning.mod_tiered": "계층 메모리",
  "learning.mod_tiered_desc": "망각 곡선이 있는 단기 및 장기 메모리",
  "learning.active": "활성",
  "learning.inactive": "비활성",
  "learning.generate": "생성",
  "learning.regenerate": "재생성",
  "learning.generating": "생성 중...",
  "learning.init_all": "모두 생성",
  "learning.generate_memory": "메모리 파일 생성",
  "learning.generate_soul": "성격 생성",
  "learning.generate_emotions": "감정 초기화",
  "learning.generate_user": "사용자 프로필 생성",
  "learning.generate_knowledge": "지식 정리",
  "learning.current_mood": "현재 기분",
  "learning.mood_valence": "감정가",
  "learning.mood_stability": "안정성",
  "learning.mood_energy": "에너지",
  "learning.mood_warmth": "따뜻함",
  "learning.memory_title": "메모리",
  "learning.tiered_memory": "계층 메모리 시스템",
  "learning.short_term": "단기",
  "learning.long_term": "장기",
  "learning.entities": "엔티티",
  "learning.entity_graph": "엔티티 그래프",
  "learning.recent_memories": "최근 메모리",
  "learning.memory_file": "MEMORY.md",
  "learning.no_memory": "메모리 데이터를 찾을 수 없습니다.",
  "learning.memory_history": "메모리 기록",
  "learning.load_history": "기록 불러오기",
  "learning.no_history": '"기록 불러오기"를 클릭하여 과거 메모리를 확인하세요.',
  "learning.emotions_title": "감정 상태",
  "learning.emotion_vector": "감정 벡터",
  "learning.vec_valence": "감정가",
  "learning.vec_arousal": "각성",
  "learning.vec_dominance": "지배성",
  "learning.vec_attachment": "애착",
  "learning.vec_desire": "욕구",
  "learning.vec_confusion": "혼란",
  "learning.active_emotions": "활성 감정",
  "learning.loyalty": "충성과 유대",
  "learning.loyalty_level": "충성 수준",
  "learning.bond_strength": "유대 강도",
  "learning.protective": "보호 본능",
  "learning.devotion_style": "헌신 스타일",
  "learning.emotional_memories": "감정적 기억",
  "learning.no_emotions": "감정 확장이 설치되지 않았습니다.",
  "learning.personality_title": "성격과 정체성",
  "learning.soul_file": "SOUL.md — 페르소나 정의",
  "learning.identity_file": "IDENTITY.md — 정체성 설정",
  "learning.no_soul": "SOUL.md를 찾을 수 없습니다.",
  "learning.user_title": "사용자 이해",
  "learning.user_profile": "학습된 사용자 프로필",
  "learning.comm_style": "소통 스타일",
  "learning.expertise": "전문 수준",
  "learning.response_pref": "응답 선호",
  "learning.mental_state": "정신 상태",
  "learning.interests": "주요 관심사",
  "learning.interaction_stats": "상호작용 통계",
  "learning.stat_messages": "메시지",
  "learning.stat_questions": "질문",
  "learning.stat_commands": "명령",
  "learning.stat_code": "코드 스니펫",
  "learning.user_file": "USER.md",
  "learning.no_user": "사용자 데이터를 찾을 수 없습니다.",
  "learning.knowledge_title": "장기 지식",
  "learning.long_term_knowledge": "통합된 지식",
  "learning.no_knowledge": "아직 장기 지식이 없습니다.",
  "learning.disconnected": "게이트웨이에 연결하여 AI 학습 데이터를 봅니다.",
  "learning.loading": "AI 학습 데이터 로드 중...",
  "learning.refresh": "새로고침",
  "learning.sec_overview": "개요",
  "learning.sec_memory": "메모리",
  "learning.sec_emotions": "감정",
  "learning.sec_personality": "성격",
  "learning.sec_user": "사용자 모델",
  "learning.sec_knowledge": "지식",
  "ks.title": "지식 소스",
  "ks.loading": "로딩 중...",
  "ks.refresh": "새로고침",
  "ks.add_source": "소스 추가",
  "ks.edit_source": "소스 편집",
  "ks.empty_title": "지식 소스가 없습니다",
  "ks.empty_description": "데이터 소스를 설명하면 AI가 자동으로 접근하여 데이터를 수집합니다.",
  "ks.tab_sources": "소스",
  "ks.tab_collected": "수집됨",
  "ks.status_idle": "대기",
  "ks.status_collecting": "수집 중",
  "ks.status_success": "활성",
  "ks.status_error": "오류",
  "ks.disabled": "비활성",
  "ks.schedule": "스케줄",
  "ks.last_collected": "마지막 수집",
  "ks.docs_ingested": "문서",
  "ks.runs": "실행 횟수",
  "ks.collect_now": "지금 수집",
  "ks.edit": "편집",
  "ks.enable": "활성화",
  "ks.disable": "비활성화",
  "ks.delete": "삭제",
  "ks.delete_confirm": "이 지식 소스를 삭제하시겠습니까?",
  "ks.field_name": "소스 이름",
  "ks.field_name_placeholder": "예: HR 대시보드, 매출 API, 재무 포털",
  "ks.field_description": "이 데이터 소스는 무엇인가요?",
  "ks.field_description_placeholder":
    "예: goldprice.org에서 매시간 금 시세 가져오기\n예: hr.company.com HR 시스템에 로그인하여 신규 직원 목록 가져오기\n예: https://api.example.com/v1/sales에서 매출 데이터 가져오기",
  "ks.field_description_hint":
    "데이터 소스를 설명해주세요 — 무엇인지, URL, 무엇을 수집할지. AI가 접근 방법을 분석합니다.",
  "ks.field_credentials": "인증 정보 (선택사항)",
  "ks.field_credentials_hint":
    "로그인이나 API 키가 필요한 경우 여기에 입력하세요. 암호화하여 저장됩니다.",
  "ks.field_username": "사용자 이름",
  "ks.field_password": "비밀번호",
  "ks.field_api_key": "API 키",
  "ks.field_interval": "수집 주기",
  "ks.minutes": "분",
  "ks.cancel": "취소",
  "ks.save": "저장",
  "ks.create": "생성",
  "ks.just_now": "방금",
  "login.title": "Nabe",
  "login.subtitle": "비밀번호를 입력하세요",
  "login.password": "비밀번호",
  "login.signin": "로그인",
  "login.hint": "Enter 키를 눌러 로그인",
  "topbar.connected": "연결됨",
  "topbar.offline": "오프라인",
  "topbar.signout": "로그아웃",
  "overview.connection": "연결",
  "overview.online": "온라인",
  "overview.offline": "오프라인",
  "overview.running_for": "가동 시간",
  "overview.not_connected": "게이트웨이에 연결되지 않음",
  "overview.channels": "채널",
  "overview.active_sessions": "활성 세션",
  "overview.system_info": "시스템 정보",
  "overview.getting_started": "시작하기",
  "overview.getting_started_sub": "Nabe를 최대한 활용하기 위한 팁입니다.",
  "overview.tip_connect": "앱 연결하기",
  "overview.tip_connect_desc": "연결 페이지에서 WhatsApp, Telegram, Discord 등을 연동하세요.",
  "overview.tip_chat": "대화 시작하기",
  "overview.tip_chat_desc": "채팅 탭에서 AI 어시스턴트와 직접 대화하세요.",
  "overview.tip_settings": "설정 커스터마이즈",
  "overview.tip_settings_desc": "설정 페이지에서 원하는 대로 조정하세요.",
  "terminal.quick_commands": "빠른 명령",
  "terminal.quick_sub": "원클릭으로 실행할 수 있는 일반 작업.",
  "terminal.run_command": "명령 실행",
  "terminal.run_sub": "명령을 입력하고 Enter 또는 실행을 클릭하세요.",
  "terminal.run": "실행",
  "terminal.running": "실행 중...",
  "terminal.output": "출력",
  "terminal.clear": "지우기",
  "terminal.no_commands": "아직 명령이 없습니다",
  "terminal.no_commands_sub": "위의 빠른 명령을 사용하거나 직접 명령을 입력하세요.",
  "terminal.not_connected": "게이트웨이에 연결되지 않음.",
  "terminal.welcome": "Nabe Gateway 터미널에 오신 것을 환영합니다",
  "terminal.welcome_hint": "아래에 명령을 입력하거나 위의 버튼을 사용하세요.",
  "terminal.status": "상태",
  "terminal.config": "설정",
  "terminal.disk": "디스크",
  "terminal.memory": "메모리",
  "terminal.processes": "프로세스",
  "terminal.network": "네트워크",
  "terminal.uptime": "가동시간",
  "terminal.logs_btn": "로그",
  "files.quick_access": "빠른 접근",
  "files.quick_sub": "서버의 자주 사용하는 디렉토리로 이동.",
  "files.home": "홈",
  "files.nabe_config": "Nabe 설정",
  "files.app_files": "앱 파일",
  "files.extensions": "확장 기능",
  "files.temp": "임시",
  "files.root": "루트",
  "files.go_up": "상위 폴더로",
  "files.refresh": "새로고침",
  "files.show_hidden": "숨김 파일 표시",
  "files.items": "개 항목",
  "files.name": "이름",
  "files.size": "크기",
  "files.modified": "수정일",
  "files.empty": "이 폴더는 비어있습니다.",
  "files.loading": "로딩 중...",
  "files.opening": "파일 여는 중...",
  "files.save": "저장",
  "files.saving": "저장 중...",
  "files.close": "닫기",
  "files.unsaved": "저장되지 않은 변경",
  "files.delete": "삭제",
  "files.delete_confirm": "삭제",
  "files.not_connected": "게이트웨이에 연결되지 않음.",
  "config.save": "저장",
  "config.saving": "저장 중...",
  "config.undo": "모두 되돌리기",
  "config.apply": "저장 후 재시작",
  "config.applying": "적용 중...",
  "config.update": "업데이트 확인",
  "config.updating": "확인 중...",
  "config.visual": "비주얼",
  "config.advanced": "고급",
  "config.search": "설정 검색...",
  "hint.gateway": "AI와 메신저 앱을 연결하는 중앙 서버. 메시지를 중계하는 '두뇌' 역할.",
  "hint.websocket": "브라우저와 서버 간의 실시간 연결. 새로고침 없이 데이터가 즉시 업데이트됩니다.",
  "hint.session_key": "각 대화의 고유 이름. 채팅방 이름처럼 다른 키로 대화를 분리합니다.",
  "hint.thinking_level":
    "AI가 답변 전에 생각하는 깊이. 높음 = 느리지만 신중, 낮음 = 빠르지만 단순한 답변.",
  "hint.block_streaming": "AI가 응답을 보내는 방식. 전체를 기다리지 않고 준비된 부분부터 보냅니다.",
  "hint.block_streaming_break":
    "응답 조각을 보내는 시점. 'text_end'는 단락마다, 'message_end'는 전체 완료 후.",
  "hint.block_streaming_chunk":
    "긴 응답을 작게 나누는 방법. 글자 수 제한이 있는 메신저에 유용합니다.",
  "hint.exec_approvals":
    "AI가 컴퓨터에서 실행할 수 있는 명령의 보안 규칙. 위험한 작업을 방지합니다.",
  "hint.bindings":
    "서버가 수신하는 네트워크 주소. 'loopback' = 이 PC만, 'lan' = 네트워크의 모든 기기.",
  "hint.auto_follow": "새 메시지가 오면 자동으로 대화를 계속합니다. 수동 클릭 불필요.",
  "hint.agent":
    "고유한 성격, 도구, 작업 공간을 가진 AI 어시스턴트. 다른 작업을 위해 여러 에이전트를 가질 수 있습니다.",
  "hint.skill":
    "AI에게 줄 수 있는 특정 능력. 웹 검색, 코드 실행, 이미지 분석 등. 필요에 따라 켜거나 끌 수 있습니다.",
  "hint.cron":
    "설정한 시간에 자동 실행되는 작업. AI의 알람시계처럼 스케줄에 따라 깨어나 작업합니다.",
  "hint.node": "게이트웨이와 페어링된 컴퓨터. 명령 실행, 파일 접근, AI에 서비스 제공이 가능합니다.",
  "hint.instance": "현재 게이트웨이에 연결된 기기나 앱. 지금 온라인인 것을 보여줍니다.",
  "hint.channel": "AI에 연결된 메시지 플랫폼. Telegram, Discord, WhatsApp, Slack 등.",
  "hint.tls":
    "보안 연결을 위한 암호화(HTTPS). 현관 잠금장치처럼 타인이 데이터를 읽는 것을 방지합니다.",
  "hint.tailscale":
    "기기 간 프라이빗 네트워크를 만드는 VPN 서비스. 원격 접근을 쉽고 안전하게 합니다.",
  "hint.rate_limit": "일정 기간 내 로그인 시도 횟수 제한. 해커의 비밀번호 추측을 방지합니다.",
  "hint.trusted_proxy":
    "게이트웨이 앞에 있는 리버스 프록시(Nginx, Cloudflare 등)가 인증을 처리합니다.",
  "hint.sandbox":
    "AI가 명령을 안전하게 실행하는 격리 컨테이너. 놀이터처럼 실제 시스템에 영향을 주지 않습니다.",
  "hint.tool_policy":
    "AI가 사용할 수 있는 도구 규칙. 보안을 위해 특정 기능을 허용하거나 차단할 수 있습니다.",
  "hint.subagent":
    "메인 에이전트가 병렬 작업을 위해 생성하는 헬퍼 AI. 어시스턴트에게 하위 작업을 위임하는 것과 같습니다.",
  "hint.memory_search":
    "AI의 장기 기억. 대화에서 중요한 사실을 저장하고 나중에 AI 검색으로 찾을 수 있습니다.",
  "hint.tts": "텍스트 음성 변환: 글을 음성으로 변환. AI가 '목소리'로 대화할 수 있습니다.",
  "hint.vision": "AI가 이미지를 이해하고 설명하는 능력. 사진을 보내면 AI가 내용을 알려줍니다.",
  "hint.ollama": "AI 모델을 로컬에서 실행하는 도구. 클라우드 불필요 — 데이터가 당신 곁에.",
  "hint.api_key": "AI가 서비스에 연결하기 위한 비밀 키. 비공개로 유지하세요. 절대 공개하지 마세요.",
  "hint.model":
    "사용 중인 AI 두뇌. 모델마다 강점이 다름 — 빠른 것, 똑똑한 것, 이미지 이해 가능한 것.",
  "hint.token": "게이트웨이 인증에 사용되는 비밀 키. 비밀번호와 비슷하지만 자동 생성됩니다.",
  "hint.password_auth":
    "간단한 비밀번호 인증. 비밀번호를 아는 사람은 누구나 게이트웨이에 접근할 수 있습니다.",
  "hint.webhook": "이벤트 발생 시 알림을 받는 URL. 초인종처럼 메시지가 오면 울립니다.",
  "hint.presence": "기기가 온라인임을 보여주는 신호. 채팅 앱의 초록 점과 같습니다.",
  "hint.compaction":
    "오래된 대화 기록을 정리하여 공간 절약. 중요한 부분은 유지, 불필요한 것은 제거.",
  "hint.funnel":
    "Tailscale Funnel은 게이트웨이를 공개 인터넷에 노출합니다. URL을 아는 사람은 누구나 접근 가능.",
  "hint.basepath":
    "웹 인터페이스의 URL 접두사. /nabe 같은 서브 경로에서 리버스 프록시 뒤에서 실행할 때 사용.",
  "guide.connect_app_title": "메신저 앱 연결 방법",
  "guide.connect_app_steps":
    "1. 연결 페이지로 이동\n2. 연결할 앱 클릭 (예: Telegram)\n3. 해당 앱의 설정 안내를 따르기\n4. 필요한 토큰이나 인증 정보 입력\n5. 저장 클릭 — 연결이 자동으로 시작",
  "guide.setup_agent_title": "AI 에이전트 설정 방법",
  "guide.setup_agent_steps":
    "1. 에이전트 페이지로 이동\n2. 기본 'main' 에이전트는 이미 생성됨\n3. 클릭하여 성격 편집 (IDENTITY.md)\n4. 사용할 AI 모델 선택\n5. 필요한 도구/스킬 활성화",
  "guide.add_skill_title": "스킬 활성화 방법",
  "guide.add_skill_steps":
    "1. 스킬 페이지로 이동\n2. 사용 가능한 스킬 탐색\n3. 스위치를 켜서 활성화\n4. 일부 스킬은 API 키 필요 — 요청 시 입력\n5. AI가 대화에서 이 스킬 사용 가능",
  "guide.schedule_task_title": "작업 스케줄 설정 방법",
  "guide.schedule_task_steps":
    "1. 예약 작업으로 이동\n2. '작업 추가' 클릭\n3. cron 표현식 설정 (예: '0 9 * * *' = 매일 오전 9시)\n4. AI가 해야 할 일 작성\n5. 저장 — 설정 시간에 자동 실행",
  "overview.sessions_now": "현재 열린 채팅 세션",
  "overview.scheduled_tasks": "예약 작업",
  "overview.scheduled_none": "없음",
  "overview.scheduled_active": "활성",
  "overview.scheduled_paused": "일시 중지",
  "overview.attention": "주의가 필요합니다",
  "overview.auth_required":
    "이 게이트웨이는 인증이 필요합니다. 토큰이나 비밀번호를 추가하고 연결을 클릭하세요.",
  "overview.auth_hint_dashboard": "컨트롤 UI 열기",
  "overview.auth_hint_token": "토큰 설정",
  "overview.auth_failed": "인증 실패. 설정에서 토큰이나 비밀번호를 업데이트하고 연결하세요.",
  "overview.docs_auth": "문서: 인증 설정",
  "overview.insecure_context":
    "이 페이지는 HTTP이므로 브라우저가 기기 식별을 차단합니다. HTTPS (Tailscale Serve)를 사용하거나 다음을 열어주세요",
  "overview.insecure_stay": "HTTP를 유지해야 한다면 다음을 설정",
  "overview.docs_tailscale": "문서: Tailscale Serve",
  "overview.docs_insecure": "문서: 비보안 HTTP",
  "overview.connection_settings": "연결 설정",
  "overview.connection_sub": "이 대시보드가 게이트웨이에 연결하는 방법.",
  "overview.server_address": "서버 주소",
  "overview.password": "비밀번호",
  "overview.enter_password": "비밀번호 입력",
  "overview.session_name": "세션 이름",
  "overview.reconnect": "재연결",
  "overview.connect_btn": "연결",
  "overview.refresh_data": "데이터 새로고침",
  "overview.system_sub": "게이트웨이의 현재 상태.",
  "overview.running_time": "가동 시간",
  "overview.connected_devices": "연결된 기기",
  "overview.last_refresh": "마지막 갱신",
  "overview.never": "없음",
  "overview.all_good": "모든 것이 정상 작동 중. 연결 페이지에서 메신저 앱을 설정하세요.",
  "channels.details": "연결 상세",
  "channels.details_sub": "연결된 각 메신저 앱의 상세 상태.",
  "channels.never_checked": "확인된 적 없음",
  "channels.no_snapshot": "스냅샷 없음.",
  "channels.status_sub": "이 연결의 상태와 설정.",
  "channels.configured": "설정됨",
  "channels.running": "실행 중",
  "channels.connected": "연결됨",
  "channels.last_inbound": "마지막 수신",
  "channels.form_unavailable": "설정 폼을 사용할 수 없습니다.",
  "channels.form_unavailable_sub": "폼을 로드할 수 없습니다. 설정 탭에서 수동 편집하세요.",
  "channels.plugin_needed": "이 연결의 설정 폼은 아직 사용할 수 없습니다.",
  "channels.plugin_needed_sub":
    "플러그인 활성화가 필요할 수 있습니다. 설정 탭을 확인하거나 게이트웨이를 재시작하세요.",
  "channels.loading_schema": "스키마 로딩 중…",
  "channels.save_changes": "저장",
  "channels.undo_changes": "되돌리기",
  "channels.saving": "저장 중...",

  "picker.title": "메신저 앱 선택",
  "picker.subtitle": "AI와 연결할 앱을 선택하세요.",
  "picker.add": "추가",
  "picker.add_channel": "채널 추가",
  "picker.all_added": "모든 채널이 추가되었습니다.",
  "picker.already_added": "추가됨",
  "picker.empty_state": '아직 채널이 없습니다. "채널 추가"를 클릭하세요.',
  "picker.easy": "쉬움",
  "picker.medium": "보통",
  "picker.hard": "고급",
  "picker.enabled": "활성화",
  "guide.title": "설정 가이드 —",
  "guide.full_docs": "전체 문서 읽기",

  "config.general": "일반",
  "config.updates": "업데이트",
  "config.ai_agents": "AI 에이전트",
  "config.security": "보안",
  "config.messaging_apps": "메신저 앱",
  "config.messages": "메시지",
  "config.commands": "명령",
  "config.hooks": "자동화 훅",
  "config.skills_section": "스킬",
  "config.tools": "도구",
  "config.server": "서버",
  "config.setup_guide": "설정 가이드",
  "config.settings": "설정",
  "config.all_settings": "모든 설정",
  "config.unsaved": "저장되지 않은 변경",
  "config.no_changes": "변경 없음",
  "config.discard_title": "변경사항 취소 후 파일에서 다시 로드",
  "config.save_title": "설정 파일에 저장",
  "config.restart_title": "저장 후 관련 서비스 재시작",
  "config.update_title": "소프트웨어 업데이트 확인",
  "config.loading_schema": "스키마 로딩 중…",
  "config.advanced_warning": "",
  "config.raw_json5": "Raw JSON5",
  "config.all": "전체",
  "agents.title": "에이전트",
  "agents.configured": "설정됨",
  "agents.no_agents": "에이전트를 찾을 수 없습니다.",
  "agents.select": "에이전트 선택",
  "agents.select_sub": "에이전트를 선택하여 워크스페이스와 도구를 확인.",
  "agents.tab_overview": "개요",
  "agents.tab_files": "파일",
  "agents.tab_tools": "도구",
  "agents.tab_skills": "스킬",
  "agents.tab_channels": "채널",
  "agents.tab_cron": "예약 작업",
  "agents.tab_runtime": "런타임",
  "agents.runtime_title": "런타임 활동",
  "agents.runtime_sub": "실행 중인 서브에이전트, 큐 상태, 최근 완료.",
  "agents.runtime_active": "실행 중인 서브에이전트",
  "agents.runtime_no_active": "실행 중인 서브에이전트 없음.",
  "agents.runtime_elapsed": "경과",
  "agents.runtime_queue": "큐 상태",
  "agents.runtime_queue_unavailable": "큐 상태를 가져올 수 없음.",
  "agents.runtime_slots": "슬롯",
  "agents.runtime_recent": "최근 완료",
  "agents.runtime_recent_sub": "최근 30분 이내 완료된 서브에이전트.",
  "agents.runtime_no_recent": "최근 완료 없음.",
  "agents.runtime_col_label": "라벨",
  "agents.runtime_col_status": "상태",
  "agents.runtime_col_model": "모델",
  "agents.runtime_col_runtime": "실행 시간",
  "agents.runtime_watchdog": "워치독",
  "agents.runtime_watchdog_on": "실행 중",
  "agents.runtime_watchdog_off": "중지",
  "agents.runtime_scans": "스캔",
  "agents.runtime_aborted": "중단",
  "agents.runtime_registered": "등록됨",
  "agents.overview_sub": "워크스페이스 경로와 메타데이터.",
  "agents.workspace": "워크스페이스",
  "agents.primary_model": "기본 모델",
  "agents.identity_name": "아이덴티티 이름",
  "agents.default": "기본값",
  "agents.identity_emoji": "이모지",
  "agents.skills_filter": "스킬 필터",
  "agents.selected": "선택됨",
  "agents.all_skills": "모든 스킬",
  "agents.model_selection": "모델 선택",
  "agents.primary_default": "기본 모델",
  "agents.inherit_default": "기본값 상속",
  "agents.fallbacks": "대체 모델 (쉼표 구분)",
  "agents.fallbacks_placeholder": "provider/model, provider/model",
  "agents.reload_config": "설정 다시 로드",
  "agents.api_keys_title": "프로바이더 API 키",
  "agents.api_keys_sub": "LLM 프로바이더 API 키. 암호화되어 모든 에이전트에 공유됩니다.",
  "agents.api_key_set": "설정됨 (숨김)",
  "agents.api_key_placeholder": "API 키 입력...",
  "agents.catalog_title": "모델 카탈로그",
  "agents.catalog_sub": "기본 또는 대체 모델로 선택 가능한 모델.",
  "agents.catalog_model_id": "모델 ID",
  "agents.catalog_alias": "별칭",
  "agents.api_key_label": "API 키",
  "agents.catalog_actions": "작업",
  "agents.catalog_add": "모델 추가",
  "agents.catalog_add_placeholder": "provider/model-name",
  "agents.catalog_alias_placeholder": "약칭 (선택사항)",
  "agents.catalog_empty": "설정된 모델이 없습니다. 아래에서 추가하세요.",
  "agents.catalog_confirm_remove":
    "이 모델은 기본 또는 대체 모델로 사용 중입니다. 삭제하시겠습니까?",
  "agents.catalog_provider": "제공업체",
  "agents.catalog_model": "모델",
  "agents.catalog_no_providers": "사용 가능한 제공업체가 없습니다. API 키를 먼저 추가하세요.",
  "agents.catalog_select_provider": "제공업체 선택…",
  "agents.catalog_select_model": "모델 선택…",
  "agents.fallback_title": "대체 모델",
  "agents.fallback_sub": "기본 모델 실패 시 순서대로 시도됩니다.",
  "agents.fallback_add": "대체 모델 추가",
  "agents.fallback_empty": "대체 모델이 설정되지 않았습니다.",
  "agents.custom_env_title": "사용자 정의 환경 변수",
  "agents.custom_env_sub": "사용자 정의 프로바이더를 위한 추가 API 키 또는 환경 변수.",
  "agents.custom_env_add": "변수 추가",
  "agents.custom_env_name_placeholder": "VARIABLE_NAME",
  "agents.custom_env_value_placeholder": "값...",
  "sessions.title": "세션",
  "sessions.title_sub": "활성 세션 키와 세션별 재정의.",
  "sessions.active_within": "활성 기간 (분)",
  "sessions.limit": "제한",
  "sessions.include_global": "전역 포함",
  "sessions.include_unknown": "알 수 없는 항목 포함",
  "sessions.store": "저장소",
  "sessions.col_key": "키",
  "sessions.col_label": "라벨",
  "sessions.col_kind": "종류",
  "sessions.col_updated": "업데이트",
  "sessions.col_tokens": "토큰",
  "sessions.col_thinking": "사고",
  "sessions.col_verbose": "상세",
  "sessions.col_reasoning": "추론",
  "sessions.col_actions": "작업",
  "sessions.no_sessions": "세션을 찾을 수 없습니다.",
  "sessions.optional": "(선택사항)",
  "sessions.inherit": "상속",
  "sessions.task_log": "작업 로그",
  "sessions.no_messages": "이 세션에 메시지가 없습니다.",
  "sessions.messages_label": "개 메시지",
  "cron.title": "스케줄러",
  "cron.title_sub": "게이트웨이 스케줄러 상태.",
  "cron.enabled": "활성화",
  "cron.jobs": "작업",
  "cron.next_wake": "다음 실행",
  "cron.new_job": "새 작업",
  "cron.new_job_sub": "예약 실행이나 에이전트 실행을 생성.",
  "cron.name": "이름",
  "cron.description": "설명",
  "cron.agent_id": "에이전트 ID",
  "cron.schedule": "스케줄",
  "cron.every": "매",
  "cron.at": "시간",
  "cron.cron_expr": "Cron",
  "cron.session": "세션",
  "cron.wake_mode": "실행 모드",
  "cron.payload": "페이로드",
  "cron.main": "메인",
  "cron.isolated": "격리",
  "cron.now": "지금",
  "cron.next_heartbeat": "다음 하트비트",
  "cron.system_event": "시스템 이벤트",
  "cron.agent_turn": "에이전트 턴",
  "cron.delivery": "전달",
  "cron.timeout": "타임아웃 (초)",
  "cron.channel": "채널",
  "cron.to": "대상",
  "cron.announce": "요약 알림 (기본값)",
  "cron.none_internal": "없음 (내부)",
  "cron.add_job": "작업 추가",
  "cron.jobs_title": "작업",
  "cron.jobs_sub": "게이트웨이에 저장된 모든 작업.",
  "cron.no_jobs": "작업 없음.",
  "cron.run_history": "실행 기록",
  "cron.run_history_sub": "최근 실행:",
  "cron.select_job": "작업을 선택하여 실행 기록 확인.",
  "cron.no_runs": "실행 기록 없음.",
  "cron.run_at": "실행 시각",
  "cron.expression": "표현식",
  "cron.timezone": "시간대 (선택)",
  "cron.unit_minutes": "분",
  "cron.unit_hours": "시간",
  "cron.unit_days": "일",
  "cron.system": "시스템",
  "cron.prompt": "프롬프트",
  "cron.run_btn": "실행",
  "cron.history_btn": "기록",
  "cron.remove": "삭제",
  "cron.status": "상태",
  "cron.next": "다음",
  "cron.last": "이전",
  "cron.open_run_chat": "실행 채팅 열기",
  "skills.title": "스킬",
  "skills.title_sub": "내장, 관리, 워크스페이스 스킬.",
  "skills.filter": "필터",
  "skills.search": "스킬 검색",
  "skills.shown": "표시됨",
  "skills.no_skills": "스킬을 찾을 수 없습니다.",
  "skills.missing": "누락",
  "skills.reason": "이유",
  "skills.api_key": "API 키",
  "skills.save_key": "키 저장",
  "nodes.title": "노드",
  "nodes.title_sub": "페어링된 기기와 라이브 링크.",
  "nodes.no_nodes": "노드를 찾을 수 없습니다.",
  "nodes.devices": "기기",
  "nodes.devices_sub": "페어링 요청 + 역할 토큰.",
  "nodes.pending": "대기 중",
  "nodes.paired": "페어링됨",
  "nodes.no_paired": "페어링된 기기 없음.",
  "nodes.role": "역할",
  "nodes.roles": "역할",
  "nodes.scopes": "범위",
  "nodes.approve": "승인",
  "nodes.reject": "거부",
  "nodes.tokens_none": "토큰: 없음",
  "nodes.tokens": "토큰",
  "nodes.revoked": "폐기됨",
  "nodes.active_token": "활성",
  "nodes.rotate": "교체",
  "nodes.revoke": "폐기",
  "nodes.exec_binding": "실행 노드 바인딩",
  "nodes.exec_binding_sub": "exec host=node 사용 시 에이전트를 특정 노드에 고정.",
  "nodes.switch_form": "폼 모드로 전환하여 편집하세요.",
  "nodes.load_config": "설정 로드",
  "nodes.default_binding": "기본 바인딩",
  "nodes.default_binding_sub": "에이전트가 노드를 지정하지 않을 때 사용.",
  "nodes.any_node": "모든 노드",
  "nodes.no_exec_nodes": "system.run 가능한 노드 없음.",
  "nodes.no_agents_found": "에이전트 없음.",
  "nodes.default_agent": "기본 에이전트",
  "nodes.uses_default": "기본값 사용",
  "nodes.binding": "바인딩",
  "nodes.use_default": "기본값 사용",
  "nodes.status_paired": "페어링됨",
  "nodes.status_unpaired": "미페어링",
  "nodes.status_connected": "연결됨",
  "nodes.status_offline": "오프라인",
  "instances.title": "연결된 인스턴스",
  "instances.title_sub": "게이트웨이와 클라이언트의 프레즌스 비콘.",
  "instances.no_instances": "인스턴스 없음.",
  "instances.last_input": "마지막 입력",
  "instances.reason": "이유",
  "debug.snapshots": "스냅샷",
  "debug.snapshots_sub": "상태, 헬스, 하트비트 데이터.",
  "debug.status": "상태",
  "debug.health": "헬스",
  "debug.last_heartbeat": "마지막 하트비트",
  "debug.security_audit": "보안 감사",
  "debug.for_details": "에서 상세 확인.",
  "debug.manual_rpc": "수동 RPC",
  "debug.manual_rpc_sub": "JSON 파라미터로 게이트웨이 메서드 전송.",
  "debug.method": "메서드",
  "debug.params": "파라미터 (JSON)",
  "debug.call": "호출",
  "debug.models": "모델",
  "debug.models_sub": "models.list 카탈로그.",
  "debug.event_log": "이벤트 로그",
  "debug.event_log_sub": "최근 게이트웨이 이벤트.",
  "debug.no_events": "이벤트 없음.",
  "logs.title": "로그",
  "logs.title_sub": "게이트웨이 파일 로그 (JSONL).",
  "logs.export": "내보내기",
  "logs.filter": "필터",
  "logs.search": "로그 검색",
  "logs.auto_follow": "자동 추적",
  "logs.truncated": "로그가 잘렸습니다. 최신 부분을 표시 중.",
  "logs.no_entries": "로그 항목 없음.",
  "chat.compacting": "컨텍스트 압축 중...",
  "chat.compacted": "컨텍스트 압축 완료",
  "chat.attachment_preview": "첨부 파일 미리보기",
  "chat.remove_attachment": "첨부 파일 제거",
  "chat.placeholder": "메시지를 추가하거나 이미지를 붙여넣기...",
  "chat.placeholder_shift": "메시지 (↩ 전송, Shift+↩ 줄바꿈, 이미지 붙여넣기 가능)",
  "chat.not_connected": "게이트웨이에 연결하여 채팅 시작…",
  "chat.loading": "채팅 로딩 중…",
  "chat.exit_focus": "포커스 모드 종료",
  "chat.queued": "큐",
  "chat.remove_queued": "큐에서 메시지 제거",
  "chat.image_count": "이미지",
  "chat.new_messages": "새 메시지",
  "chat.message": "메시지",
  "chat.stop": "중지",
  "chat.new_session": "새 세션",
  "chat.queue_btn": "큐",
  "chat.queued_batch": "함께 보냄",
  "chat.send": "전송",
  "chat.history_hidden": "개 메시지 숨겨짐",
  "chat.compaction_divider": "압축",
  "app.brand": "NABE",
  "app.subtitle": "게이트웨이 대시보드",
  "app.expand_sidebar": "사이드바 펼치기",
  "app.collapse_sidebar": "사이드바 접기",
  "app.resources": "리소스",
  "app.docs": "문서",
  "app.refresh_chat": "채팅 데이터 새로고침",
  "app.disabled_onboarding": "온보딩 중 비활성화",
  "app.toggle_thinking": "AI 사고 과정 표시 전환",
  "app.toggle_focus": "포커스 모드 전환 (사이드바 숨기기)",
  "app.theme_system": "시스템",
  "app.theme_light": "라이트",
  "app.theme_dark": "다크",
  "app.theme": "테마",
  "common.loading": "로딩 중...",
  "common.error": "오류",
  "common.cancel": "취소",
  "common.confirm": "확인",
  "common.yes": "예",
  "common.no": "아니오",
  "common.na": "없음",
  "common.save": "저장",
  "common.saving": "저장 중...",
  "common.refresh": "새로고침",
  "common.refreshing": "갱신 중...",
  "common.delete": "삭제",
  "common.enable": "활성화",
  "common.disable": "비활성화",
  "common.enabled": "활성화됨",
  "common.disabled": "비활성화됨",
  "common.language": "언어",
  "common.what_is_this": "이것은 무엇인가요?",
  "common.learn_more": "자세히 보기",
  "common.step": "단계",
  "common.docs": "문서",
  "common.resources": "리소스",
  "common.agent": "에이전트",
  // Quick Settings
  "qs.title": "빠른 설정",
  "qs.subtitle": "자주 사용하는 설정입니다. 모든 옵션은 사이드바에서 선택하세요.",
  "qs.ai": "AI 어시스턴트",
  "qs.ai.model": "AI 모델",
  "qs.ai.model_help": "어시스턴트 응답에 사용되는 AI 모델",
  "qs.ai.name": "어시스턴트 이름",
  "qs.ai.name_help": "어시스턴트가 자기소개할 때 사용하는 이름",
  "qs.ai.workspace": "작업 폴더",
  "qs.ai.workspace_help": "파일과 프로젝트가 저장되는 폴더",
  "qs.conn": "연결",
  "qs.conn.port": "포트 번호",
  "qs.conn.port_help": "게이트웨이에 연결할 네트워크 포트",
  "qs.conn.bind": "연결 허용 대상",
  "qs.conn.bind_help": "게이트웨이에 접근할 수 있는 장치를 제어합니다",
  "qs.conn.bind.loopback": "이 컴퓨터만",
  "qs.conn.bind.lan": "같은 네트워크의 장치",
  "qs.conn.bind.tailnet": "인터넷 (Tailscale 경유)",
  "qs.conn.bind.auto": "자동",
  "qs.conn.auth": "비밀번호 보호",
  "qs.conn.auth_help": "연결 시 비밀번호 필요",
  "qs.conn.password": "비밀번호",
  "qs.apps": "메시징 앱",
  "qs.apps.subtitle": "메시징 플랫폼에 어시스턴트 연결",
  "qs.apps.configure": "설정",
  "qs.updates": "업데이트",
  "qs.updates.channel": "릴리스 채널",
  "qs.updates.channel_help": "새 버전을 받는 빈도",
  "qs.updates.auto": "자동 확인",
  "qs.updates.auto_help": "게이트웨이 시작 시 업데이트 확인",
  "qs.log": "로그",
  "qs.log.level": "상세 수준",
  "qs.log.level_help": "로그에 기록되는 상세 정보의 양",
  "qs.all_settings": "모든 설정 보기",
};

const zh: TranslationKeys = {
  "nav.chat": "聊天",
  "nav.dashboard": "仪表盘",
  "nav.automation": "自动化",
  "nav.system": "系统",
  "nav.simple.chat": "聊天",
  "nav.simple.status": "状态",
  "nav.simple.settings": "设置",
  "nav.simple.tools": "工具",
  "nav.simple.help": "帮助",
  "nav.mode.simple": "简单",
  "nav.mode.advanced": "高级",
  "nav.mode.toggle_label": "导航模式",
  "tab.chat": "聊天",
  "tab.overview": "首页",
  "tab.dashboard": "仪表盘",
  "tab.channels": "连接",
  "tab.instances": "设备",
  "tab.sessions": "对话",
  "tab.usage": "用量",
  "tab.agents": "智能体",
  "tab.skills": "技能",
  "tab.cron": "定时任务",
  "tab.terminal": "终端",
  "tab.files": "文件",
  "tab.nodes": "计算机",
  "tab.config": "设置",
  "tab.debug": "高级",
  "tab.heartbeat": "心跳",
  "sub.heartbeat": "心跳分析与行为监控",
  "hb.title": "心跳分析",
  "hb.subtitle": "实时心跳行为监控",
  "hb.timeline": "时间线",
  "hb.event_log": "事件日志",
  "hb.no_events": "暂无事件",
  "hb.refresh": "刷新",
  "hb.pause": "暂停",
  "hb.resume": "继续",
  "hb.loading": "加载中...",
  "hb.new": "NEW",
  "hb.stat_total": "总运行",
  "hb.stat_ok_rate": "OK率",
  "hb.stat_alert_rate": "告警率",
  "hb.stat_avg_duration": "平均耗时",
  "hb.stat_skipped": "跳过",
  "hb.stat_failed": "失败",
  "hb.stat_duplicates": "重复检测",
  "hb.stat_api_saved": "API节省",
  "hb.status_sent": "已发送",
  "hb.status_ok-empty": "OK (空)",
  "hb.status_ok-token": "OK (令牌)",
  "hb.status_skipped": "已跳过",
  "hb.status_failed": "失败",
  "tab.logs": "活动日志",
  "tab.learning": "AI学习",
  "tab.mind": "思维",
  "tab.activity-log": "活动日志 (DB)",
  "sub.chat": "与您的AI助手直接对话。",
  "sub.overview": "一目了然地查看所有运行状态。",
  "sub.dashboard": "实时监控节点、智能体和任务。",
  "sub.channels": "连接WhatsApp、Telegram、Discord等消息应用。",
  "sub.instances": "查看当前连接的设备和应用。",
  "sub.sessions": "查看和管理进行中的对话。",
  "sub.agents": "您的AI助手及其配置。",
  "sub.skills": "启用或禁用AI的功能。",
  "sub.cron": "设置自动执行的定时任务。",
  "sub.terminal": "无需SSH即可在服务器上运行命令。",
  "sub.files": "浏览和编辑服务器上的文件。",
  "sub.nodes": "管理已连接的计算机和权限。",
  "sub.config": "在一处调整所有系统设置。",
  "sub.debug": "故障排除的技术详情。",
  "sub.logs": "查看最近发生的事件。",
  "sub.learning": "查看AI记忆、情感和知识。",
  "sub.mind": "认知架构监控和分析。",
  "mind.soul": "AI灵魂",
  "mind.soul.emotions": "情感状态",
  "mind.soul.lifecycle": "生命周期",
  "mind.soul.personality": "个性",
  "mind.soul.values": "核心价值观",
  "mind.disconnected": "连接网关以查看认知状态",
  "mind.plugin_unavailable": "Cognitive Mind插件未激活",
  "mind.plugin_unavailable_hint": "启用autonomous-mind扩展以查看数据",
  "mind.loading": "正在加载认知状态...",
  "mind.retry": "重试",
  "mind.sec_overview": "概览",
  "mind.sec_soul": "灵魂",
  "mind.sec_depth": "深度",
  "mind.sec_thoughts": "思维",
  "mind.sec_workspace": "工作区",
  "mind.sec_identity": "身份",
  "mind.sec_analytics": "分析",
  "mind.emotion_love": "爱",
  "mind.emotion_anger": "怒",
  "mind.emotion_sadness": "悲",
  "mind.emotion_joy": "喜",
  "mind.emotion_fear": "惧",
  "mind.emotion_trust": "信",
  "mind.emotion_greed": "贪",
  "mind.emotion_delusion": "迷",
  "mind.mood_valence": "情绪方向",
  "mind.mood_balanced": "平衡",
  "mind.mood_unknown": "未知",
  "mind.loyalty": "忠诚",
  "mind.stage_newborn": "新生",
  "mind.stage_child": "幼年",
  "mind.stage_adolescent": "少年",
  "mind.stage_adult": "成年",
  "mind.stage_elder": "老年",
  "mind.stage_transcendent": "超越",
  "mind.meter_health": "健康",
  "mind.meter_energy": "能量",
  "mind.meter_wisdom": "智慧",
  "mind.personality_developing": "个性正通过对话发展中...",
  "mind.overview_title": "认知概览",
  "mind.card_depth": "认知深度",
  "mind.card_mirror": "MIRROR线程",
  "mind.card_workspace": "工作区",
  "mind.card_identity": "身份",
  "mind.card_goals": "目标",
  "mind.card_thoughts": "思维",
  "mind.inactive": "未激活",
  "mind.no_data": "无数据",
  "mind.active": "活跃",
  "mind.idle": "空闲",
  "mind.waiting": "等待输入",
  "mind.entries": "条目",
  "mind.traits": "特质",
  "mind.values_count": "价值观",
  "mind.active_count": "活跃",
  "mind.recent_count": "最近",
  "mind.global_memory": "全局工作记忆",
  "mind.inner_monologue": "内心独白流",
  "mind.no_goals": "无活跃目标",
  "mind.unavailable": "不可用",
  "mind.recent_thoughts": "最近的思考",
  "mind.no_thoughts": "尚无思考。心境平静。",
  "mind.depth_title": "认知深度分析",
  "mind.depth_current": "当前深度",
  "mind.depth_instinctive": "本能",
  "mind.depth_associative": "联想",
  "mind.depth_analytical": "分析",
  "mind.depth_strategic": "战略",
  "mind.confidence": "置信度",
  "mind.depth_distribution": "深度分布",
  "mind.recent_classifications": "最近分类",
  "mind.col_depth": "深度",
  "mind.col_confidence": "置信度",
  "mind.col_phases": "阶段",
  "mind.col_time": "时间",
  "mind.loading_depth": "正在加载深度数据...",
  "mind.thoughts_title": "思维流",
  "mind.no_thoughts_yet": "尚无记录的思考。",
  "mind.workspace_title": "全局工作区",
  "mind.ws_entries": "条目",
  "mind.ws_active_entries": "活跃工作区条目",
  "mind.ws_attention": "注意力",
  "mind.ws_top5": "前5",
  "mind.ws_salience": "按重要性加权选择",
  "mind.ws_description": "全局工作区保存AI当前的工作记忆。按重要性和新鲜度排名。",
  "mind.identity_title": "身份",
  "mind.id_continuity": "连续性",
  "mind.id_consistent": "身份一致",
  "mind.id_drift": "检测到身份漂移",
  "mind.id_traits": "自我模型特质",
  "mind.id_values": "价值观",
  "mind.id_no_traits": "尚未发展出特质。",
  "mind.id_no_values": "尚未建立价值观。",
  "mind.id_loading": "正在加载身份数据...",
  "mind.analytics_title": "分析",
  "mind.an_classifications": "分类数",
  "mind.an_override_rate": "覆盖率",
  "mind.an_loop_perf": "循环性能",
  "mind.an_avg": "平均",
  "mind.an_runs_max": "次",
  "mind.an_dominant": "主要深度",
  "mind.an_avg_confidence": "平均置信度",
  "mind.an_phase_failures": "阶段失败",
  "mind.an_failures": "失败",
  "mind.an_loading": "正在加载分析数据...",
  "mind.time_just_now": "刚刚",
  "mind.time_m_ago": "分钟前",
  "mind.time_h_ago": "小时前",
  "mind.time_d_ago": "天前",
  "sub.activity-log": "MongoDB存储的完整活动日志（保留7天）。",
  "tab.spawn-monitor": "Spawn监控",
  "sub.spawn-monitor": "监控编排器调度和子代理。",
  "spawn_monitor.title": "Spawn监控",
  "spawn_monitor.sub": "编排器/spawn模式的实时监控。",
  "spawn_monitor.active_now": "活跃",
  "spawn_monitor.total_recent": "最近",
  "spawn_monitor.avg_runtime": "平均耗时",
  "spawn_monitor.success_rate": "成功率",
  "spawn_monitor.active_title": "活跃的Spawn",
  "spawn_monitor.no_active": "没有活跃的子代理。",
  "spawn_monitor.elapsed": "已用时",
  "spawn_monitor.recent_title": "最近完成",
  "spawn_monitor.recent_sub": "已完成的Spawn调度（最近30分钟）。",
  "spawn_monitor.no_recent": "没有最近的Spawn完成。",
  "spawn_monitor.col_message": "消息",
  "spawn_monitor.col_status": "状态",
  "spawn_monitor.col_model": "模型",
  "spawn_monitor.col_runtime": "运行时间",
  "spawn_monitor.col_time": "时间",
  "spawn_monitor.events_title": "实时事件",
  "spawn_monitor.events_sub": "实时Spawn生命周期事件。",
  "spawn_monitor.no_events": "暂无Spawn事件。启用Spawn模式后会显示。",
  "spawn_monitor.clear_events": "清除",
  "spawn_monitor.auto_refresh": "自动刷新",
  "dashboard.nodes": "节点",
  "dashboard.nodes_online": "在线",
  "dashboard.agents": "智能体",
  "dashboard.agents_registered": "已注册",
  "dashboard.devices": "设备",
  "dashboard.devices_connected": "已连接",
  "dashboard.realtime": "实时",
  "dashboard.refresh": "刷新",
  "dashboard.connected_nodes": "已连接的节点",
  "dashboard.disconnected_nodes": "已断开的节点",
  "dashboard.loading": "正在加载节点...",
  "dashboard.no_nodes": "没有已连接的节点。",
  "dashboard.commands": "命令",
  "dashboard.node_capabilities": "节点功能",
  "dashboard.node_capabilities_sub": "每个节点可用的命令和功能。",
  "dashboard.node": "节点",
  "dashboard.status": "状态",
  "dashboard.capabilities": "命令",
  "dashboard.uptime": "连接时间",
  "dashboard.agents_list": "智能体",
  "dashboard.queue_title": "命令队列",
  "dashboard.queue_sub": "所有通道的活动和排队任务",
  "dashboard.queue_active": "活动",
  "dashboard.queue_queued": "排队",
  "dashboard.queue_lane": "通道",
  "dashboard.queue_concurrency": "最大并发",
  "dashboard.queue_idle": "所有队列空闲。",
  "dashboard.queue_idle_lanes": "空闲通道",
  "learning.overview_title": "AI学习概览",
  "learning.memory_entries": "记忆条目",
  "learning.emotions_active": "活跃情感",
  "learning.user_messages": "已分析消息",
  "learning.modules": "学习模块",
  "learning.mod_memory": "记忆",
  "learning.mod_memory_desc": "用于持久知识的核心记忆文件",
  "learning.mod_soul": "人格",
  "learning.mod_soul_desc": "定义人格和声音的灵魂文件",
  "learning.mod_emotions": "情感核心",
  "learning.mod_emotions_desc": "情感追踪和情绪分析",
  "learning.mod_user": "用户建模",
  "learning.mod_user_desc": "自适应用户配置文件和偏好",
  "learning.mod_tiered": "分层记忆",
  "learning.mod_tiered_desc": "带遗忘曲线的短期和长期记忆",
  "learning.active": "活跃",
  "learning.inactive": "未激活",
  "learning.generate": "生成",
  "learning.regenerate": "重新生成",
  "learning.generating": "生成中...",
  "learning.init_all": "全部生成",
  "learning.generate_memory": "生成记忆文件",
  "learning.generate_soul": "生成人格",
  "learning.generate_emotions": "初始化情感",
  "learning.generate_user": "生成用户档案",
  "learning.generate_knowledge": "整理知识",
  "learning.current_mood": "当前心情",
  "learning.mood_valence": "情感效价",
  "learning.mood_stability": "稳定性",
  "learning.mood_energy": "能量",
  "learning.mood_warmth": "温暖",
  "learning.memory_title": "记忆",
  "learning.tiered_memory": "分层记忆系统",
  "learning.short_term": "短期",
  "learning.long_term": "长期",
  "learning.entities": "实体",
  "learning.entity_graph": "实体图谱",
  "learning.recent_memories": "最近记忆",
  "learning.memory_file": "MEMORY.md",
  "learning.no_memory": "未找到记忆数据。",
  "learning.memory_history": "记忆历史",
  "learning.load_history": "加载历史",
  "learning.no_history": '点击"加载历史"查看过去的记忆文件。',
  "learning.emotions_title": "情感状态",
  "learning.emotion_vector": "情感向量",
  "learning.vec_valence": "效价",
  "learning.vec_arousal": "唤醒度",
  "learning.vec_dominance": "支配性",
  "learning.vec_attachment": "依恋",
  "learning.vec_desire": "欲望",
  "learning.vec_confusion": "困惑",
  "learning.active_emotions": "活跃情感",
  "learning.loyalty": "忠诚与纽带",
  "learning.loyalty_level": "忠诚度",
  "learning.bond_strength": "纽带强度",
  "learning.protective": "保护本能",
  "learning.devotion_style": "奉献风格",
  "learning.emotional_memories": "情感记忆",
  "learning.no_emotions": "情感扩展未安装或无数据。",
  "learning.personality_title": "人格与身份",
  "learning.soul_file": "SOUL.md — 人格定义",
  "learning.identity_file": "IDENTITY.md — 身份配置",
  "learning.no_soul": "未找到SOUL.md。",
  "learning.user_title": "用户理解",
  "learning.user_profile": "学习到的用户档案",
  "learning.comm_style": "沟通风格",
  "learning.expertise": "专业水平",
  "learning.response_pref": "回应偏好",
  "learning.mental_state": "精神状态",
  "learning.interests": "主要兴趣",
  "learning.interaction_stats": "交互统计",
  "learning.stat_messages": "消息",
  "learning.stat_questions": "问题",
  "learning.stat_commands": "命令",
  "learning.stat_code": "代码片段",
  "learning.user_file": "USER.md",
  "learning.no_user": "未找到用户数据。",
  "learning.knowledge_title": "长期知识",
  "learning.long_term_knowledge": "整合的知识",
  "learning.no_knowledge": "暂无长期知识。",
  "learning.disconnected": "连接网关以查看AI学习数据。",
  "learning.loading": "正在加载AI学习数据...",
  "learning.refresh": "刷新",
  "learning.sec_overview": "概览",
  "learning.sec_memory": "记忆",
  "learning.sec_emotions": "情感",
  "learning.sec_personality": "人格",
  "learning.sec_user": "用户模型",
  "learning.sec_knowledge": "知识",
  "ks.title": "知识源",
  "ks.loading": "加载中...",
  "ks.refresh": "刷新",
  "ks.add_source": "添加源",
  "ks.edit_source": "编辑源",
  "ks.empty_title": "暂无知识源",
  "ks.empty_description": "描述一个数据源，AI将自动分析并收集数据。",
  "ks.tab_sources": "数据源",
  "ks.tab_collected": "已收集",
  "ks.status_idle": "空闲",
  "ks.status_collecting": "收集中",
  "ks.status_success": "活跃",
  "ks.status_error": "错误",
  "ks.disabled": "已禁用",
  "ks.schedule": "计划",
  "ks.last_collected": "上次收集",
  "ks.docs_ingested": "文档",
  "ks.runs": "运行次数",
  "ks.collect_now": "立即收集",
  "ks.edit": "编辑",
  "ks.enable": "启用",
  "ks.disable": "禁用",
  "ks.delete": "删除",
  "ks.delete_confirm": "确定要删除此知识源吗？",
  "ks.field_name": "源名称",
  "ks.field_name_placeholder": "例如：HR后台、销售API、财务门户",
  "ks.field_description": "这是什么数据源？",
  "ks.field_description_placeholder":
    "例如：每小时从goldprice.org获取金价\n例如：登录hr.company.com的HR系统获取新员工名单\n例如：从https://api.example.com/v1/sales获取销售数据",
  "ks.field_description_hint": "描述数据源 — 是什么、URL、要收集什么。AI会自动分析访问方式。",
  "ks.field_credentials": "认证信息（可选）",
  "ks.field_credentials_hint": "如需登录或API密钥，请在此输入。数据将加密存储。",
  "ks.field_username": "用户名",
  "ks.field_password": "密码",
  "ks.field_api_key": "API密钥",
  "ks.field_interval": "收集频率",
  "ks.minutes": "分钟",
  "ks.cancel": "取消",
  "ks.save": "保存",
  "ks.create": "创建",
  "ks.just_now": "刚刚",
  "login.title": "Nabe",
  "login.subtitle": "请输入密码以继续",
  "login.password": "密码",
  "login.signin": "登录",
  "login.hint": "按回车键登录",
  "topbar.connected": "已连接",
  "topbar.offline": "离线",
  "topbar.signout": "退出",
  "overview.connection": "连接",
  "overview.online": "在线",
  "overview.offline": "离线",
  "overview.running_for": "运行时间",
  "overview.not_connected": "未连接到网关",
  "overview.channels": "频道",
  "overview.active_sessions": "活跃会话",
  "overview.system_info": "系统信息",
  "overview.getting_started": "开始使用",
  "overview.getting_started_sub": "以下是一些帮助您充分利用Nabe的提示。",
  "overview.tip_connect": "连接应用",
  "overview.tip_connect_desc": "前往连接页面关联WhatsApp、Telegram、Discord等。",
  "overview.tip_chat": "开始对话",
  "overview.tip_chat_desc": "使用聊天标签直接与AI助手对话。",
  "overview.tip_settings": "自定义设置",
  "overview.tip_settings_desc": "前往设置页面按您的喜好进行调整。",
  "terminal.quick_commands": "快捷命令",
  "terminal.quick_sub": "一键运行常用任务。",
  "terminal.run_command": "运行命令",
  "terminal.run_sub": "输入命令后按回车或点击运行。",
  "terminal.run": "运行",
  "terminal.running": "运行中...",
  "terminal.output": "输出",
  "terminal.clear": "清空",
  "terminal.no_commands": "还没有命令",
  "terminal.no_commands_sub": "使用上方的快捷命令或输入自己的命令。",
  "terminal.not_connected": "未连接到网关。",
  "terminal.welcome": "欢迎使用 Nabe Gateway 终端",
  "terminal.welcome_hint": "在下方输入命令或使用上方的快捷按钮。",
  "terminal.status": "状态",
  "terminal.config": "配置",
  "terminal.disk": "磁盘",
  "terminal.memory": "内存",
  "terminal.processes": "进程",
  "terminal.network": "网络",
  "terminal.uptime": "运行时间",
  "terminal.logs_btn": "日志",
  "files.quick_access": "快速访问",
  "files.quick_sub": "跳转到服务器上的常用目录。",
  "files.home": "主目录",
  "files.nabe_config": "Nabe配置",
  "files.app_files": "应用文件",
  "files.extensions": "扩展",
  "files.temp": "临时",
  "files.root": "根目录",
  "files.go_up": "返回上级",
  "files.refresh": "刷新",
  "files.show_hidden": "显示隐藏文件",
  "files.items": "个项目",
  "files.name": "名称",
  "files.size": "大小",
  "files.modified": "修改时间",
  "files.empty": "此文件夹为空。",
  "files.loading": "加载中...",
  "files.opening": "正在打开文件...",
  "files.save": "保存",
  "files.saving": "保存中...",
  "files.close": "关闭",
  "files.unsaved": "未保存的更改",
  "files.delete": "删除",
  "files.delete_confirm": "删除",
  "files.not_connected": "未连接到网关。",
  "config.save": "保存",
  "config.saving": "保存中...",
  "config.undo": "全部撤销",
  "config.apply": "保存并重启",
  "config.applying": "应用中...",
  "config.update": "检查更新",
  "config.updating": "检查中...",
  "config.visual": "可视化",
  "config.advanced": "高级",
  "config.search": "搜索设置...",
  "hint.gateway": "连接AI与消息应用的中央服务器。如同中转大脑，在你和AI之间传递消息。",
  "hint.websocket": "浏览器与服务器之间的实时连接。无需刷新页面即可即时更新数据。",
  "hint.session_key": "每个对话的唯一名称。像聊天室名一样，不同的key将对话分开。",
  "hint.thinking_level": "AI回答前思考的深度。高=慢但更深思熟虑，低=快但答案更简单。",
  "hint.block_streaming": "AI返回响应的方式。不等待完整答案，准备好的部分立即发送。",
  "hint.block_streaming_break":
    "何时发送响应的各部分。'text_end'按段落发送，'message_end'等完整回答。",
  "hint.block_streaming_chunk": "将长响应分成小块的方式。对有字数限制的消息应用很有用。",
  "hint.exec_approvals": "AI可以在计算机上执行哪些命令的安全规则。防止危险操作。",
  "hint.bindings": "服务器监听的网络地址。'loopback'=仅本机，'lan'=网络上的任何设备。",
  "hint.auto_follow": "新消息到达时自动继续对话，无需手动点击。",
  "hint.agent": "拥有独特个性、工具和工作空间的AI助手。你可以有多个代理用于不同任务。",
  "hint.skill": "给AI的特定能力——如网页搜索、代码执行、图像分析。根据需要启用或禁用。",
  "hint.cron": "在设定时间自动执行的任务。像AI的闹钟——按计划唤醒并工作。",
  "hint.node": "与网关配对的计算机。可以执行命令、访问文件或为AI提供服务。",
  "hint.instance": "当前连接到网关的设备或应用。显示当前在线的内容。",
  "hint.channel": "连接到AI的消息平台——如Telegram、Discord、WhatsApp、Slack。",
  "hint.tls": "安全连接的加密（HTTPS）。如同门锁——防止他人读取你的数据。",
  "hint.tailscale": "在设备之间创建私有网络的VPN服务。使远程访问简单且安全。",
  "hint.rate_limit": "限制一段时间内的登录尝试次数。防止黑客猜测密码。",
  "hint.trusted_proxy": "位于网关前的反向代理（如Nginx、Cloudflare）处理认证。",
  "hint.sandbox": "AI安全执行命令的隔离容器。如同游乐场——做什么都不会影响真实系统。",
  "hint.tool_policy": "AI可以使用哪些工具的规则。出于安全考虑，可以允许或阻止特定功能。",
  "hint.subagent": "主代理为并行工作生成的辅助AI。如同将子任务委派给助手。",
  "hint.memory_search": "AI的长期记忆。从对话中保存重要事实，之后可通过AI搜索找到。",
  "hint.tts": "文字转语音：将文字转为语音。AI可以用'声音'与你对话。",
  "hint.vision": "AI理解和描述图像的能力。发送照片，AI可以告诉你图中有什么。",
  "hint.ollama": "在本地运行AI模型的工具。无需云端——数据留在你身边。",
  "hint.api_key": "AI连接服务的密钥（如OpenAI、Google）。请保密——绝不要公开分享。",
  "hint.model": "正在使用的AI大脑。不同模型有不同优势——有的快，有的聪明，有的能理解图像。",
  "hint.token": "用于网关认证的密钥。类似密码但自动生成。",
  "hint.password_auth": "简单的密码认证。知道密码的人都可以访问网关。",
  "hint.webhook": "事件发生时接收通知的URL。如同门铃——有消息时会响。",
  "hint.presence": "显示设备在线的信号。如同聊天应用中的绿点。",
  "hint.compaction": "清理旧对话历史以节省空间。保留重要部分，删除不需要的。",
  "hint.funnel": "Tailscale Funnel将网关暴露到公共互联网。知道URL的人都可以访问。",
  "hint.basepath": "Web界面的URL前缀。在如 /nabe 的子路径下通过反向代理运行时使用。",
  "guide.connect_app_title": "如何连接消息应用",
  "guide.connect_app_steps":
    "1. 前往连接页面\n2. 点击要连接的应用（如Telegram）\n3. 按照该应用的设置说明操作\n4. 输入所需的令牌或凭据\n5. 点击保存——连接将自动启动",
  "guide.setup_agent_title": "如何设置AI智能体",
  "guide.setup_agent_steps":
    "1. 前往智能体页面\n2. 默认的'main'智能体已创建\n3. 点击编辑其个性（IDENTITY.md）\n4. 选择要使用的AI模型\n5. 启用所需的工具/技能",
  "guide.add_skill_title": "如何启用技能",
  "guide.add_skill_steps":
    "1. 前往技能页面\n2. 浏览可用技能\n3. 切换开关以启用\n4. 部分技能需要API密钥——按提示输入\n5. AI现在可以在对话中使用此技能",
  "guide.schedule_task_title": "如何设置定时任务",
  "guide.schedule_task_steps":
    "1. 前往定时任务\n2. 点击'添加任务'\n3. 设置cron表达式（如'0 9 * * *' = 每天上午9点）\n4. 写下AI应该做什么\n5. 保存——将在设定时间自动执行",
  "overview.sessions_now": "当前打开的聊天会话",
  "overview.scheduled_tasks": "定时任务",
  "overview.scheduled_none": "无",
  "overview.scheduled_active": "运行中",
  "overview.scheduled_paused": "已暂停",
  "overview.attention": "需要注意",
  "overview.auth_required": "此网关需要认证。添加令牌或密码，然后点击连接。",
  "overview.auth_hint_dashboard": "打开控制UI",
  "overview.auth_hint_token": "设置令牌",
  "overview.auth_failed": "认证失败。在设置中更新令牌或密码，然后重新连接。",
  "overview.docs_auth": "文档：认证设置",
  "overview.insecure_context":
    "此页面是HTTP，浏览器会阻止设备识别。使用HTTPS (Tailscale Serve) 或打开",
  "overview.insecure_stay": "如果必须使用HTTP，请设置",
  "overview.docs_tailscale": "文档：Tailscale Serve",
  "overview.docs_insecure": "文档：非安全HTTP",
  "overview.connection_settings": "连接设置",
  "overview.connection_sub": "此仪表盘如何连接到网关。",
  "overview.server_address": "服务器地址",
  "overview.password": "密码",
  "overview.enter_password": "输入密码",
  "overview.session_name": "会话名称",
  "overview.reconnect": "重新连接",
  "overview.connect_btn": "连接",
  "overview.refresh_data": "刷新数据",
  "overview.system_sub": "网关当前状态。",
  "overview.running_time": "运行时间",
  "overview.connected_devices": "已连接设备",
  "overview.last_refresh": "上次刷新",
  "overview.never": "从未",
  "overview.all_good": "一切运行正常。前往连接页面设置消息应用。",
  "channels.details": "连接详情",
  "channels.details_sub": "已连接消息应用的详细状态。",
  "channels.never_checked": "从未检查",
  "channels.no_snapshot": "暂无快照。",
  "channels.status_sub": "此连接的状态和设置。",
  "channels.configured": "已配置",
  "channels.running": "运行中",
  "channels.connected": "已连接",
  "channels.last_inbound": "最后接收",
  "channels.form_unavailable": "设置表单不可用。",
  "channels.form_unavailable_sub": "无法加载配置表单。您可以在设置标签中手动编辑。",
  "channels.plugin_needed": "此连接的设置表单暂不可用。",
  "channels.plugin_needed_sub": "可能需要先启用插件。请检查设置标签或重启网关。",
  "channels.loading_schema": "加载配置模式…",
  "channels.save_changes": "保存",
  "channels.undo_changes": "撤销",
  "channels.saving": "保存中...",

  "picker.title": "选择消息应用",
  "picker.subtitle": "选择要连接AI的应用。",
  "picker.add": "添加",
  "picker.add_channel": "添加频道",
  "picker.all_added": "所有可用频道已添加。",
  "picker.already_added": "已添加",
  "picker.empty_state": '尚未配置频道。点击"添加频道"开始。',
  "picker.easy": "简单",
  "picker.medium": "中等",
  "picker.hard": "高级",
  "picker.enabled": "启用",
  "guide.title": "设置指南 —",
  "guide.full_docs": "阅读完整文档",

  "config.general": "常规",
  "config.updates": "更新",
  "config.ai_agents": "AI智能体",
  "config.security": "安全",
  "config.messaging_apps": "消息应用",
  "config.messages": "消息",
  "config.commands": "命令",
  "config.hooks": "自动化钩子",
  "config.skills_section": "技能",
  "config.tools": "工具",
  "config.server": "服务器",
  "config.setup_guide": "设置向导",
  "config.settings": "设置",
  "config.all_settings": "所有设置",
  "config.unsaved": "未保存的更改",
  "config.no_changes": "无更改",
  "config.discard_title": "放弃更改并从文件重新加载",
  "config.save_title": "保存到配置文件",
  "config.restart_title": "保存并重启相关服务",
  "config.update_title": "检查软件更新",
  "config.loading_schema": "加载模式…",
  "config.advanced_warning": "",
  "config.raw_json5": "Raw JSON5",
  "config.all": "全部",
  "agents.title": "智能体",
  "agents.configured": "已配置",
  "agents.no_agents": "未找到智能体。",
  "agents.select": "选择智能体",
  "agents.select_sub": "选择一个智能体以查看其工作区和工具。",
  "agents.tab_overview": "概览",
  "agents.tab_files": "文件",
  "agents.tab_tools": "工具",
  "agents.tab_skills": "技能",
  "agents.tab_channels": "频道",
  "agents.tab_cron": "定时任务",
  "agents.tab_runtime": "运行时",
  "agents.runtime_title": "运行时活动",
  "agents.runtime_sub": "正在运行的子代理、队列状态和最近完成。",
  "agents.runtime_active": "正在运行的子代理",
  "agents.runtime_no_active": "没有正在运行的子代理。",
  "agents.runtime_elapsed": "已用时间",
  "agents.runtime_queue": "队列状态",
  "agents.runtime_queue_unavailable": "无法获取队列状态。",
  "agents.runtime_slots": "槽位",
  "agents.runtime_recent": "最近完成",
  "agents.runtime_recent_sub": "过去30分钟内完成的子代理。",
  "agents.runtime_no_recent": "没有最近完成。",
  "agents.runtime_col_label": "标签",
  "agents.runtime_col_status": "状态",
  "agents.runtime_col_model": "模型",
  "agents.runtime_col_runtime": "运行时间",
  "agents.runtime_watchdog": "看门狗",
  "agents.runtime_watchdog_on": "运行中",
  "agents.runtime_watchdog_off": "已停止",
  "agents.runtime_scans": "扫描",
  "agents.runtime_aborted": "已中断",
  "agents.runtime_registered": "已注册",
  "agents.overview_sub": "工作区路径和身份元数据。",
  "agents.workspace": "工作区",
  "agents.primary_model": "主模型",
  "agents.identity_name": "身份名称",
  "agents.default": "默认",
  "agents.identity_emoji": "表情符号",
  "agents.skills_filter": "技能筛选",
  "agents.selected": "已选",
  "agents.all_skills": "所有技能",
  "agents.model_selection": "模型选择",
  "agents.primary_default": "主模型",
  "agents.inherit_default": "继承默认值",
  "agents.fallbacks": "备用模型（逗号分隔）",
  "agents.fallbacks_placeholder": "provider/model, provider/model",
  "agents.reload_config": "重新加载配置",
  "agents.api_keys_title": "提供商 API 密钥",
  "agents.api_keys_sub": "LLM 提供商的 API 密钥。加密存储，所有智能体共享。",
  "agents.api_key_set": "已设置（已隐藏）",
  "agents.api_key_placeholder": "输入 API 密钥...",
  "agents.catalog_title": "模型目录",
  "agents.catalog_sub": "可选为主模型或备用模型的模型列表。",
  "agents.catalog_model_id": "模型ID",
  "agents.catalog_alias": "别名",
  "agents.api_key_label": "API密钥",
  "agents.catalog_actions": "操作",
  "agents.catalog_add": "添加模型",
  "agents.catalog_add_placeholder": "provider/model-name",
  "agents.catalog_alias_placeholder": "简称（可选）",
  "agents.catalog_empty": "暂无模型配置。请在下方添加。",
  "agents.catalog_confirm_remove": "此模型正在用作主模型或备用模型。确定删除？",
  "agents.catalog_provider": "服务商",
  "agents.catalog_model": "模型",
  "agents.catalog_no_providers": "没有可用的服务商。请先添加 API 密钥。",
  "agents.catalog_select_provider": "选择服务商…",
  "agents.catalog_select_model": "选择模型…",
  "agents.fallback_title": "备用模型",
  "agents.fallback_sub": "主模型失败时按顺序尝试。",
  "agents.fallback_add": "添加备用模型",
  "agents.fallback_empty": "暂无备用模型。",
  "agents.custom_env_title": "自定义环境变量",
  "agents.custom_env_sub": "自定义提供商的额外 API 密钥或环境变量。",
  "agents.custom_env_add": "添加变量",
  "agents.custom_env_name_placeholder": "VARIABLE_NAME",
  "agents.custom_env_value_placeholder": "值...",
  "sessions.title": "会话",
  "sessions.title_sub": "活跃的会话密钥和会话级覆盖。",
  "sessions.active_within": "活跃时间内（分钟）",
  "sessions.limit": "限制",
  "sessions.include_global": "包含全局",
  "sessions.include_unknown": "包含未知",
  "sessions.store": "存储",
  "sessions.col_key": "密钥",
  "sessions.col_label": "标签",
  "sessions.col_kind": "类型",
  "sessions.col_updated": "更新",
  "sessions.col_tokens": "令牌",
  "sessions.col_thinking": "思考",
  "sessions.col_verbose": "详细",
  "sessions.col_reasoning": "推理",
  "sessions.col_actions": "操作",
  "sessions.no_sessions": "未找到会话。",
  "sessions.optional": "（可选）",
  "sessions.inherit": "继承",
  "sessions.task_log": "任务日志",
  "sessions.no_messages": "此会话中没有消息。",
  "sessions.messages_label": "条消息",
  "cron.title": "调度器",
  "cron.title_sub": "网关定时调度器状态。",
  "cron.enabled": "已启用",
  "cron.jobs": "任务",
  "cron.next_wake": "下次唤醒",
  "cron.new_job": "新任务",
  "cron.new_job_sub": "创建定时唤醒或智能体运行。",
  "cron.name": "名称",
  "cron.description": "描述",
  "cron.agent_id": "智能体ID",
  "cron.schedule": "计划",
  "cron.every": "每",
  "cron.at": "在",
  "cron.cron_expr": "Cron",
  "cron.session": "会话",
  "cron.wake_mode": "唤醒模式",
  "cron.payload": "负载",
  "cron.main": "主要",
  "cron.isolated": "隔离",
  "cron.now": "立即",
  "cron.next_heartbeat": "下次心跳",
  "cron.system_event": "系统事件",
  "cron.agent_turn": "智能体轮次",
  "cron.delivery": "交付",
  "cron.timeout": "超时（秒）",
  "cron.channel": "频道",
  "cron.to": "发送到",
  "cron.announce": "摘要通知（默认）",
  "cron.none_internal": "无（内部）",
  "cron.add_job": "添加任务",
  "cron.jobs_title": "任务",
  "cron.jobs_sub": "网关中存储的所有定时任务。",
  "cron.no_jobs": "暂无任务。",
  "cron.run_history": "运行历史",
  "cron.run_history_sub": "最近运行：",
  "cron.select_job": "选择任务查看运行历史。",
  "cron.no_runs": "暂无运行记录。",
  "cron.run_at": "运行时间",
  "cron.expression": "表达式",
  "cron.timezone": "时区（可选）",
  "cron.unit_minutes": "分钟",
  "cron.unit_hours": "小时",
  "cron.unit_days": "天",
  "cron.system": "系统",
  "cron.prompt": "提示",
  "cron.run_btn": "运行",
  "cron.history_btn": "历史",
  "cron.remove": "删除",
  "cron.status": "状态",
  "cron.next": "下次",
  "cron.last": "上次",
  "cron.open_run_chat": "打开运行对话",
  "skills.title": "技能",
  "skills.title_sub": "内置、托管和工作区技能。",
  "skills.filter": "筛选",
  "skills.search": "搜索技能",
  "skills.shown": "显示",
  "skills.no_skills": "未找到技能。",
  "skills.missing": "缺失",
  "skills.reason": "原因",
  "skills.api_key": "API密钥",
  "skills.save_key": "保存密钥",
  "nodes.title": "节点",
  "nodes.title_sub": "已配对设备和实时链接。",
  "nodes.no_nodes": "未找到节点。",
  "nodes.devices": "设备",
  "nodes.devices_sub": "配对请求 + 角色令牌。",
  "nodes.pending": "待处理",
  "nodes.paired": "已配对",
  "nodes.no_paired": "无已配对设备。",
  "nodes.role": "角色",
  "nodes.roles": "角色",
  "nodes.scopes": "范围",
  "nodes.approve": "批准",
  "nodes.reject": "拒绝",
  "nodes.tokens_none": "令牌：无",
  "nodes.tokens": "令牌",
  "nodes.revoked": "已吊销",
  "nodes.active_token": "活跃",
  "nodes.rotate": "轮换",
  "nodes.revoke": "吊销",
  "nodes.exec_binding": "执行节点绑定",
  "nodes.exec_binding_sub": "使用exec host=node时将智能体绑定到特定节点。",
  "nodes.switch_form": "切换到表单模式进行编辑。",
  "nodes.load_config": "加载配置",
  "nodes.default_binding": "默认绑定",
  "nodes.default_binding_sub": "智能体未指定节点时使用。",
  "nodes.any_node": "任意节点",
  "nodes.no_exec_nodes": "无可用system.run节点。",
  "nodes.no_agents_found": "未找到智能体。",
  "nodes.default_agent": "默认智能体",
  "nodes.uses_default": "使用默认值",
  "nodes.node": "节点",
  "nodes.override": "覆盖",
  "nodes.binding": "绑定",
  "nodes.use_default": "使用默认值",
  "nodes.status_paired": "已配对",
  "nodes.status_unpaired": "未配对",
  "nodes.status_connected": "已连接",
  "nodes.status_offline": "离线",
  "instances.title": "已连接实例",
  "instances.title_sub": "来自网关和客户端的在线信标。",
  "instances.no_instances": "暂无实例。",
  "instances.last_input": "最后输入",
  "instances.reason": "原因",
  "debug.snapshots": "快照",
  "debug.snapshots_sub": "状态、健康和心跳数据。",
  "debug.status": "状态",
  "debug.health": "健康",
  "debug.last_heartbeat": "最后心跳",
  "debug.security_audit": "安全审计",
  "debug.for_details": "查看详情。",
  "debug.manual_rpc": "手动RPC",
  "debug.manual_rpc_sub": "使用JSON参数发送网关方法。",
  "debug.method": "方法",
  "debug.params": "参数 (JSON)",
  "debug.call": "调用",
  "debug.models": "模型",
  "debug.models_sub": "models.list目录。",
  "debug.event_log": "事件日志",
  "debug.event_log_sub": "最新网关事件。",
  "debug.no_events": "暂无事件。",
  "logs.title": "日志",
  "logs.title_sub": "网关文件日志 (JSONL)。",
  "logs.export": "导出",
  "logs.filter": "筛选",
  "logs.search": "搜索日志",
  "logs.auto_follow": "自动跟踪",
  "logs.truncated": "日志已截断，显示最新部分。",
  "logs.no_entries": "无日志条目。",
  "chat.compacting": "正在压缩上下文...",
  "chat.compacted": "上下文压缩完成",
  "chat.attachment_preview": "附件预览",
  "chat.remove_attachment": "移除附件",
  "chat.placeholder": "添加消息或粘贴图片...",
  "chat.placeholder_shift": "消息 (↩ 发送, Shift+↩ 换行, 可粘贴图片)",
  "chat.not_connected": "连接到网关以开始聊天…",
  "chat.loading": "加载聊天中…",
  "chat.exit_focus": "退出专注模式",
  "chat.queued": "队列",
  "chat.remove_queued": "从队列中移除消息",
  "chat.image_count": "图片",
  "chat.new_messages": "新消息",
  "chat.message": "消息",
  "chat.stop": "停止",
  "chat.new_session": "新会话",
  "chat.queue_btn": "排队",
  "chat.queued_batch": "将合并发送",
  "chat.send": "发送",
  "chat.history_hidden": "条消息已隐藏",
  "chat.compaction_divider": "压缩",
  "app.brand": "NABE",
  "app.subtitle": "网关仪表盘",
  "app.expand_sidebar": "展开侧边栏",
  "app.collapse_sidebar": "收起侧边栏",
  "app.resources": "资源",
  "app.docs": "文档",
  "app.refresh_chat": "刷新聊天数据",
  "app.disabled_onboarding": "引导期间已禁用",
  "app.toggle_thinking": "切换AI思考过程显示",
  "app.toggle_focus": "切换专注模式（隐藏侧边栏）",
  "app.theme_system": "系统",
  "app.theme_light": "浅色",
  "app.theme_dark": "深色",
  "app.theme": "主题",
  "common.loading": "加载中...",
  "common.error": "错误",
  "common.cancel": "取消",
  "common.confirm": "确认",
  "common.yes": "是",
  "common.no": "否",
  "common.na": "无",
  "common.save": "保存",
  "common.saving": "保存中...",
  "common.refresh": "刷新",
  "common.refreshing": "刷新中...",
  "common.delete": "删除",
  "common.enable": "启用",
  "common.disable": "禁用",
  "common.enabled": "已启用",
  "common.disabled": "已禁用",
  "common.language": "语言",
  "common.what_is_this": "这是什么？",
  "common.learn_more": "了解更多",
  "common.step": "步骤",
  "common.docs": "文档",
  "common.resources": "资源",
  "common.agent": "智能体",
  // Quick Settings
  "qs.title": "快速设置",
  "qs.subtitle": "常用设置。如需所有选项，请从侧边栏选择分类。",
  "qs.ai": "AI 助手",
  "qs.ai.model": "AI 模型",
  "qs.ai.model_help": "为助手提供回答的 AI 模型",
  "qs.ai.name": "助手名称",
  "qs.ai.name_help": "助手自我介绍时使用的名字",
  "qs.ai.workspace": "工作文件夹",
  "qs.ai.workspace_help": "存放文件和项目的文件夹",
  "qs.conn": "连接",
  "qs.conn.port": "端口号",
  "qs.conn.port_help": "连接网关的网络端口",
  "qs.conn.bind": "谁可以连接",
  "qs.conn.bind_help": "控制哪些设备可以访问网关",
  "qs.conn.bind.loopback": "仅本机",
  "qs.conn.bind.lan": "同一网络的设备",
  "qs.conn.bind.tailnet": "互联网（通过 Tailscale）",
  "qs.conn.bind.auto": "自动",
  "qs.conn.auth": "密码保护",
  "qs.conn.auth_help": "连接时需要密码",
  "qs.conn.password": "密码",
  "qs.apps": "消息应用",
  "qs.apps.subtitle": "将助手连接到消息平台",
  "qs.apps.configure": "配置",
  "qs.updates": "更新",
  "qs.updates.channel": "更新通道",
  "qs.updates.channel_help": "接收新版本的频率",
  "qs.updates.auto": "自动检查",
  "qs.updates.auto_help": "网关启动时检查更新",
  "qs.log": "日志",
  "qs.log.level": "详细程度",
  "qs.log.level_help": "日志中记录的详细信息量",
  "qs.all_settings": "查看所有设置",
};

const translations: Record<Locale, TranslationKeys> = { en, th, ja, ko, zh };

let currentLocale: Locale = "en";

export function setLocale(locale: Locale) {
  currentLocale = locale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function t(key: keyof TranslationKeys): string {
  return translations[currentLocale]?.[key] ?? translations.en[key] ?? key;
}

export type TranslationKey = keyof TranslationKeys;
